import "firebase/compat/auth";
import {
  getDatabase,
  ref,
  get,
  update,
  set,
  push,
  remove,
  onValue,
  query,
  orderByChild,
  equalTo,
  serverTimestamp,
} from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import emailjs from "emailjs-com";
import moment from "moment-timezone";
import {
  notifyOwnerBoatApproved,
  notifyOwnerBoatRejected,
  notifyOwnerWaiverCompleted,
  notifyTripEndedForRenter,
  isTripEndedStatus,
} from "./utils/userNotifications";
import { normalizeAssetUrl, normalizeListingAssetUrls } from "./utils/assetUrl";
import { getPassengerCounts, toPassengerCount } from "./utils/passengerCounts";

const database = getDatabase();

/**
 * Get booking source tracking data for web platform.
 * Captures source and basic device info (OS, browser).
 * All fields except 'source' are optional and won't break functionality if missing.
 * 
 * @returns {Object} Booking source data with source and device_info
 */
export function getBookingSourceData() {
  const sourceData = {
    source: 'web',
  };

  // Capture device info (optional) - only if navigator is available
  if (typeof navigator !== 'undefined') {
    try {
      const deviceInfo = {};
      
      // User Agent for browser/OS detection
      if (navigator.userAgent) {
        const ua = navigator.userAgent;
        
        // Parse OS from user agent
        if (ua.includes('Windows')) {
          deviceInfo.os = 'Windows';
        } else if (ua.includes('Mac OS')) {
          deviceInfo.os = 'macOS';
        } else if (ua.includes('Linux')) {
          deviceInfo.os = 'Linux';
        } else if (ua.includes('Android')) {
          deviceInfo.os = 'Android';
        } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
          deviceInfo.os = 'iOS';
        }
        
        // Parse browser from user agent
        if (ua.includes('Chrome') && !ua.includes('Edg')) {
          deviceInfo.browser = 'Chrome';
        } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
          deviceInfo.browser = 'Safari';
        } else if (ua.includes('Firefox')) {
          deviceInfo.browser = 'Firefox';
        } else if (ua.includes('Edg')) {
          deviceInfo.browser = 'Edge';
        } else if (ua.includes('Opera') || ua.includes('OPR')) {
          deviceInfo.browser = 'Opera';
        }
      }
      
      // Only add device_info if we captured something
      if (Object.keys(deviceInfo).length > 0) {
        sourceData.device_info = deviceInfo;
      }
    } catch (error) {
      // Silently fail - device_info is optional
      console.warn('Failed to capture device info:', error);
    }
  }

  return sourceData;
}

export function lockDateHasTimeRanges(lockDate) {
  return (
    (Array.isArray(lockDate?.time_ranges) && lockDate.time_ranges.length > 0) ||
    Boolean(lockDate?.start_time && lockDate?.end_time)
  );
}

export function getDateStringsInRange(startDate, endDate, outputFormat = "YYYY-MM-DD") {
  const dates = [];
  const currentDate = moment(startDate).startOf("day");
  const end = moment(endDate).startOf("day");

  if (!currentDate.isValid() || !end.isValid()) {
    return dates;
  }

  while (currentDate.isSameOrBefore(end, "day")) {
    dates.push(currentDate.format(outputFormat));
    currentDate.add(1, "day");
  }

  return dates;
}

export function getFullDayLockedDates(lockDates, outputFormat = "YYYY-MM-DD") {
  if (!lockDates || typeof lockDates !== "object") {
    return [];
  }

  return Object.values(lockDates).reduce((dates, lockDate) => {
    if (!lockDate?.start_date || !lockDate?.end_date || lockDateHasTimeRanges(lockDate)) {
      return dates;
    }

    return [
      ...dates,
      ...getDateStringsInRange(lockDate.start_date, lockDate.end_date, outputFormat),
    ];
  }, []);
}

export function parseBookingDateTime(date, time) {
  if (!date && !time) return null;
  const dateStr = String(date || "").trim();
  const timeStr = String(time || "").trim();

  const dateFormats = [
    "dddd, MMMM D, YYYY",
    "dddd, MMMM DD, YYYY",
    "MMMM D, YYYY",
    "MMMM DD, YYYY",
    "YYYY-MM-DD",
    "MM/DD/YYYY",
    "M/D/YYYY",
  ];
  const timeFormats = [
    "h:mm A",
    "hh:mm A",
    "h:mm a",
    "hh:mm a",
    "h:mm:a",
    "hh:mm:a",
    "h:mmA",
    "hh:mmA",
    "h:mma",
    "hh:mma",
    "HH:mm",
    "HH:mm:ss",
  ];

  for (const dateFormat of dateFormats) {
    for (const timeFormat of timeFormats) {
      const parsed = moment(`${dateStr} ${timeStr}`, `${dateFormat} ${timeFormat}`, true);
      if (parsed.isValid()) {
        return parsed;
      }
    }
  }

  const fallback = moment(`${dateStr} ${timeStr}`);
  return fallback.isValid() ? fallback : null;
}

export function formatTimeDisplay(time) {
  if (!time) return "";
  const str = String(time).trim();
  if (!str) return "";

  const timeFormats = [
    "h:mm A",
    "hh:mm A",
    "h:mm a",
    "hh:mm a",
    "h:mm:a",
    "hh:mm:a",
    "h:mmA",
    "hh:mmA",
    "h:mma",
    "hh:mma",
    "HH:mm",
    "HH:mm:ss"
  ];

  for (const timeFormat of timeFormats) {
    const parsed = moment(str, timeFormat, true);
    if (parsed.isValid()) {
      return parsed.format("h:mm A");
    }
  }

  const fallback = moment(str, timeFormats);
  if (fallback.isValid()) {
    return fallback.format("h:mm A");
  }

  const directFallback = moment(str);
  if (directFallback.isValid()) {
    return directFallback.format("h:mm A");
  }

  return str;
}

export function formatDateTimeDisplay(date, time) {
  if (!date && !time) return "N/A";
  if (!time) {
    const parsedDateOnly = moment(date);
    return parsedDateOnly.isValid() ? parsedDateOnly.format("MMMM D, YYYY") : String(date);
  }
  if (!date) {
    return formatTimeDisplay(time);
  }

  const parsed = parseBookingDateTime(date, time);
  if (parsed && parsed.isValid()) {
    return parsed.format("MMMM D, YYYY [at] h:mm A");
  }

  const fallback = moment(`${date} ${time}`);
  if (fallback.isValid()) {
    return fallback.format("MMMM D, YYYY [at] h:mm A");
  }

  return `${date} at ${formatTimeDisplay(time)}`;
}

function getBookingEventStartDate(inquireInfo, booking) {
  const offerData = inquireInfo?.offerData;
  const inquiryData = inquireInfo?.inquiryData;

  if (offerData?.arivingDateTime) {
    const arrivalMoment = moment(offerData.arivingDateTime, [
      moment.ISO_8601,
      "dddd, MMMM D, YYYY h:mma",
      "dddd, MMMM D, YYYY hh:mma",
      "dddd, MMMM D, YYYY h:mm A",
      "dddd, MMMM D, YYYY hh:mm A",
      "dddd, MMMM D, YYYY HH:mm",
      "MMMM D, YYYY h:mma",
      "MMMM D, YYYY h:mm A",
    ], true);
    if (arrivalMoment.isValid()) {
      return arrivalMoment.toDate();
    }

    const fallback = moment(offerData.arivingDateTime);
    if (fallback.isValid()) {
      return fallback.toDate();
    }
  }

  const date =
    offerData?.offeredDate ||
    inquiryData?.prefferedDate ||
    booking?.tripDate;
  const time =
    offerData?.offeredTime ||
    inquiryData?.prefferedTime ||
    booking?.tripTime;

  if (date && time) {
    const parsed = parseBookingDateTime(date, time);
    if (parsed?.isValid()) {
      return parsed.toDate();
    }
  }

  if (date) {
    const dateOnly = moment(date, [
      "dddd, MMMM D, YYYY",
      "dddd, MMMM DD, YYYY",
      "YYYY-MM-DD",
      "MM/DD/YYYY",
    ], true);
    if (dateOnly.isValid()) {
      return dateOnly.toDate();
    }

    const fallback = new Date(date);
    if (!isNaN(fallback.getTime())) {
      return fallback;
    }
  }

  return null;
}

export function getLockedTimeConflict(lockDates, date, startTime, durationHours = 0, durationMinutes = 0, overnight = 0) {
  if (!lockDates || typeof lockDates !== "object" || !date || !startTime) {
    return null;
  }

  const bookingStart = parseBookingDateTime(date, startTime);
  if (!bookingStart) {
    return null;
  }

  const bookingEnd = bookingStart.clone();
  if (Number(overnight) > 0) {
    bookingEnd.add(Number(overnight), "days");
  } else {
    bookingEnd
      .add(Number(durationHours) || 0, "hours")
      .add(Number(durationMinutes) || 0, "minutes");
  }

  if (!bookingEnd.isAfter(bookingStart)) {
    bookingEnd.add(1, "hour");
  }

  for (const lockDate of Object.values(lockDates)) {
    if (!lockDate?.start_date || !lockDate?.end_date || !lockDateHasTimeRanges(lockDate)) {
      continue;
    }

    const lockStartDay = moment(lockDate.start_date).startOf("day");
    const lockEndDay = moment(lockDate.end_date).startOf("day");
    if (!lockStartDay.isValid() || !lockEndDay.isValid()) {
      continue;
    }

    const ranges = Array.isArray(lockDate.time_ranges) && lockDate.time_ranges.length > 0
      ? lockDate.time_ranges
      : [{ start: lockDate.start_time, end: lockDate.end_time }];

    const currentDay = lockStartDay.clone();
    while (currentDay.isSameOrBefore(lockEndDay, "day")) {
      for (const range of ranges) {
        if (!range?.start || !range?.end) {
          continue;
        }

        const lockStart = parseBookingDateTime(currentDay.format("YYYY-MM-DD"), range.start);
        let lockEnd = parseBookingDateTime(currentDay.format("YYYY-MM-DD"), range.end);

        if (!lockStart || !lockEnd) {
          continue;
        }

        if (!lockEnd.isAfter(lockStart)) {
          lockEnd = lockEnd.add(1, "day");
        }

        if (bookingStart.isBefore(lockEnd) && bookingEnd.isAfter(lockStart)) {
          return {
            start: range.start,
            end: range.end,
            date: currentDay.format("YYYY-MM-DD"),
          };
        }
      }

      currentDay.add(1, "day");
    }
  }

  return null;
}

export function getLockedTimeRangesForDate(lockDates, date) {
  if (!lockDates || typeof lockDates !== "object" || !date) {
    return [];
  }

  const selectedDay = moment(date, [
    "dddd, MMMM D, YYYY",
    "dddd, MMMM DD, YYYY",
    "YYYY-MM-DD",
    "MM/DD/YYYY",
  ], true);
  const normalizedSelectedDay = selectedDay.isValid()
    ? selectedDay.startOf("day")
    : moment(date).startOf("day");

  if (!normalizedSelectedDay.isValid()) {
    return [];
  }

  return Object.values(lockDates).reduce((rangesForDate, lockDate) => {
    if (!lockDate?.start_date || !lockDate?.end_date || !lockDateHasTimeRanges(lockDate)) {
      return rangesForDate;
    }

    const lockStartDay = moment(lockDate.start_date).startOf("day");
    const lockEndDay = moment(lockDate.end_date).startOf("day");

    if (
      !lockStartDay.isValid() ||
      !lockEndDay.isValid() ||
      normalizedSelectedDay.isBefore(lockStartDay, "day") ||
      normalizedSelectedDay.isAfter(lockEndDay, "day")
    ) {
      return rangesForDate;
    }

    const ranges = Array.isArray(lockDate.time_ranges) && lockDate.time_ranges.length > 0
      ? lockDate.time_ranges
      : [{ start: lockDate.start_time, end: lockDate.end_time }];

    return [
      ...rangesForDate,
      ...ranges
        .filter((range) => range?.start && range?.end)
        .map((range) => ({ start: range.start, end: range.end })),
    ];
  }, []);
}

export function getAvailableTimeSlotsForDate(
  lockDates,
  date,
  durationHours = 1,
  durationMinutes = 0,
  options = {}
) {
  if (!date) {
    return [];
  }

  const {
    dayStart = "06:00",
    dayEnd = "20:00",
    intervalMinutes = 60,
    displayFormat = "hh:mm A",
  } = options;

  const selectedDay = moment(date, [
    "dddd, MMMM D, YYYY",
    "dddd, MMMM DD, YYYY",
    "YYYY-MM-DD",
    "MM/DD/YYYY",
  ], true);
  const normalizedSelectedDay = selectedDay.isValid()
    ? selectedDay.format("YYYY-MM-DD")
    : moment(date).format("YYYY-MM-DD");

  const startOfWindow = parseBookingDateTime(normalizedSelectedDay, dayStart);
  const endOfWindow = parseBookingDateTime(normalizedSelectedDay, dayEnd);

  if (!startOfWindow || !endOfWindow || !endOfWindow.isAfter(startOfWindow)) {
    return [];
  }

  const slots = [];
  const slotStart = startOfWindow.clone();
  const bookingDurationMinutes =
    ((Number(durationHours) || 0) * 60) + (Number(durationMinutes) || 0);
  const minimumDuration = bookingDurationMinutes > 0 ? bookingDurationMinutes : 60;

  while (slotStart.clone().add(minimumDuration, "minutes").isSameOrBefore(endOfWindow)) {
    const value = slotStart.format(displayFormat);
    const conflict = getLockedTimeConflict(
      lockDates,
      normalizedSelectedDay,
      value,
      Math.floor(minimumDuration / 60),
      minimumDuration % 60,
      0
    );

    if (!conflict) {
      slots.push({
        value,
        label: value,
      });
    }

    slotStart.add(intervalMinutes, "minutes");
  }

  return slots;
}

export async function addWaiver(waiverName, waiverDescription, questions) {
  const waiversRef = ref(database, "waivers");
  const newWaiverRef = push(waiversRef);

  // Create the waiver object
  const waiverData = {
    waiverName,
    waiverDescription,
    questions,
  };

  // Save the waiver to the Realtime Database
  await set(newWaiverRef, waiverData);

  console.log("Waiver added to Firebase:", waiverData);
}

export async function addCoupon(code, amount) {
  const couponCodesRef = ref(database, "portalSettings/settingsID/couponCodes");

  const newWaiverRef = push(couponCodesRef, { coupon: code, discount: amount });
}

export async function updateCoupon(couponId, code, amount) {
  try {
    const couponRef = ref(
      database,
      `portalSettings/settingsID/couponCodes/${couponId}`
    );
    await set(couponRef, { coupon: code, discount: amount });
    console.log("Coupon updated successfully");
  } catch (error) {
    throw new Error("Error updating coupon: " + error.message);
  }
}

export async function deleteCoupon(couponId) {
  try {
    const couponRef = ref(
      database,
      `portalSettings/settingsID/couponCodes/${couponId}`
    );
    await remove(couponRef);
    console.log("Coupon deleted successfully");
  } catch (error) {
    throw new Error("Error deleting coupon: " + error.message);
  }
}

export async function getAllCouponCodes() {
  const database = getDatabase();
  const couponCodesRef = ref(database, "portalSettings/settingsID/couponCodes");

  try {
    const snapshot = await get(couponCodesRef);
    const couponCodes = [];

    if (snapshot.exists()) {
      snapshot.forEach((childSnapshot) => {
        const coupon = childSnapshot.val();
        couponCodes.push({
          id: childSnapshot.key,
          coupon: coupon.coupon,
          discount: coupon.discount,
        });
      });
    }

    return couponCodes;
  } catch (error) {
    console.error("Error getting coupon codes: ", error);
    throw error;
  }
}

export async function getAllBoatTypes() {
  const boatTypesRef = ref(database, "portalSettings/settingsID/boatTypes");
  try {
    const snapshot = await get(boatTypesRef);
    if (snapshot.exists()) {
      const boatTypesData = [];
      snapshot.forEach((childSnapshot) => {
        const type = childSnapshot.val();
        boatTypesData.push(type);
      });
      return boatTypesData;
    } else {
      console.log("No boat types found");
      return [];
    }
  } catch (error) {
    throw new Error("Error fetching boat types: " + error.message);
  }
}

export async function getSignedWaiverByID(signedWaiverId) {
  try {
    const allBookings = await getAllBookings();

    var result = false;

    allBookings.forEach((booking) => {
      if (booking.tripData) {
        for (const waiverType in booking.tripData.waivers) {
          for (const waiverId in booking.tripData.waivers[waiverType]) {
            if (waiverId == signedWaiverId) {
              const waiver = booking.tripData.waivers[waiverType][waiverId];
              result = waiver;
              return;
            }
          }
        }
      }
    });

    return result;
  } catch (error) {
    throw new Error("Error fetching Booking: " + error.message);
  }
}

export async function getSpecificWaiverDataAcrossBookings(waiverId) {
  const bookingsRef = ref(database, "bookings");

  try {
    const snapshot = await get(bookingsRef);
    if (snapshot.exists()) {
      // Iterate over each booking
      snapshot.forEach((bookingSnapshot) => {
        const bookingId = bookingSnapshot.key;

        console.log("bookingId", bookingId);
        const tripDataRef = ref(
          database,
          `bookings/${bookingId}/tripData/waivers`
        );

        get(tripDataRef)
          .then((tripDataSnapshot) => {
            if (tripDataSnapshot.exists()) {
              // Iterate over each waiver type under trip data
              tripDataSnapshot.forEach((waiverTypeSnapshot) => {
                const waiverType = waiverTypeSnapshot.key;
                console.log("waiverType", waiverType);
                const specificWaiverRef = ref(
                  database,
                  `bookings/${bookingId}/tripData/waivers/${waiverType}/`
                );

                get(specificWaiverRef)
                  .then((specificWaiverSnapshot) => {
                    if (specificWaiverSnapshot.exists()) {
                      console.log(
                        "specificWaiverSnapshot",
                        specificWaiverSnapshot
                      );
                    }
                  })
                  .catch((error) => {
                    throw new Error(
                      "Error fetching trip data: " + error.message
                    );
                  });
              });
            }
          })
          .catch((error) => {
            throw new Error("Error fetching trip data: " + error.message);
          });
      });

      return "no waiver found";
    } else {
      // No bookings found
      return "no bookings";
    }
  } catch (error) {
    throw new Error(
      "Error fetching specific waiver data across bookings: " + error.message
    );
  }
}

export async function updateWaiverById(waiverId, updatedWaiverData) {
  const waiverRef = ref(database, `waivers/${waiverId}`);

  // Update the waiver with the new data
  await update(waiverRef, updatedWaiverData);

  console.log("Waiver updated in Firebase:", updatedWaiverData);
}

export async function sendEmail(to, subject, emailBody, retryCount = 0) {
  try {
    // Check if environment variables are properly set
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMP_ID;
    const userId = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !userId) {
      console.error("EmailJS environment variables not properly configured:", {
        serviceId: !!serviceId,
        templateId: !!templateId,
        userId: !!userId,
      });
      return false;
    }

    console.log(
      `Attempting to send email to: ${to} with subject: ${subject} (attempt ${retryCount + 1
      })`
    );

    const response = await emailjs.send(
      serviceId,
      templateId,
      {
        to_email: to,
        email_subject: subject,
        email_body: emailBody,
      },
      userId,
      {
        "Content-Type": "text/html",
      }
    );

    console.log("Email sent successfully to:", to, "Response:", response);
    return true;
  } catch (error) {
    console.error(
      `Email failed to send to: ${to} (attempt ${retryCount + 1})`,
      "Error details:",
      {
        message: error.message,
        status: error.status,
        text: error.text,
        stack: error.stack,
      }
    );

    // Retry logic for transient errors (max 2 retries)
    if (retryCount < 2 && (error.status === 429 || error.status >= 500)) {
      console.log(`Retrying email send to ${to} in 2 seconds...`);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return sendEmail(to, subject, emailBody, retryCount + 1);
    }

    return false;
  }
}

// Helper function to send email with better error handling
export async function sendEmailWithRetry(
  to,
  subject,
  emailBody,
  maxRetries = 2
) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const success = await sendEmail(to, subject, emailBody, attempt);
    if (success) {
      return true;
    }

    if (attempt < maxRetries) {
      console.log(
        `Email send failed, retrying in ${(attempt + 1) * 2} seconds...`
      );
      await new Promise((resolve) => setTimeout(resolve, (attempt + 1) * 2000));
    }
  }

  console.error(
    `Failed to send email to ${to} after ${maxRetries + 1} attempts`
  );
  return false;
}

export async function contactUsEmail(name, email, phone, zipcode, message) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
  <title></title>
  <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
  <meta content="width=device-width, initial-scale=1.0" name="viewport" />
  <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
  <!--[if !mso]><!-->
  <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
    rel="stylesheet" type="text/css" />
  <!--<![endif]-->
  <style>
    * {
      box-sizing: border-box;
    }

    body {
      margin: 0;
      padding: 0;
    }

    a[x-apple-data-detectors] {
      color: inherit !important;
      text-decoration: inherit !important;
    }

    #MessageViewBody a {
      color: inherit;
      text-decoration: none;
    }

    p {
      line-height: inherit
    }

    .desktop_hide,
    .desktop_hide table {
      mso-hide: all;
      display: none;
      max-height: 0px;
      overflow: hidden;
    }

    .image_block img+div {
      display: none;
    }

    @media (max-width:700px) {
      .desktop_hide table.icons-inner {
        display: inline-block !important;
      }

      .icons-inner {
        text-align: center;
      }

      .icons-inner td {
        margin: 0 auto;
      }

      .mobile_hide {
        display: none;
      }

      .row-content {
        width: 100% !important;
      }

      .stack .column {
        width: 100%;
        display: block;
      }

      .mobile_hide {
        min-height: 0;
        max-height: 0;
        max-width: 0;
        overflow: hidden;
        font-size: 0px;
      }

      .desktop_hide,
      .desktop_hide table {
        display: table !important;
        max-height: none !important;
      }

      .row-1 .column-1 .block-3.paragraph_block td.pad>div {
        font-size: 16px !important;
      }

      .row-1 .column-1 .block-2.heading_block h1 {
        font-size: 24px !important;
      }

      .row-1 .column-1 {
        padding: 40px 20px 30px !important;
      }
    }
  </style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
  <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
    <tbody>
      <tr>
        <td>
          <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
            role="presentation"
            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
            width="100%">
            <tbody>
              <tr>
                <td>
                  <table align="center" border="0" cellpadding="0" cellspacing="0"
                    class="row-content stack" role="presentation"
                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                    width="680">
                    <tbody>
                      <tr>
                        <td class="column column-1"
                          style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                          width="100%">
                          <table border="0" cellpadding="0" cellspacing="0"
                            class="icons_block block-1" role="presentation"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                            width="100%">
                            <tr>
                              <td class="pad"
                                style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                <table align="center" cellpadding="0" cellspacing="0"
                                  class="alignment" role="presentation"
                                  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                  <tr>
                                    <td
                                      style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                      <img align="center" class="icon"
                                        height="128"
                                        src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                        style="display: block; height: auto; margin: 0 auto; border: 0; background-color:#121212;"
                                        width="256" /></td>
                                  </tr>
                                </table>
                              </td>
                            </tr>
                          </table>
                          <table border="0" cellpadding="10" cellspacing="0"
                            class="heading_block block-2" role="presentation"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                            width="100%">
                          
                          </table>
                          <table border="0" cellpadding="0" cellspacing="0"
                            class="paragraph_block block-3" role="presentation"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                            width="100%">
                            <tr>
                              <td class="pad"
                                style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                <div
                                  style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                  <p style="text-align: center;margin: 0; margin-bottom: 16px;">You have received a submission                                                                  </p>
                                  
                                  <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       BBR Boat Rentals
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">from</p>
                                  <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${name}
                                                                    </span></h1>
                                  <h6 style="text-align: center; margin: 0; margin-top: 16px; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 24px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;">
                                    <span style="display: block; font-size: 26px; text-align: center; margin-top: 32px" class="tinyMce-placeholder">
                                      Submission Details
                                  </span>
                                    <span style="display: block; margin-top: 16px" class="tinyMce-placeholder">
                                        Email: ${email}
                                    </span>
                                    <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                      Phone #: ${phone}
                                  </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Zipcode: ${zipcode}
                                </span>
                                  <span style="display: block; text-align: center; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Message
                                </span>
                                  <span style="display: block; font-size: 19px; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    ${message}
                                </span>
                                </h6>
                                  <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 32px;  color: #ffffff;">
                                                                         <a style=" color: #ffffff;" href="${process.env.REACT_APP_BASE_URL}">BASSBOATRENTALS.COM</a>
</p>
                                  <p style="text-align: center;margin: 0;">Thank you!</p>
                                  <p style="text-align: center;margin: 0;">Best Regards.</p>
                                </div>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </tbody>
  </table><!-- End -->
</body>

</html>`;
}

export async function adultWaiverEmailBody(
  bookingTitle,
  bookingId,
  passengerName,
  passengerDob,
  passengerEmail,
  captainName,
  signatureUrl
) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
  <title></title>
  <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
  <meta content="width=device-width, initial-scale=1.0" name="viewport" />
  <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
  <!--[if !mso]><!-->
  <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
    rel="stylesheet" type="text/css" />
  <!--<![endif]-->
  <style>
    * {
      box-sizing: border-box;
    }

    body {
      margin: 0;
      padding: 0;
    }

    a[x-apple-data-detectors] {
      color: inherit !important;
      text-decoration: inherit !important;
    }

    #MessageViewBody a {
      color: inherit;
      text-decoration: none;
    }

    p {
      line-height: inherit
    }

    .desktop_hide,
    .desktop_hide table {
      mso-hide: all;
      display: none;
      max-height: 0px;
      overflow: hidden;
    }

    .image_block img+div {
      display: none;
    }

    @media (max-width:700px) {
      .desktop_hide table.icons-inner {
        display: inline-block !important;
      }

      .icons-inner {
        text-align: center;
      }

      .icons-inner td {
        margin: 0 auto;
      }

      .mobile_hide {
        display: none;
      }

      .row-content {
        width: 100% !important;
      }

      .stack .column {
        width: 100%;
        display: block;
      }

      .mobile_hide {
        min-height: 0;
        max-height: 0;
        max-width: 0;
        overflow: hidden;
        font-size: 0px;
      }

      .desktop_hide,
      .desktop_hide table {
        display: table !important;
        max-height: none !important;
      }

      .row-1 .column-1 .block-3.paragraph_block td.pad>div {
        font-size: 16px !important;
      }

      .row-1 .column-1 .block-2.heading_block h1 {
        font-size: 24px !important;
      }

      .row-1 .column-1 {
        padding: 40px 20px 30px !important;
      }
    }
  </style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
  <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
    <tbody>
      <tr>
        <td>
          <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
            role="presentation"
            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
            width="100%">
            <tbody>
              <tr>
                <td>
                  <table align="center" border="0" cellpadding="0" cellspacing="0"
                    class="row-content stack" role="presentation"
                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                    width="680">
                    <tbody>
                      <tr>
                        <td class="column column-1"
                          style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                          width="100%">
                          <table border="0" cellpadding="0" cellspacing="0"
                            class="icons_block block-1" role="presentation"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                            width="100%">
                            <tr>
                              <td class="pad"
                                style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                <table align="center" cellpadding="0" cellspacing="0"
                                  class="alignment" role="presentation"
                                  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                  <tr>
                                    <td
                                      style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                      <img align="center" class="icon"
                                        height="128"
                                        src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                        style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                        width="256" /></td>
                                  </tr>
                                </table>
                              </td>
                            </tr>
                          </table>
                          <table border="0" cellpadding="10" cellspacing="0"
                            class="heading_block block-2" role="presentation"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                            width="100%">
                          
                          </table>
                          <table border="0" cellpadding="0" cellspacing="0"
                            class="paragraph_block block-3" role="presentation"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                            width="100%">
                            <tr>
                              <td class="pad"
                                style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                <div
                                  style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                  <p style="text-align: center;margin: 0; margin-bottom: 16px;">Adult Passenger Waiver has been submitted for the                                                                </p>
                                  
                                  <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       ${bookingTitle}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for Booking ID</p>
                                  <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${bookingId}
                                                                    </span></h1>
                                  <h6 style="text-align: center; margin: 0; margin-top: 16px; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 24px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;">
                                    <span style="display: block; text-decoration: underline; font-size: 26px; text-align: center; margin-top: 32px" class="tinyMce-placeholder">
                                      Waiver Details
                                  </span>
                                    <span style="display: block; margin-top: 16px" class="tinyMce-placeholder">
                                        Adult Passenger Name: ${passengerName}
                                    </span>
                                    <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                      Adult Passenger DOB #: ${passengerDob}
                                  </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Adult Passenger Email: ${passengerEmail}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Name of the captain operating the boat: ${captainName}
                                </span>
                                  <span style="display: block; text-align: center; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Signature
                                </span>
                                  <span style="display: block; font-size: 19px; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                 <div style="background: white; padding: 19px; border-radius: 16px;>   
								  <img src='${signatureUrl}' width="240" alt="Signature Image"/>
								  </div>
                                </span>
                                </h6>
                                  <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 32px;  color: #ffffff;">
                                                                         <a style=" color: #ffffff;" href="${process.env.REACT_APP_BASE_URL}">BASSBOATRENTALS.COM</a>
</p>
                                  <p style="text-align: center;margin: 0;">Thank you!</p>
                                  <p style="text-align: center;margin: 0;">Best Regards.</p>
                                </div>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </tbody>
  </table><!-- End -->
</body>

</html>`;
}

export async function minorWaiverEmailBody(
  bookingTitle,
  bookingId,
  minorName,
  minorDob,
  minorAddress,
  minorCountry,
  minorCity,
  minorState,
  minorZipcode,
  minorGuardianEmail,
  minorEmergencyContactName,
  minorEmergencyContactNumber,
  insuranceCarrier,
  insurancePolicyName,
  captainName,
  parentName,
  parentNumber
) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
  <title></title>
  <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
  <meta content="width=device-width, initial-scale=1.0" name="viewport" />
  <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
  <!--[if !mso]><!-->
  <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
    rel="stylesheet" type="text/css" />
  <!--<![endif]-->
  <style>
    * {
      box-sizing: border-box;
    }

    body {
      margin: 0;
      padding: 0;
    }

    a[x-apple-data-detectors] {
      color: inherit !important;
      text-decoration: inherit !important;
    }

    #MessageViewBody a {
      color: inherit;
      text-decoration: none;
    }

    p {
      line-height: inherit
    }

    .desktop_hide,
    .desktop_hide table {
      mso-hide: all;
      display: none;
      max-height: 0px;
      overflow: hidden;
    }

    .image_block img+div {
      display: none;
    }

    @media (max-width:700px) {
      .desktop_hide table.icons-inner {
        display: inline-block !important;
      }

      .icons-inner {
        text-align: center;
      }

      .icons-inner td {
        margin: 0 auto;
      }

      .mobile_hide {
        display: none;
      }

      .row-content {
        width: 100% !important;
      }

      .stack .column {
        width: 100%;
        display: block;
      }

      .mobile_hide {
        min-height: 0;
        max-height: 0;
        max-width: 0;
        overflow: hidden;
        font-size: 0px;
      }

      .desktop_hide,
      .desktop_hide table {
        display: table !important;
        max-height: none !important;
      }

      .row-1 .column-1 .block-3.paragraph_block td.pad>div {
        font-size: 16px !important;
      }

      .row-1 .column-1 .block-2.heading_block h1 {
        font-size: 24px !important;
      }

      .row-1 .column-1 {
        padding: 40px 20px 30px !important;
      }
    }
  </style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
  <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
    <tbody>
      <tr>
        <td>
          <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
            role="presentation"
            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
            width="100%">
            <tbody>
              <tr>
                <td>
                  <table align="center" border="0" cellpadding="0" cellspacing="0"
                    class="row-content stack" role="presentation"
                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                    width="680">
                    <tbody>
                      <tr>
                        <td class="column column-1"
                          style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                          width="100%">
                          <table border="0" cellpadding="0" cellspacing="0"
                            class="icons_block block-1" role="presentation"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                            width="100%">
                            <tr>
                              <td class="pad"
                                style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                <table align="center" cellpadding="0" cellspacing="0"
                                  class="alignment" role="presentation"
                                  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                  <tr>
                                    <td
                                      style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                      <img align="center" class="icon"
                                        height="128"
                                        src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                        style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                        width="256" /></td>
                                  </tr>
                                </table>
                              </td>
                            </tr>
                          </table>
                          <table border="0" cellpadding="10" cellspacing="0"
                            class="heading_block block-2" role="presentation"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                            width="100%">
                          
                          </table>
                          <table border="0" cellpadding="0" cellspacing="0"
                            class="paragraph_block block-3" role="presentation"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                            width="100%">
                            <tr>
                              <td class="pad"
                                style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                <div
                                  style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                  <p style="text-align: center;margin: 0; margin-bottom: 16px;">Minor Passenger Waiver has been submitted for the                                                                </p>
                                  
                                  <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       ${bookingTitle}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for Booking ID</p>
                                  <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${bookingId}
                                                                    </span></h1>
                                  <h6 style="text-align: center; margin: 0; margin-top: 16px; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 24px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;">
                                    <span style="display: block; font-size: 26px; text-align: center; margin-top: 32px" class="tinyMce-placeholder">
                                      Waiver Details
                                  </span>
                                    <span style="display: block; margin-top: 16px" class="tinyMce-placeholder">
                                        Minor's Name: ${minorName}
                                    </span>
                                    <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                      Minor's DOB #: ${minorDob}
                                  </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Address: ${minorAddress}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Country: ${minorCountry}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    City: ${minorCity}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    State: ${minorState}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Zip/Postal: ${minorZipcode}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Parent or Guardian's Email: ${minorGuardianEmail}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Emergency Contact Name: ${minorEmergencyContactName}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Emergency Contact Number: ${minorEmergencyContactNumber}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Insurance Carrier: ${insuranceCarrier}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Insurance Policy Name: ${insurancePolicyName}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Name of the captain operating the boat: ${captainName}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Parent or Guardian's Name: ${parentName}
                                </span>
                                  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
                                    Parent or Guardian's Number: ${parentNumber}
                                </span>
                                </h6>
                                  <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 32px;  color: #ffffff;">
                                                                         <a style=" color: #ffffff;" href="${process.env.REACT_APP_BASE_URL}">BASSBOATRENTALS.COM</a>
</p>
                                  <p style="text-align: center;margin: 0;">Thank you!</p>
                                  <p style="text-align: center;margin: 0;">Best Regards.</p>
                                </div>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </tbody>
  </table><!-- End -->
</body>

</html>`;
}

// export async function welcomeEmailBody (name, email) {
//   return `
//   <!DOCTYPE html>

//   <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

//   <head>
// 	<title></title>
// 	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
// 	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
// 	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
// 	<!--[if !mso]><!-->
// 	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
// 	  rel="stylesheet" type="text/css" />
// 	<!--<![endif]-->
// 	<style>
// 	  * {
// 		box-sizing: border-box;
// 	  }

// 	  body {
// 		margin: 0;
// 		padding: 0;
// 	  }

// 	  a[x-apple-data-detectors] {
// 		color: inherit !important;
// 		text-decoration: inherit !important;
// 	  }

// 	  #MessageViewBody a {
// 		color: inherit;
// 		text-decoration: none;
// 	  }

// 	  p {
// 		line-height: inherit
// 	  }

// 	  .desktop_hide,
// 	  .desktop_hide table {
// 		mso-hide: all;
// 		display: none;
// 		max-height: 0px;
// 		overflow: hidden;
// 	  }

// 	  .image_block img+div {
// 		display: none;
// 	  }

// 	  @media (max-width:700px) {
// 		.desktop_hide table.icons-inner {
// 		  display: inline-block !important;
// 		}

// 		.icons-inner {
// 		  text-align: center;
// 		}

// 		.icons-inner td {
// 		  margin: 0 auto;
// 		}

// 		.mobile_hide {
// 		  display: none;
// 		}

// 		.row-content {
// 		  width: 100% !important;
// 		}

// 		.stack .column {
// 		  width: 100%;
// 		  display: block;
// 		}

// 		.mobile_hide {
// 		  min-height: 0;
// 		  max-height: 0;
// 		  max-width: 0;
// 		  overflow: hidden;
// 		  font-size: 0px;
// 		}

// 		.desktop_hide,
// 		.desktop_hide table {
// 		  display: table !important;
// 		  max-height: none !important;
// 		}

// 		.row-1 .column-1 .block-3.paragraph_block td.pad>div {
// 		  font-size: 16px !important;
// 		}

// 		.row-1 .column-1 .block-2.heading_block h1 {
// 		  font-size: 24px !important;
// 		}

// 		.row-1 .column-1 {
// 		  padding: 40px 20px 30px !important;
// 		}
// 	  }
// 	</style>
//   </head>

//   <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
// 	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
// 	  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
// 	  <tbody>
// 		<tr>
// 		  <td>
// 			<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
// 			  role="presentation"
// 			  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
// 			  width="100%">
// 			  <tbody>
// 				<tr>
// 				  <td>
// 					<table align="center" border="0" cellpadding="0" cellspacing="0"
// 					  class="row-content stack" role="presentation"
// 					  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
// 					  width="680">
// 					  <tbody>
// 						<tr>
// 						  <td class="column column-1"
// 							style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
// 							width="100%">
// 							<table border="0" cellpadding="0" cellspacing="0"
// 							  class="icons_block block-1" role="presentation"
// 							  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
// 							  width="100%">
// 							  <tr>
// 								<td class="pad"
// 								  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
// 								  <table align="center" cellpadding="0" cellspacing="0"
// 									class="alignment" role="presentation"
// 									style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
// 									<tr>
// 									  <td
// 										style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
// 										<img align="center" class="icon"
// 										  height="128"
// 										  src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
// 										  style="display: block; height: auto; margin: 0 auto; border: 0;"
// 										  width="256" /></td>
// 									</tr>
// 								  </table>
// 								</td>
// 							  </tr>
// 							</table>
// 							<table border="0" cellpadding="10" cellspacing="0"
// 							  class="heading_block block-2" role="presentation"
// 							  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
// 							  width="100%">

// 							</table>
// 							<table border="0" cellpadding="0" cellspacing="0"
// 							  class="paragraph_block block-3" role="presentation"
// 							  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
// 							  width="100%">
// 							  <tr>
// 								<td class="pad"
// 								  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
// 								  <div
// 									style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
// 									<p style="text-align: center;margin: 0; margin-bottom: 16px;">Your account has been created on                                                                    </p>

// 									<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
// 																		 BBR Bass Boat Rentals
// 																	  </span></h1>
// 																	  <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">under the name</p>
// 									<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
// 																		  ${name}
// 																	  </span></h1>
// 									<h6 style="text-align: center; margin: 0; margin-top: 16px; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 24px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;">
// 									  <span style="display: block; font-size: 26px; text-align: center; margin-top: 32px" class="tinyMce-placeholder">
// 										Credentials
// 									</span>
// 									  <span style="display: block; margin-top: 16px" class="tinyMce-placeholder">
// 										  Email: ${email}
// 									  </span>
// 									  <span style="display: block; margin-top: 8px; margin-bottom: 16px;" class="tinyMce-placeholder">
// 										Password: bbrboatrentals
// 									</span></h6>
// 									<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
// 																		  Please log in and change your password from the portal. <a style=" color: #ffffff;" href="${process.env.REACT_APP_BASE_URL}">BASSBOATRENTALS.COM</a>
//   </p>
// 									<p style="text-align: center;margin: 0;">Thank you!</p>
// 									<p style="text-align: center;margin: 0;">Best Regards.</p>
// 								  </div>
// 								</td>
// 							  </tr>
// 							</table>
// 						  </td>
// 						</tr>
// 					  </tbody>
// 					</table>
// 				  </td>
// 				</tr>
// 			  </tbody>
// 			</table>
// 		  </td>
// 		</tr>
// 	  </tbody>
// 	</table><!-- End -->
//   </body>

//   </html>
//   `;
// }

export async function welcomeEmailBody(name) {
  return `
  <!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }
  
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                            
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <p style="text-align: center;margin: 0; margin-bottom: 16px;">Your account has been registered on BBR Bass Boat Rentals</p>
                                    
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                         Account Registered
                                                                      </span></h1>
                                                                      <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          ${name}
                                                                      </span></h1>
                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">  </p>
                                    <p style="text-align: center;margin: 0;">Thank you!</p>
                                    <p style="text-align: center;margin: 0;">Best Regards.</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>
  `;
}

export async function getPlaceReviews() {
  try {
    const placeId = "ChIJc-0iKkIjR4YRwmBh60kNnzQ"; // Replace with the actual place ID
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/get-place-reviews/${placeId}`
    );
    const data = await response.json();
    return data.reviews;
  } catch (error) {
    console.error(error);
  }
}

export function sendSMS(to, message) {
  // Validate inputs
  if (!to || !message) {
    console.warn("SMS not sent: Phone number and message are required");
    return Promise.resolve({ success: false, message: "Invalid inputs" });
  }

  // Check if BASE_URL is available
  if (!process.env.REACT_APP_BASE_URL) {
    console.warn("SMS not sent: REACT_APP_BASE_URL is not defined");
    return Promise.resolve({
      success: false,
      message: "Base URL not configured",
    });
  }

  let smsEndpoint = `${process.env.REACT_APP_BASE_URL}/send-sms`;

  return fetch(smsEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ to, message }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then((data) => {
      console.log("SMS sent successfully:", data);
      return { success: true, data };
    })
    .catch((error) => {
      console.warn(
        "SMS service failed, but this is non-critical:",
        error.message
      );

      // Return a successful promise instead of rejecting
      // This ensures SMS failures don't break the main flow
      return {
        success: false,
        error: error.message,
        message: "SMS service unavailable - this is non-critical",
      };
    });
}

export async function welcomeRenterEmailBody(name) {
  return `
  <!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <meta charset="UTF-8" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Welcome to BBR Boat Rentals!</title>
    <style>
      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        padding: 0;
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 20px;
        color: rgb(34, 34, 34);
        background-color: #ffffff;
      }

      .container {
        max-width: 100%;
        margin: 0 auto;
        padding: 40px 20px;
      }

      .logo {
        text-align: center;
        margin-bottom: 40px;
      }

      .logo img {
        max-width: 200px;
        height: auto;
      }

      h1 {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 700;
        font-size: 13px;
        line-height: 20px;
        margin: 0 0 20px 0;
        color: rgb(34, 34, 34);
      }

      p {
        margin: 0 0 16px 0;
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 20px;
        color: rgb(34, 34, 34);
      }

      a {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 20px;
        color: rgb(17, 85, 204);
        text-decoration: underline;
      }

      a:hover {
        text-decoration: none;
      }

      strong {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 700;
        font-size: 13px;
        line-height: 20px;
        color: rgb(34, 34, 34);
      }

      .section {
        margin-bottom: 30px;
      }

      .section-title {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 700;
        font-size: 13px;
        line-height: 20px;
        margin-bottom: 15px;
        color: rgb(34, 34, 34);
      }

      ol {
        padding-left: 35px;
      }

      ol li {
        margin-bottom: 15px;
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 20px;
        color: rgb(34, 34, 34);
      }

      .contact-info {
        margin-top: 40px;
        padding-top: 30px;
        border-top: 1px solid #eeeeee;
      }

      .footer {
        margin-top: 30px;
        text-align: left;
        font-family: Georgia, serif;
        font-weight: 400;
        font-size: 18px;
        line-height: 27px;
        color: rgb(0, 0, 0);
      }

      .tagline {
        font-family: Georgia, serif;
        font-weight: 600;
        font-size: 18px;
        line-height: 27px;
        font-style: italic;
        color: rgb(0, 0, 0);
        margin-top: 20px;
      }

      .bbr-brands a {
        font-size: 18px;
        line-height: 27px;
        text-decoration: underline;
        color: rgb(17, 85, 204);
      }

      .bbr-brands a.email {
        font-family: Georgia, serif;
        font-weight: 400;
      }

      .bbr-brands a.website {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 700;
      }

      .number {
        font-family: Georgia, serif;
        font-weight: 700;
        font-size: 18px;
        line-height: 27px;
        color: rgb(0, 0, 0);
      }
      .brand-name {
        font-family: Georgia, serif;
        font-weight: 700;
        font-size: 18px;
        line-height: 27px;
        color: rgb(0, 0, 0);
      }

      @media (max-width: 600px) {
        .container {
          padding: 20px 15px;
        }

        h1 {
          font-size: 13px;
        }

        .section-title {
          font-size: 13px;
        }
      }
    </style>
  </head>

  <body>
    <div class="container">
      <div class="logo">
        <img
          align="center"
          class="icon"
          height="128"
          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
          style="
            display: block;
            height: auto;
            margin: 0 auto;
            border: 0;
            background-color: #121212;
          "
          width="256"
        />
      </div>

      <h1>Hi ${name},</h1>

      <p>
        Welcome to the BBR Boat Rentals community! We are thrilled that you chose to sign up and rent through our booking platform!
      </p>
      
      <p>
        In this welcome email, you will find tips on how to navigate the platform and to get the most out of your upcoming rental experience.
      </p>

      <p>
        If you are using your phone, we recommend you download our App available on both Google Play and the Apple App store. We recommend you update the app frequently to catch our latest developer updates. Here's a quick link to download the App.
      </p>

      <p>
        <a href="${process.env.REACT_APP_BASE_URL}/download-app" target="_blank"
          >BBR Boat Rentals - Download App</a
        >
      </p>

      <div class="section">
        <p>
          To help you get started, we've created a quick series of Videos that explain some of the basics of renting and for listing your own boat for rent, if that's something you would like to explore. If you still have questions, we can help via phone, text or email.
        </p>

        <p>
          <a href="${process.env.REACT_APP_BASE_URL}/how-it-works" target="_blank"
            >BBR Boat Rentals - how it works</a
          >
        </p>
      </div>

      <div class="section">
        <p>
          The following is an important link to online boater safety education courses pertinent to the state in which you are renting a boat. For example, if you plan to be the driver of the boat that you rented in Texas you will need to be 21 years old to drive, if you were born after Sept 1st, 1993, you will also need to have a boaters education certificate. Please follow this link:
        </p>
      </div>

      <p>State Specific Boating Safety Courses: <a href="https://www.boatus.org/free" target="_blank">Boat US Foundation</a></p>

      <div class="section">
        <div class="section-title">
          What happens after I send the booking inquiry?
        </div>

        <ol>
          <li>
            <strong>The Boat owner or fishing guide will respond to your inquiry,</strong> you will receive a text message notification from 1-866-671-2735, this is our notification number, that message will have a link taking you directly to your BBR dashboard. From there navigate to your INBOX and look for a new message.
          </li>

          <li>
            <strong>Respond Promptly</strong> - The message from the boat owner will contain an offer with your booking preference or a similar option, you will click on the VIEW OFFER button within the message. Here you will be able to accept the offer and follow the instructions to make payment. You can also message the boat owner if you have any further questions. Once paid, your DASHBOARD calendar will show the trip as pending waivers (blue color), which means you're locked in for the selected date. Before the rental starts, all passengers need to complete their liability waivers. You can share these with all passengers using the link provided in the booking within the calendar. To maintain clear communications and for your security, the BBR platform requires all communications between renter and boat owner to be made through the messaging inbox.
          </li>

          <li>
            <strong>Meeting the boat owner</strong> – If you are renting to drive (captain) the boat yourself, the Renter will be responsible for starting the trip through their dashboard calendar. Just click on the trip under the calendar date and click START TRIP. The renter driving the boat will follow the equipment checklist and captain's waiver on their phone and need to enter their credit card to hold a deposit before the trip will start in the system. The boat owner will answer all your questions on safety features and boat instructions i.e. life jackets, throwable PFD, kill switch, horn, whistle, fire extinguisher. Ask the owner to share local and state laws with you such as local boater safety bylaws, registration documents and a copy of the insurance card. If the boat owner is driving the boat for the renter, the boat owner will start the trip from their calendar booking.
          </li>

          <li>
            <strong>If you are a renter meeting a fishing guide or boat captain</strong> – Just show up at the agreed time and location found on the booking under your dashboard calendar. The guide or captain will start the trip from their phone, there's nothing else for you to do except sit back, relax and follow your guide's instructions.
          </li>

          <li>
            <strong>What happens when I return the boat I'm driving?</strong> - Return the boat at the agreed time and location, the boat owner will be there to meet you, if anything changes with the return schedule, just send a message to confirm with the boatowner through your messaging INBOX. The boat owner will be responsible for completing the trip in the system once you have returned. Make sure you collect all your possessions and take them with you. The boat owner will check the boat and equipment, if there are no issues, your credit card hold will be released. If there are any issues or you have forgotten something, the boat owner may contact you through the messaging system to let you know.
          </li>

          <li>
            <strong>What is VERIFICATION?</strong> - If you plan to rent a BASS Boat you will need to get verified, any other boat types do not require verification. To get verified as an experienced bass boat operator click on the "get verified" link on the website home page.
          </li>
        </ol>
      </div>

      <div class="section">
        <p>
          Before you go check out this link for some exciting new boats and fishing guide services recently added to our network:           <a href="${process.env.REACT_APP_BASE_URL}/listings" target="_blank">Listings</a>
        </p>
      </div>

      <div class="contact-info">
        <p>
          Need help? Have comments? Our team is here to support you every step of the way via phone, text or email.
        </p>

        <p>Thanks again for choosing BBR – here's to more time on the water!</p>

        <p>
          Thank you,<br />
          The BBR Team!
        </p>
      </div>

      <div class="footer">
        <strong class="brand-name">BBR Boat Rentals</strong>
        <br />
        96 Beach Walk Blvd, Suite #1255
        <br />
        Conroe, Texas 77304<br />
        <b class="number">Phone: 936-276-7787</b>

        <div class="bbr-brands">
          <a class="email" href="mailto:info@bbrboatrentals.com">Email</a><br />
          <a class="website" href="${process.env.REACT_APP_BASE_URL}">BassBoatRentals.com</a>
        </div>

        <p class="tagline"><em>Your Boat. Your Trip. Your Way.</em></p>

        <div style="margin-top: 20px">
          <img
            align="left"
            class="icon"
            height="128"
            src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
            style="
              display: block;
              height: auto;
              margin: 0 auto;
              border: 0;
              background-color: #121212;
            "
            width="256"
          />
        </div>
      </div>
    </div>
  </body>
</html>
  `;
}

export async function welcomeOwnerEmailBody(name) {
  return `
  <!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <meta charset="UTF-8" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Welcome to BBR Boat Rentals!</title>
    <style>
      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        padding: 0;
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 20px;
        color: rgb(34, 34, 34);
        background-color: #ffffff;
      }

      .container {
        max-width: 100%;
        margin: 0 auto;
        padding: 40px 20px;
      }

      .logo {
        text-align: center;
        margin-bottom: 40px;
      }

      .logo img {
        max-width: 200px;
        height: auto;
      }

      h1 {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 700;
        font-size: 13px;
        line-height: 20px;
        margin: 0 0 20px 0;
        color: rgb(34, 34, 34);
      }

      p {
        margin: 0 0 16px 0;
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 20px;
        color: rgb(34, 34, 34);
      }

      a {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 20px;
        color: rgb(17, 85, 204);
        text-decoration: underline;
      }

      a:hover {
        text-decoration: none;
      }

      strong {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 700;
        font-size: 13px;
        line-height: 20px;
        color: rgb(34, 34, 34);
      }

      .section {
        margin-bottom: 30px;
      }

      .section-title {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 700;
        font-size: 13px;
        line-height: 20px;
        margin-bottom: 15px;
        color: rgb(34, 34, 34);
      }

      ol {
        padding-left: 35px;
      }

      ol li {
        margin-bottom: 15px;
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 400;
        font-size: 13px;
        line-height: 20px;
        color: rgb(34, 34, 34);
      }

      .contact-info {
        margin-top: 40px;
        padding-top: 30px;
        border-top: 1px solid #eeeeee;
      }

      .footer {
        margin-top: 30px;
        text-align: left;
        font-family: Georgia, serif;
        font-weight: 400;
        font-size: 18px;
        line-height: 27px;
        color: rgb(0, 0, 0);
      }

      .tagline {
        font-family: Georgia, serif;
        font-weight: 600;
        font-size: 18px;
        line-height: 27px;
        font-style: italic;
        color: rgb(0, 0, 0);
        margin-top: 20px;
      }

      .bbr-brands a {
        font-size: 18px;
        line-height: 27px;
        text-decoration: underline;
        color: rgb(17, 85, 204);
      }

      .bbr-brands a.email {
        font-family: Georgia, serif;
        font-weight: 400;
      }

      .bbr-brands a.website {
        font-family: Arial, Helvetica, sans-serif;
        font-weight: 700;
      }

      .number {
        font-family: Georgia, serif;
        font-weight: 700;
        font-size: 18px;
        line-height: 27px;
        color: rgb(0, 0, 0);
      }
      .brand-name {
        font-family: Georgia, serif;
        font-weight: 700;
        font-size: 18px;
        line-height: 27px;
        color: rgb(0, 0, 0);
      }

      @media (max-width: 600px) {
        .container {
          padding: 20px 15px;
        }

        h1 {
          font-size: 13px;
        }

        .section-title {
          font-size: 13px;
        }
      }
    </style>
  </head>

  <body>
    <div class="container">
      <div class="logo">
        <img
          align="center"
          class="icon"
          height="128"
          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
          style="
            display: block;
            height: auto;
            margin: 0 auto;
            border: 0;
            background-color: #121212;
          "
          width="256"
        />
      </div>

      <h1>Hi ${name},</h1>

      <p>
        Welcome to the BBR Boat Rentals community – we're excited that you chose
        to sign up and list your boat through our platform!
      </p>
      <p>
        This welcome email will provide some guidelines and tips to help you get
        started with managing your boat listing.
      </p>

      <p>
        If you are using your phone, we recommend you download our App available
        on both Google Play and the Apple App store. Here's a quick link to
        download the App.
      </p>

      <p>
        <a href="${process.env.REACT_APP_BASE_URL}/download-app" target="_blank"
          >BBR Boat Rentals - Download App</a
        >
      </p>

      <div class="section">
        <p>
          To help you get started, we've created a quick series of
          <strong>Videos</strong> that walk you through some of the basics of
          renting and for listing your own boat for rent, if that's something
          you would like to explore. If you still have questions, we can help
          via phone, text or email.
        </p>

        <p>
          <a href="${process.env.REACT_APP_BASE_URL}/how-it-works" target="_blank"
            >BBR Boat Rentals - how it works</a
          >
        </p>
      </div>

      <div class="section">
        <p>
          The following is an important link to online boater safety education
          courses pertinent to the state in which you are renting a boat. If you
          plan to be the driver of the boat, please follow this link:
        </p>
      </div>

      <p>State Specific Boating Safety Courses: <a href="https://www.boatus.org/free" target="_blank">BoatUSFoundation</a></p>

      <div class="section">
        <div class="section-title">
          What to Do When You Receive a Booking Inquiry:
        </div>

        <ol>
          <li>
            <strong
              >You will receive a text message notification from
              1-866-671-2735,</strong
            >
            this is our notification number, the message will have a link taking
            you directly to your BBR dashboard. From there you navigate to your
            INBOX and look for a new message.
          </li>

          <li>
            <strong>Respond Promptly</strong> - The message from the renter will
            contain their booking preference, you will click on the CREATE OFFER
            button within the message. Here you will be able to offer discounts
            or add $ for additional services such as delivery or for additional
            passengers. The renter will respond by accepting the offer and
            paying for the trip. Once paid, your dashboard calendar will show
            the trip as pending waivers (blue color), which means you're locked
            in for the selected date. You can still use your messaging inbox to
            communicate any additional information to the renter. To maintain
            clear communications and to protect you for liability, security and
            safety, the BBR platform requires all communication to be made
            through the messaging inbox.
          </li>

          <li>
            <strong>Prepare Your Boat</strong> – Ensure your boat is clean,
            fueled, and ready with all required safety equipment per state
            regulations. If you're a guide service meeting a client you've got
            this down already!
          </li>

          <li>
            <strong
              >If you are a renter meeting a fishing guide or boat
              captain</strong
            >
            – Just show up at the agreed time and location found on the booking
            under your dashboard calendar. The guide will start the trip from
            their phone, there's nothing else for you to do but kick back, relax
            and follow your guide's instructions.
          </li>

          <li>
            <strong>Boat owner meeting your Renter</strong> - If the renter is
            driving the boat, the Renter needs to start the trip from their
            dashboard after they have completed liability waivers for all
            passengers. They will click the start trip button after clicking on
            the calendar booking. The renter driving the boat will follow the
            equipment checklist and captain's waiver on their phone and need to
            have their credit card to hold a deposit before the trip will start
            in the system. Your job as a boat owner is to ensure you share all
            safety features and boat instructions i.e. lifejackets, throwable
            PFD, kill switch, horn, whistle, fire extinguisher. Share local and
            state laws with the renter such as local boater safety bylaws,
            registration documents and a copy of your insurance card. If the
            boat owner is driving the boat for the renter, the boatowner will
            start the trip from their calendar booking.
          </li>

          <li>
            <strong>Guides meeting your client</strong> - you will start the
            trip and end the trip from your calendar booking, click on that
            booking and click on START TRIP. Also make sure you share the
            digital liability waivers with the client before they step aboard
            your boat.
          </li>

          <li>
            <strong> To end the trip and get paid</strong> - both boat owners
            and guides will be responsible for completing the trip once the trip
            has returned. Click on the calendar booking which is yellow at this
            time and follow the prompts to END TRIP. To get paid you will click
            on payouts in your dashboard and request a withdrawal for the
            available payout amount. Payouts may take between 2 and 10 days to
            process into your bank account.
          </li>
        </ol>
      </div>

      <div class="contact-info">
        <p>
          Need help? Have comments? Our team is here to support you every step
          of the way via phone, text or email.
        </p>

        <p>Thanks again for choosing BBR – here's to more time on the water!</p>

        <p>
          Best,<br />
          The BBR Team!
        </p>
      </div>

      <div class="footer">
        <strong class="brand-name">BBR Boat Rentals</strong>
        <br />
        96 Beach Walk Blvd, Suite #1255
        <br />
        Conroe, Texas 77304<br />
        <b class="number">Phone: 936-276-7787</b>

        <div class="bbr-brands">
          <a class="email" href="mailto:info@bbrboatrentals.com">Email</a><br />
          <a class="website" href="${process.env.REACT_APP_BASE_URL}">BassBoatRentals.com</a>
        </div>

        <p class="tagline"><em>Your Boat. Your Trip. Your Way.</em></p>

        <div style="margin-top: 20px">
          <img
            align="left"
            class="icon"
            height="128"
            src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
            style="
              display: block;
              height: auto;
              margin: 0 auto;
              border: 0;
              background-color: #121212;
            "
            width="256"
          />
        </div>
      </div>
    </div>
  </body>
</html>

  `;
}

// export async function welcomeOwnerEmailBody(name) {
//   return `
//   <!DOCTYPE html><html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office"><head>
// <!--[if gte mso 15]>
// <xml>
// <o:OfficeDocumentSettings>
// <o:AllowPNG/>
// <o:PixelsPerInch>96</o:PixelsPerInch>
// </o:OfficeDocumentSettings>
// </xml>
// <![endif]-->
// <meta charset="UTF-8">
// <meta http-equiv="X-UA-Compatible" content="IE=edge">
// <meta name="viewport" content="width=device-width, initial-scale=1">
// <title>Welcome to BBR boat rentals!</title>
// <link rel="preconnect" href="https://fonts.googleapis.com">
// <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="">
//     <!--<![endif]-->
//     <style>
//       * {
//         box-sizing: border-box;
//       }

//       body {
//         margin: 0;
//         padding: 0;
//       }

//       a[x-apple-data-detectors] {
//         color: inherit !important;
//         text-decoration: inherit !important;
//       }

//       #MessageViewBody a {
//         color: inherit;
//         text-decoration: none;
//       }

//       p {
//         line-height: inherit
//       }

//       .desktop_hide,
//       .desktop_hide table {
//         mso-hide: all;
//         display: none;
//         max-height: 0px;
//         overflow: hidden;
//       }

//       .image_block img+div {
//         display: none;
//       }

//       @media (max-width:700px) {
//         .desktop_hide table.icons-inner {
//           display: inline-block !important;
//         }

//         .icons-inner {
//           text-align: center;
//         }

//         .icons-inner td {
//           margin: 0 auto;
//         }

//         .mobile_hide {
//           display: none;
//         }

//         .row-content {
//           width: 100% !important;
//         }

//         .stack .column {
//           width: 100%;
//           display: block;
//         }

//         .mobile_hide {
//           min-height: 0;
//           max-height: 0;
//           max-width: 0;
//           overflow: hidden;
//           font-size: 0px;
//         }

//         .desktop_hide,
//         .desktop_hide table {
//           display: table !important;
//           max-height: none !important;
//         }

//         .row-1 .column-1 .block-3.paragraph_block td.pad>div {
//           font-size: 16px !important;
//         }

//         .row-1 .column-1 .block-2.heading_block h1 {
//           font-size: 24px !important;
//         }

//         .row-1 .column-1 {
//           padding: 40px 20px 30px !important;
//         }
//       }
//     </style>
//   </head>

//   <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
//     <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
//       style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
//       <tbody>
//         <tr>
//           <td>
//             <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
//               role="presentation"
//               style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
//               width="100%">
//               <tbody>
//                 <tr>
//                   <td>
//                     <table align="center" border="0" cellpadding="0" cellspacing="0"
//                       class="row-content stack" role="presentation"
//                       style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
//                       width="680">
//                       <tbody>
//                         <tr>
//                           <td class="column column-1"
//                             style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
//                             width="100%">
//                             <table border="0" cellpadding="0" cellspacing="0"
//                               class="icons_block block-1" role="presentation"
//                               style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
//                               width="100%">
//                               <tr>
//                                 <td class="pad"
//                                   style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
//                                   <table align="center" cellpadding="0" cellspacing="0"
//                                     class="alignment" role="presentation"
//                                     style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
//                                     <tr>
//                                       <td
//                                         style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
//                                         <img align="center" class="icon"
//                                           height="128"
//                                           src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
//                                           style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
//                                           width="256" /></td>
//                                     </tr>
//                                   </table>
//                                 </td>
//                               </tr>
//                             </table>
//                             <table border="0" cellpadding="10" cellspacing="0"
//                               class="heading_block block-2" role="presentation"
//                               style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
//                               width="100%">
//                               <tr>
//                                 <td class="pad">
//                                   <h1
//                                     style="margin: 0; color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: left; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;">
//                                     <span class="tinyMce-placeholder">Hi ${name},</span></h1>
//                                 </td>
//                               </tr>
//                             </table>
//                             <table border="0" cellpadding="0" cellspacing="0"
//                               class="paragraph_block block-3" role="presentation"
//                               style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
//                               width="100%">
//                               <tr>
//                                 <td class="pad"
//                                   style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
//                                   <div
//                                     style="color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
//                                     <p style="margin: 0; margin-bottom: 16px;">Welcome to the BBR Boat Rental community! We are thrilled to have your boat listed on our platform!</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">This welcome email will provide some guidelines and tips to help you get started with managing your boat listing.</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">If you are using your phone, we recommend you download our App available on both Google Play and the Apple App store. Here's a quick link to download the App.</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">BBR Boat Rentals - Download App</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">To help you get started, we've created a quick series of Videos that walk you through some of the basics. If you still have questions, we can help via phone, text or email.</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">BBR Boat Rentals - how it works</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">The following is an important link to online boater safety education courses pertinent to the state in which you are renting a boat. If you plan to be the driver of the boat, please follow this link:</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">State Specific Boating Safety Courses: BoatUSFoundation</p>

//                                     <p style="margin: 0; margin-bottom: 16px;"><strong>What to Do When You Receive a Booking Inquiry:</strong></p>

//                                     <p style="margin: 0; margin-bottom: 16px;">1.    You will receive a text message notification from 1-866-671-2735, this is our notification number, the message will have a link taking you directly to your BBR dashboard. From there you navigate to your INBOX and look for a new message.</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">2.    Respond Promptly - The message from the renter will contain their booking preference, you will click on the CREATE OFFER button within the message. Here you will be able to offer discounts or add $ for additional services such as delivery or for additional passengers. The renter will respond by accepting the offer and paying for the trip. Once paid, your dashboard calendar will show the trip as pending waivers (blue color), which means you're locked in for the selected date. You can still use your messaging inbox to communicate any additional information to the renter. To maintain clear communications and to protect you for liability, security and safety, the BBR platform requires all communication to be made through the messaging inbox.</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">3.    Prepare Your Boat – Ensure your boat is clean, fueled, and ready with all required safety equipment per state regulations. If you're a guide service meeting a client you've got this down already!</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">4.    Boat owner meeting your Renter – If the renter is driving the boat, the Renter needs to start the trip from their dashboard after they have completed liability waivers for all passengers. They will click the start trip button after clicking on the calendar booking. The renter driving the boat will follow the equipment checklist and captain's waiver on their phone and need to have their credit card to hold a deposit before the trip will start in the system. Your job as a boat owner is to ensure you share all safety features and boat instructions i.e. lifejackets, throwable PFD, kill switch, horn, whistle, fire extinguisher. Share local and state laws with the renter such as local boater safety bylaws, registration documents and a copy of your insurance card. If the boat owner is driving the boat for the renter, the boatowner will start the trip from their calendar booking.</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">5.    Guides meeting your client – you will start the trip and end the trip from your calendar booking, click on that booking and click on START TRIP. Also make sure you share the digital liability waivers with the client before they step aboard your boat.</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">6.    To end the trip and get paid - both boat owners and guides will be responsible for completing the trip once the trip has returned. Click on the calendar booking which is yellow at this time and follow the prompts to END TRIP. To get paid you will click on payouts in your dashboard and request a withdrawal for the available payout amount. Payouts may take between 2 and 10 days to process into your bank account.</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">Before you go, check out this link for some exciting new boats and fishing guide services recently added to our network:</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">Listings</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">Need help? Have comments? Our team is here to support you every step of the way via phone, text or email.</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">Phone: 936-276-7787</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">Link to Email</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">Link to website BBRBoatRentals.com</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">Thanks again for choosing BBR – here's to more time on the water and some extra income too!</p>

//                                     <p style="margin: 0; margin-bottom: 16px;">Thank you,<br>The BBR Team!</p>

//                                     <p style="margin: 0;">Your Boat. Your Trip. Your Way.</p>
//                                   </div>
//                                 </td>
//                               </tr>
//                             </table>
//                           </td>
//                         </tr>
//                       </tbody>
//                     </table>
//                   </td>
//                 </tr>
//               </tbody>
//             </table>
//           </td>
//         </tr>
//       </tbody>
//     </table><!-- End -->
//   </body>

//   </html>
//   `;
// }

export async function newListingAddedAdminEmailBody(
  ownerName,
  bbrId,
  listingName,
  boatType
) {
  return `
	<!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }
  
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad">
                                  <h1
                                    style="margin: 0; color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: left; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;">
                                    <span class="tinyMce-placeholder">Hi Admin,</span></h1>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <p style="margin: 0; margin-bottom: 16px;">${ownerName} - ${bbrId} just added new ${boatType} Boat Listing:</p>
                                    <p style="margin: 0; margin-bottom: 16px;">Listing Name: ${listingName}</p>
                                    <p style="margin: 0; margin-bottom: 16px;">Boat Type: ${boatType}</p>
                                    <p style="margin: 0; margin-bottom: 16px;">Please login into your admin account to activate listing.</p>
                                    <p style="margin: 0; margin-bottom: 16px;"> </p>
                                    <p style="margin: 0;">Best Regards.</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>
	`;
}

export async function widthrawalRequestToAdminEmailBody() {
  return `
	<!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }
  
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad">
                                  <h1
                                    style="margin: 0; color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: left; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;">
                                    <span class="tinyMce-placeholder">Hi Admin,</span></h1>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <p style="margin: 0; margin-bottom: 16px;">You have received a new withdrawal request.</p>
                                    <p style="margin: 0; margin-bottom: 16px;">Please login into your admin account to review and process the withdrawal request.</p>
                                    <p style="margin: 0; margin-bottom: 16px;"> </p>
                                    <p style="margin: 0;">Best Regards.</p>
                                    <p style="margin: 0;">BBR BOATRENTALS</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>
	`;
}

export async function newUserAddedAdminEmailBody(userRole, bbrId, name, email) {
  return `<!DOCTYPE html>

	<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
	
	<head>
	  <title></title>
	  <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	  <meta content="width=device-width, initial-scale=1.0" name="viewport" />
	  <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	  <!--[if !mso]><!-->
	  <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	  <!--<![endif]-->
	  <style>
		* {
		  box-sizing: border-box;
		}
	
		body {
		  margin: 0;
		  padding: 0;
		}
	
		a[x-apple-data-detectors] {
		  color: inherit !important;
		  text-decoration: inherit !important;
		}
	
		#MessageViewBody a {
		  color: inherit;
		  text-decoration: none;
		}
	
		p {
		  line-height: inherit
		}
	
		.desktop_hide,
		.desktop_hide table {
		  mso-hide: all;
		  display: none;
		  max-height: 0px;
		  overflow: hidden;
		}
	
		.image_block img+div {
		  display: none;
		}
	
		@media (max-width:700px) {
		  .desktop_hide table.icons-inner {
			display: inline-block !important;
		  }
	
		  .icons-inner {
			text-align: center;
		  }
	
		  .icons-inner td {
			margin: 0 auto;
		  }
	
		  .mobile_hide {
			display: none;
		  }
	
		  .row-content {
			width: 100% !important;
		  }
	
		  .stack .column {
			width: 100%;
			display: block;
		  }
	
		  .mobile_hide {
			min-height: 0;
			max-height: 0;
			max-width: 0;
			overflow: hidden;
			font-size: 0px;
		  }
	
		  .desktop_hide,
		  .desktop_hide table {
			display: table !important;
			max-height: none !important;
		  }
	
		  .row-1 .column-1 .block-3.paragraph_block td.pad>div {
			font-size: 16px !important;
		  }
	
		  .row-1 .column-1 .block-2.heading_block h1 {
			font-size: 24px !important;
		  }
	
		  .row-1 .column-1 {
			padding: 40px 20px 30px !important;
		  }
		}
	  </style>
	</head>
	
	<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	  <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
		  <tr>
			<td>
			  <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
				role="presentation"
				style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
				width="100%">
				<tbody>
				  <tr>
					<td>
					  <table align="center" border="0" cellpadding="0" cellspacing="0"
						class="row-content stack" role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
						width="680">
						<tbody>
						  <tr>
							<td class="column column-1"
							  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
							  width="100%">
							  <table border="0" cellpadding="0" cellspacing="0"
								class="icons_block block-1" role="presentation"
								style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
								width="100%">
								<tr>
								  <td class="pad"
									style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
									<table align="center" cellpadding="0" cellspacing="0"
									  class="alignment" role="presentation"
									  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
									  <tr>
										<td
										  style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
										  <img align="center" class="icon"
											height="128"
											src="https://dev.bbrboatrentals.com/assets/backend/images/logo.png"
											style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
											width="256" /></td>
									  </tr>
									</table>
								  </td>
								</tr>
							  </table>
							  <table border="0" cellpadding="10" cellspacing="0"
								class="heading_block block-2" role="presentation"
								style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
								width="100%">
								<tr>
								  <td class="pad">
									<h1
									  style="margin: 0; color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: left; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;">
									  <span class="tinyMce-placeholder">Hi Admin,</span></h1>
								  </td>
								</tr>
							  </table>
							  <table border="0" cellpadding="0" cellspacing="0"
								class="paragraph_block block-3" role="presentation"
								style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
								width="100%">
								<tr>
								  <td class="pad"
									style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
									<div
									  style="color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
									  <p style="margin: 0; margin-bottom: 16px;">New ${userRole} just signed up</p>
									  <p style="margin: 0; margin-bottom: 16px;">BBR ID: ${bbrId}</p>
									  <p style="margin: 0; margin-bottom: 16px;">Name: ${name}</p>
									  <p style="margin: 0; margin-bottom: 16px;">Email: ${email}</p>
									  <p style="margin: 0; margin-bottom: 16px;"> </p>
									  <p style="margin: 0;">Best Regards.</p>
									  <p style="margin: 0;">BBR BOATRENTALS</p>
									</div>
								  </td>
								</tr>
							  </table>
							</td>
						  </tr>
						</tbody>
					  </table>
					</td>
				  </tr>
				</tbody>
			  </table>
			</td>
		  </tr>
		</tbody>
	  </table><!-- End -->
	</body>
	
	</html>`;
}

export async function clientVerificationEmail(name) {
  return `<!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }
  
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                            
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <p style="text-align: center;margin: 0; margin-bottom: 16px;">Your application has been submitted and copies of                                                                     </p>
                                    
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                         RENTER VERIFICATION
                                                                      </span></h1>
                                                                      <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          ${name}
                                                                      </span></h1>
                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                          has been sent to BBR for approval, we will contact you shortly!
  If you have not heard back from us within 24 hours please send a follow up email to <a style=" color: #ffffff;" href="mailto:waivers@bbrboatrentals.com">waivers@bbrboatrentals.com</a>
  </p>
                                    <p style="text-align: center;margin: 0;">Thank you!</p>
                                    <p style="text-align: center;margin: 0;">Best Regards.</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>`;
}
export async function adminVerificationEmail(name) {
  return `<!DOCTYPE html>

	<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
	
	<head>
	  <title></title>
	  <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	  <meta content="width=device-width, initial-scale=1.0" name="viewport" />
	  <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	  <!--[if !mso]><!-->
	  <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	  <!--<![endif]-->
	  <style>
		* {
		  box-sizing: border-box;
		}
	
		body {
		  margin: 0;
		  padding: 0;
		}
	
		a[x-apple-data-detectors] {
		  color: inherit !important;
		  text-decoration: inherit !important;
		}
	
		#MessageViewBody a {
		  color: inherit;
		  text-decoration: none;
		}
	
		p {
		  line-height: inherit
		}
	
		.desktop_hide,
		.desktop_hide table {
		  mso-hide: all;
		  display: none;
		  max-height: 0px;
		  overflow: hidden;
		}
	
		.image_block img+div {
		  display: none;
		}
	
		@media (max-width:700px) {
		  .desktop_hide table.icons-inner {
			display: inline-block !important;
		  }
	
		  .icons-inner {
			text-align: center;
		  }
	
		  .icons-inner td {
			margin: 0 auto;
		  }
	
		  .mobile_hide {
			display: none;
		  }
	
		  .row-content {
			width: 100% !important;
		  }
	
		  .stack .column {
			width: 100%;
			display: block;
		  }
	
		  .mobile_hide {
			min-height: 0;
			max-height: 0;
			max-width: 0;
			overflow: hidden;
			font-size: 0px;
		  }
	
		  .desktop_hide,
		  .desktop_hide table {
			display: table !important;
			max-height: none !important;
		  }
	
		  .row-1 .column-1 .block-3.paragraph_block td.pad>div {
			font-size: 16px !important;
		  }
	
		  .row-1 .column-1 .block-2.heading_block h1 {
			font-size: 24px !important;
		  }
	
		  .row-1 .column-1 {
			padding: 40px 20px 30px !important;
		  }
		}
	  </style>
	</head>
	
	<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	  <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
		  <tr>
			<td>
			  <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
				role="presentation"
				style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
				width="100%">
				<tbody>
				  <tr>
					<td>
					  <table align="center" border="0" cellpadding="0" cellspacing="0"
						class="row-content stack" role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
						width="680">
						<tbody>
						  <tr>
							<td class="column column-1"
							  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
							  width="100%">
							  <table border="0" cellpadding="0" cellspacing="0"
								class="icons_block block-1" role="presentation"
								style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
								width="100%">
								<tr>
								  <td class="pad"
									style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
									<table align="center" cellpadding="0" cellspacing="0"
									  class="alignment" role="presentation"
									  style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
									  <tr>
										<td
										  style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
										  <img align="center" class="icon"
											height="128"
											src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
											style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
											width="256" /></td>
									  </tr>
									</table>
								  </td>
								</tr>
							  </table>
							  <table border="0" cellpadding="10" cellspacing="0"
								class="heading_block block-2" role="presentation"
								style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
								width="100%">
								<tr>
								  <td class="pad">
									<h1
									  style="text-align: center; margin: 0; color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;">
									  Hi Admin,</h1>
								  </td>
								</tr>
							  </table>
							  <table border="0" cellpadding="0" cellspacing="0"
								class="paragraph_block block-3" role="presentation"
								style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
								width="100%">
								<tr>
								  <td class="pad"
									style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
									<div
									  style="color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
									  <p style="text-align: center; margin: 0; margin-bottom: 16px;">${name} has submitted a verification request!</p>
                    <p style="text-align: center; margin: 0; margin-bottom: 16px;">Please login into your admin account to verify user</p>
									  <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
									  </p>
									</div>
								  </td>
								</tr>
							  </table>
							</td>
						  </tr>
						</tbody>
					  </table>
					</td>
				  </tr>
				</tbody>
			  </table>
			</td>
		  </tr>
		</tbody>
	  </table><!-- End -->
	</body>
	
	</html>`;
}

export async function RenterVerificationApprovedEmailBody(name, url) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        CONGRATULATIONS!<br> RENTER VERIFICATION
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${name}
                                                                    </span></h1>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                        has been approved, your account has been updated accordingly.
                                                                        On behalf of the entire team here at BBR, 
                                                                        <br>"Welcome and have fun out there!"                                                                        
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0; font-size: 16px;">If you need a signed copy of this waiver for your records you may download it <a style="color: #ffffff;" href="${url}" target="_blank">here</a>.</p>
																	<p style="text-align: center;margin: 0;">Thank you!</p>
																	<p style="text-align: center;margin: 0;">Best Regards.</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function RenterVerificationRejectedEmailBody(name, url, reason) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
                                                                style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                                                <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Thank you for taking the time to complete the renter verification form. Unfortunately the</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        RENTER VERIFICATION
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${name}
                                                                    </span></h1>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                        has not been approved. Your account has been updated accordingly. 

                                                                    </p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                        The following list of reasons were given to establish this decision: ${reason}                                                                  
                                                                    </p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                        The BBR team are all bass fishing enthusiasts who would like to get you on the water safely. If you believe an error was made on the application process, and you would like us to challenge the decision by providing additional or missing information, please contact a member of our team at the following email address <a href="mailto:waivers@bbrboatrentals.com" target="_blank" style="color: #ffffff;">waivers@bbrboatrentals.com</a>
                                                                    </p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                        Although your verification has not yet been approved, you are still eligible to request a rental with a qualified boat captain, or you can contact boat owners through the booking portal to inquire if they are willing to accept your non-approved verification.

On behalf of the entire team here at BBR, we appreciate your business, 
<br>"Welcome to BBR and have fun out there!"

                                                                    </p>
                                                                    <p style="text-align: center;margin: 0; font-size: 16px;">If you need a signed copy of this waiver for your records you may download it <a style="color: #ffffff;" href="${url}" target="_blank">here</a>.</p>
																	<p style="text-align: center;margin: 0;">Thank you!</p>
																	<p style="text-align: center;margin: 0;">Best Regards.</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function OwnerVerificationApprovedEmailBody(name, url) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        CONGRATULATIONS!<br> BOAT OWNER VERIFICATION
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${name}
                                                                    </span></h1>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                        has been approved, your account has been updated accordingly.
                                                                        On behalf of the entire team here at BBR, 
                                                                        <br>"Welcome and have fun out there!"                                                                        
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0; font-size: 16px;">If you need a signed copy of this waiver for your records you may download it <a style="color: #ffffff;" href="${url}" target="_blank">here</a>.</p>
																	<p style="text-align: center;margin: 0;">Thank you!</p>
																	<p style="text-align: center;margin: 0;">Best Regards.</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function OwnerVerificationRejectedEmailBody(name, url, reason) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
                                                                style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                                                <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Thank you for taking the time to complete the Boat Owner verification form. Unfortunately the</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        BOAT OWNER VERIFICATION
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${name}
                                                                    </span></h1>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                        has not been approved. Your account has been updated accordingly. 

                                                                    </p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                        The following list of reasons were given to establish this decision: ${reason}                                                                  
                                                                    </p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                        If you believe an error was made on the application process, and you would like us to challenge the decision by providing additional or missing information, please contact a member of our team at the following email address <a href="mailto:waivers@bbrboatrentals.com" target="_blank" style="color: #ffffff;">waivers@bbrboatrentals.com</a>
                                                                    </p>
                                                                   
                                                                    <p style="text-align: center;margin: 0; font-size: 16px;">If you need a signed copy of this waiver for your records you may download it <a style="color: #ffffff;" href="${url}" target="_blank">here</a>.</p>
																	<p style="text-align: center;margin: 0;">Thank you!</p>
																	<p style="text-align: center;margin: 0;">Best Regards.</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function InquirySentEmailBody(boatOwnerName, listingTitle) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       CONGRATULATONS INQUIRY SENT!
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Your inquiry has been successfully sent to</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${boatOwnerName} <sub style="font-size: 20px;">Boat Owner</sub>
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${listingTitle}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		Kindly await the boat owner's response, as they will get back to you with an offer. If you have any further questions or concerns, please feel free to reach out.<br>
																		<br>On behalf of the entire team here at BBR,                                                                   
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function InquiryReceivedEmailBody(renterName, listingTitle) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       NEW INQUIRY RECEIVED
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">from</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${renterName}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${listingTitle}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		On behalf of the entire team here at BBR,                                                                      
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function NewOfferRecievedEmailBody(ownerName, listingTitle) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       NEW OFFER RECEIVED
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">from</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${ownerName}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${listingTitle}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		On behalf of the entire team here at BBR,                                                                     
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function sendWaiverBody(waiverName, url) {
  return `
  <!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }

      .btn:hover{
        background-color: #e7e7e7 !important;
      }
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                            
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <p style="text-align: center;margin: 0; margin-bottom: 16px;">You have been requested to submit  the                                                                     </p>
                                    
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                         ${waiverName} 
                                                                      </span></h1>
                                     <h1 style="  text-align: center; margin-top: 40px; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center;  mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                       <a target="_blank" class="btn" style="background-color:#fff;  padding: 6px 26px; border-radius: 8px; color: #000000; text-decoration: none" href="${url}">OPEN WAIVER </a>
                                    </span></h1>                       
                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 32px;  color: #ffffff;">
                                                                          <a style=" color: #ffffff;" href="mailto:waivers@bbrboatrentals.com">waivers@bbrboatrentals.com</a>
  </p>
                                    <p style="text-align: center;margin: 0;">Thank you!</p>
                                    <p style="text-align: center;margin: 0;">Best Regards.</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>
  `;
}

export async function OfferAccepetedRenterEmailBody(listingTitle) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       CONGRATULATIONS OFFER ACCEPTED!
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">You've accepeted offer for</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${listingTitle}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">To finalize your booking, kindly proceed with the payment.																	</p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		On behalf of the entire team here at BBR,                                                                       
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function OfferAccepetedOwnerEmailBody(listingTitle, renterName) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       CONGRATULATIONS!
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Your offer for</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${listingTitle}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">has been accepted by</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${renterName}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		On behalf of the entire team here at BBR,                                                                     
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function BookingStatusUpdateEmailBody(
  userName,
  bookingTitle,
  status
) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        Hi ${userName}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">your booking:</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${bookingTitle}
                                                                    </span></h1>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		is ${status}.<br>
                                                                        On behalf of the entire team here at BBR,                                                                     
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function BookingFormEmailBody(userName, bookingTitle, status) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        Hi ${userName}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">your booking:</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${bookingTitle}
                                                                    </span></h1>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		${status}.<br>
                                                                        On behalf of the entire team here at BBR,                                                                     
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function proMemberStatusBody(userName, status, referralCode) {
  return `
  <!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }
  
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                            
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          Hi ${userName}
                                                                      </span></h1>
                                                                      <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Your Pro Member Application has been ${status}, your referral code is:</p>
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          ${referralCode}
                                                                      </span></h1>
                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                          On behalf of the entire team here at BBR,                                                                     
                                                                      </p>
                                                                      <p style="text-align: center;margin: 0;">Thank you!</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>
  `;
}

export async function proMemberRejectedStatusBody(userName, status) {
  return `
  <!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }
  
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                            
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          Hi ${userName}
                                                                      </span></h1>
                                                                      <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Your Pro Member Application has been</p>
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          ${status}
                                                                      </span></h1>
                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                                                          On behalf of the entire team here at BBR,                                                                       
                                                                      </p>
                                                                      <p style="text-align: center;margin: 0;">Thank you!</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>
  `;
}

export async function GetVerifiedForBassBoat(userName) {
  return `
  <!DOCTYPE html>
  <html lang="en">
  
  <head>
    <title>Become a Pro Member</title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <style>
      body {
        margin: 0;
        padding: 0;
        background-color: #171620;
        font-family: Arial, sans-serif;
        color: #ffffff;
      }
      .container {
        width: 100%;
        max-width: 680px;
        margin: auto;
        text-align: center;
        padding: 40px;
        background-color: #222;
      }
      .logo img {
        max-width: 200px;
      }
      .content {
        margin-top: 20px;
        font-size: 18px;
        line-height: 1.6;
      }
      .content p {
         color: #ffffff;
      }
      .content h1 {
         color: #ffffff;
      }
      .button {
        display: inline-block;
        padding: 12px 24px;
        margin-top: 20px;
        font-size: 18px;
        font-weight: bold;
        color: #ffffff;
        background-color: #5555e7;
        text-decoration: none;
        border-radius: 5px;
      }
        .content a {
         color: #ffffff;
      }
    </style>
  </head>
  
  <body>
    <div class="container">
      <div class="logo">
        <img src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png" alt="BBR Boat Rentals Logo" style="background-color:#121212;"/>
      </div>
      <div class="content">
        <h1>Hi ${userName},</h1>
        <p>Please click the button below to get verified for the BBR Boat:</p>
        <a href="${process.env.REACT_APP_BASE_URL}/dashboard/get-verified" class="button">Get Verified</a>
        <p>Best Regards,<br>BBR Boat Rentals Team</p>
      </div>
    </div>
  </body>
  
  </html>`;
}

export async function GetEditedOfferForBassBoat(userName) {
  return `
  <!DOCTYPE html>
  <html lang="en">
  
  <head>
    <title>Become a Pro Member</title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <style>
      body {
        margin: 0;
        padding: 0;
        background-color: #171620;
        font-family: Arial, sans-serif;
        color: #ffffff;
      }
      .container {
        width: 100%;
        max-width: 680px;
        margin: auto;
        text-align: center;
        padding: 40px;
        background-color: #222;
      }
      .logo img {
        max-width: 200px;
      }
      .content {
        margin-top: 20px;
        font-size: 18px;
        line-height: 1.6;
      }
      .content p {
         color: #ffffff;
      }
      .content h1 {
         color: #ffffff;
      }
      .button {
        display: inline-block;
        padding: 12px 24px;
        margin-top: 20px;
        font-size: 18px;
        font-weight: bold;
        color: #ffffff;
        background-color: #5555e7;
        text-decoration: none;
        border-radius: 5px;
      }
        .content a {
         color: #ffffff;
      }
    </style>
  </head>
  
  <body>
    <div class="container">
      <div class="logo">
        <img src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png" alt="Bass Boat Rentals Logo" style="background-color:#121212;"/>
      </div>
      <div class="content">
        <h1>Hi ${userName},</h1>
        <p>The boat owner has edited an offer. Please review it via this email.</p>
        <a href="${process.env.REACT_APP_BASE_URL}/dashboard/inbox" class="button">View Offer</a>
        <p>Best Regards,<br>BBR Boat Rentals Team</p>
      </div>
    </div>
  </body>
  
  </html>`;
}

export async function becomeProMemberRequestSentEmailBody(userName) {
  return `
  <!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }
  
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                            
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          Hi ${userName}
                                                                      </span></h1>
                                                                      <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Thank you for submitting your request to become a pro staff member. We are currently in the process of verifying your answers.</p>
                                                                      <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Please note that due to the high volume of requests, this may take some time.</p>
                                                                      <p style="text-align: center;margin: 0;">Thank you!</p>
                                    <p style="text-align: center;margin: 0;">Best Regards.</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>`;
}

export async function becomeProMemberRequestAdminEmailBody(userName) {
  return `
  <!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }
  
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                            
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          Hi Admin
                                                                      </span></h1>
                                                                      <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">${userName} has expressed interest in becoming a pro staff member.</p>
                                                                      <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Please review pending pro staff requests from your dashboard.</p>
                                                                      <p style="text-align: center;margin: 0;">Thank you!</p>
                                    <p style="text-align: center;margin: 0;">Best Regards.</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>`;
}

export async function BookingCompletedWithDisputeOwnerEmailBody(
  boatOwner,
  bookingTitle,
  amount = 0,
  securityDeposit = 0
) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_NATIVE_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_NATIVE_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       Hi ${boatOwner}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">your booking</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${bookingTitle}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">is now completed, with dispute.</p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Security Deposit: ${securityDeposit}</p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Amount Charged: $${amount}</p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">You charged the renters credit card based upon your agreements with the renter, the renter has been advised. If you need to discuss this further with the renter, please discuss through your dashboard messaging inbox. Where you can send copies of receipts for any repairs or a full explanation of the reason for the additional charge. Neither BBR Boat Rentals or BBR Boat Rentals LLC are responsible for settling disputes between boat owners and renters.</p>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		On behalf of the entire team here at BBR,                                                                        
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function BookingCompletedWithDisputeRenterEmailBody(
  boatOwner,
  bookingTitle,
  amount,
  securityDeposit = 0
) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_NATIVE_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_NATIVE_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                       Hi ${boatOwner}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">your booking</p>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${bookingTitle}
                                                                    </span></p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">is now completed, with dispute.</p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Security Deposit: ${securityDeposit}</p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">Amount Charged: $${amount}</p>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">If you did not agree to this charge or need it adjusted, please communicate directly with the boat owner through your dashboard messaging inbox. Where you can request a copy of receipts for any repairs or a full explanation of the reason for the additional charge. Neither BBR Boat R entals or BBR Boat Rentals LLC are responsible for settling disputes between boat owners and renters.</p>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		On behalf of the entire team here at BBR,                                                                        
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function WaiverSignedEmailBody(
  waiverName,
  filerName,
  url,
  submissionID
) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        CONGRATULATIONS!<br> ${waiverName}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">for</p>
                                                                    <h4 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${filerName}
                                                                    </span></h4>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		has been received.<br>
                                                                        On behalf of the entire team here at BBR, 
                                                                        <br>"Welcome and have fun out there!"                                                                        
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0; font-size: 16px;">If you need a signed copy of this waiver for your records you may download it <a style="color: #ffffff;" href="${url}" target="_blank">here</a>. Or use your submission ID ${submissionID}</p>
																	<p style="text-align: center;margin: 0;">Thank you!</p>
																	<p style="text-align: center;margin: 0;">Best Regards.</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function tripStatusUpdateEmailBody(
  userName,
  bookingTitle,
  status
) {
  return `
  <!DOCTYPE html>

<html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">

<head>
	<title></title>
	<meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
	<meta content="width=device-width, initial-scale=1.0" name="viewport" />
	<!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
	<!--[if !mso]><!-->
	<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
		rel="stylesheet" type="text/css" />
	<!--<![endif]-->
	<style>
		* {
			box-sizing: border-box;
		}

		body {
			margin: 0;
			padding: 0;
		}

		a[x-apple-data-detectors] {
			color: inherit !important;
			text-decoration: inherit !important;
		}

		#MessageViewBody a {
			color: inherit;
			text-decoration: none;
		}

		p {
			line-height: inherit
		}

		.desktop_hide,
		.desktop_hide table {
			mso-hide: all;
			display: none;
			max-height: 0px;
			overflow: hidden;
		}

		.image_block img+div {
			display: none;
		}

		@media (max-width:700px) {
			.desktop_hide table.icons-inner {
				display: inline-block !important;
			}

			.icons-inner {
				text-align: center;
			}

			.icons-inner td {
				margin: 0 auto;
			}

			.mobile_hide {
				display: none;
			}

			.row-content {
				width: 100% !important;
			}

			.stack .column {
				width: 100%;
				display: block;
			}

			.mobile_hide {
				min-height: 0;
				max-height: 0;
				max-width: 0;
				overflow: hidden;
				font-size: 0px;
			}

			.desktop_hide,
			.desktop_hide table {
				display: table !important;
				max-height: none !important;
			}

			.row-1 .column-1 .block-3.paragraph_block td.pad>div {
				font-size: 16px !important;
			}

			.row-1 .column-1 .block-2.heading_block h1 {
				font-size: 24px !important;
			}

			.row-1 .column-1 {
				padding: 40px 20px 30px !important;
			}
		}
	</style>
</head>

<body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
	<table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
		style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
		<tbody>
			<tr>
				<td>
					<table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
						role="presentation"
						style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
						width="100%">
						<tbody>
							<tr>
								<td>
									<table align="center" border="0" cellpadding="0" cellspacing="0"
										class="row-content stack" role="presentation"
										style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
										width="680">
										<tbody>
											<tr>
												<td class="column column-1"
													style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
													width="100%">
													<table border="0" cellpadding="0" cellspacing="0"
														class="icons_block block-1" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
														<tr>
															<td class="pad"
																style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
																<table align="center" cellpadding="0" cellspacing="0"
																	class="alignment" role="presentation"
																	style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
																	<tr>
																		<td
																			style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
																			<img align="center" class="icon"
																				height="128"
																				src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
																				style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
																				width="256" /></td>
																	</tr>
																</table>
															</td>
														</tr>
													</table>
													<table border="0" cellpadding="10" cellspacing="0"
														class="heading_block block-2" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
														width="100%">
													
													</table>
													<table border="0" cellpadding="0" cellspacing="0"
														class="paragraph_block block-3" role="presentation"
														style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
														width="100%">
														<tr>
															<td class="pad"
																style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
																<div
																	style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        Hi ${userName}
                                                                    </span></h1>
                                                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">trip of your booking</p>
																	<h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                        ${bookingTitle}
                                                                    </span></h1>
																	<p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
																		is ${status}.<br>
                                                                        On behalf of the entire team here at BBR,                                                                   
                                                                    </p>
                                                                    <p style="text-align: center;margin: 0;">Thank you!</p>
																</div>
															</td>
														</tr>
													</table>
												</td>
											</tr>
										</tbody>
									</table>
								</td>
							</tr>
						</tbody>
					</table>
				</td>
			</tr>
		</tbody>
	</table><!-- End -->
</body>

</html>
  `;
}

export async function tripCompletedEmailBody(
  userName,
  bookingTitle,
  listingId,
  bookingId
) {
  return `
  <!DOCTYPE html>

  <html lang="en" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:v="urn:schemas-microsoft-com:vml">
  
  <head>
    <title></title>
    <meta content="text/html; charset=utf-8" http-equiv="Content-Type" />
    <meta content="width=device-width, initial-scale=1.0" name="viewport" />
    <!--[if mso]><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch><o:AllowPNG/></o:OfficeDocumentSettings></xml><![endif]-->
    <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Oswald:wght@100;200;300;400;500;600;700;800;900"
      rel="stylesheet" type="text/css" />
    <!--<![endif]-->
    <style>
      * {
        box-sizing: border-box;
      }
  
      body {
        margin: 0;
        padding: 0;
      }
  
      a[x-apple-data-detectors] {
        color: inherit !important;
        text-decoration: inherit !important;
      }
  
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
      }
  
      p {
        line-height: inherit
      }
  
      .desktop_hide,
      .desktop_hide table {
        mso-hide: all;
        display: none;
        max-height: 0px;
        overflow: hidden;
      }
  
      .image_block img+div {
        display: none;
      }
  
      @media (max-width:700px) {
        .desktop_hide table.icons-inner {
          display: inline-block !important;
        }
  
        .icons-inner {
          text-align: center;
        }
  
        .icons-inner td {
          margin: 0 auto;
        }
  
        .mobile_hide {
          display: none;
        }
  
        .row-content {
          width: 100% !important;
        }
  
        .stack .column {
          width: 100%;
          display: block;
        }
  
        .mobile_hide {
          min-height: 0;
          max-height: 0;
          max-width: 0;
          overflow: hidden;
          font-size: 0px;
        }
  
        .desktop_hide,
        .desktop_hide table {
          display: table !important;
          max-height: none !important;
        }
  
        .row-1 .column-1 .block-3.paragraph_block td.pad>div {
          font-size: 16px !important;
        }
  
        .row-1 .column-1 .block-2.heading_block h1 {
          font-size: 24px !important;
        }
  
        .row-1 .column-1 {
          padding: 40px 20px 30px !important;
        }
      }
    </style>
  </head>
  
  <body style="background-color: #171620; margin: 0; padding: 0; -webkit-text-size-adjust: none; text-size-adjust: none;">
    <table border="0" cellpadding="0" cellspacing="0" class="nl-container" role="presentation"
      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-color: #171620;" width="100%">
      <tbody>
        <tr>
          <td>
            <table align="center" border="0" cellpadding="0" cellspacing="0" class="row row-1"
              role="presentation"
              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-image: url('${process.env.REACT_APP_BASE_URL}/images/bg_lines.png'); background-repeat: no-repeat; background-size: cover;"
              width="100%">
              <tbody>
                <tr>
                  <td>
                    <table align="center" border="0" cellpadding="0" cellspacing="0"
                      class="row-content stack" role="presentation"
                      style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; background-size: auto; border-radius: 0; color: #000000; width: 680px; margin: 0 auto;"
                      width="680">
                      <tbody>
                        <tr>
                          <td class="column column-1"
                            style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-weight: 400; text-align: left; padding-bottom: 60px; padding-top: 60px; vertical-align: middle; border-top: 0px; border-right: 0px; border-bottom: 0px; border-left: 0px;"
                            width="100%">
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="icons_block block-1" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="vertical-align: middle; color: #000000; font-family: inherit; font-size: 16px; font-weight: 400; padding-bottom: 45px; text-align: center;">
                                  <table align="center" cellpadding="0" cellspacing="0"
                                    class="alignment" role="presentation"
                                    style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;">
                                    <tr>
                                      <td
                                        style="vertical-align: middle; text-align: center; padding-top: 5px; padding-bottom: 5px; padding-left: 5px; padding-right: 5px;">
                                        <img align="center" class="icon"
                                          height="128"
                                          src="${process.env.REACT_APP_BASE_URL}/assets/backend/images/logo.png"
                                          style="display: block; height: auto; margin: 0 auto; border: 0;background-color:#121212;"
                                          width="256" /></td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                            </table>
                            <table border="0" cellpadding="10" cellspacing="0"
                              class="heading_block block-2" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt;"
                              width="100%">
                            
                            </table>
                            <table border="0" cellpadding="0" cellspacing="0"
                              class="paragraph_block block-3" role="presentation"
                              style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word;"
                              width="100%">
                              <tr>
                                <td class="pad"
                                  style="padding-bottom:20px;padding-left:10px;padding-right:10px;padding-top:10px;">
                                  <div
                                    style="text-align: center; color:#acabaf;direction:ltr;font-family:Arial, Helvetica, sans-serif;font-size:24px;font-weight:400;letter-spacing:0px;line-height:150%;text-align:left;mso-line-height-alt:36px;">
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          Hi ${userName}
                                                                      </span></h1>
                                                                      <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;">your trip of booking</p>
                                    <h1 style="text-align: center; margin: 0; margin-bottom: 16px;  color: #ffffff; direction: ltr; font-family: 'Oswald', Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 48px; font-weight: 700; letter-spacing: normal; line-height: 120%; text-align: center; margin-top: 0; margin-bottom: 0; mso-line-height-alt: 57.599999999999994px;"><span class="tinyMce-placeholder">
                                                                          ${bookingTitle}
                                                                      </span></h1>
                                    <p style="text-align: center; margin: 0; margin-bottom: 16px; margin-top: 16px;  color: #ffffff;">
                                      is now completed.<br>
                                      <br>
                                      We are delighted that you completed your rental with BBR, we hope you had a great experience. You can review the boat owner and your trip by clicking this <a href="${process.env.REACT_APP_BASE_URL}/dashboard/event/${bookingId}">link</a>. 
                                      <br><br>Tips are also a great way to show your appreciation. If you'd like to leave a tip for outstanding service, please follow this <a href="${process.env.REACT_APP_BASE_URL}/tip/${bookingId}">link</a>
                                      <br>On behalf of the entire team here at BBR,                                                                        
                                                                      </p>
                                    <br>
                                                                      <p style="text-align: center;margin: 0;">Thank you!</p>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table><!-- End -->
  </body>
  
  </html>
  `;
}

export async function getAllOwnerSelectionQuestions() {
  try {
    const database = getDatabase();

    const pathToOwnerSelectionQuestions =
      "portalSettings/settingsID/defaultOwnerQuestionsSignup/selectionQuestions";
    // Get a reference to the selectionQuestions path
    const selectionQuestionsRef = ref(database, pathToOwnerSelectionQuestions);

    // Retrieve data once from the database
    const snapshot = await get(selectionQuestionsRef);

    // Check if data exists
    if (snapshot.exists()) {
      // Convert the data to an array
      const selectionQuestionsData = snapshot.val();
      const selectionQuestionsArray = Object.values(selectionQuestionsData);
      return selectionQuestionsArray;
    } else {
      // Return an empty array if no data is found
      return [];
    }
  } catch (error) {
    // Handle any errors that may occur during the database operation
    console.error("Error retrieving selectionQuestions:", error);
    throw error; // Re-throw the error to handle it at a higher level if needed
  }
}

export async function getAllOwnerTextQuestions() {
  try {
    const database = getDatabase();

    const pathToOwnerTextQuestions =
      "portalSettings/settingsID/defaultOwnerQuestionsSignup/textQuestions";
    // Get a reference to the selectionQuestions path
    const selectionQuestionsRef = ref(database, pathToOwnerTextQuestions);

    // Retrieve data once from the database
    const snapshot = await get(selectionQuestionsRef);

    // Check if data exists
    if (snapshot.exists()) {
      // Convert the data to an array
      const selectionQuestionsData = snapshot.val();
      const selectionQuestionsArray = Object.values(selectionQuestionsData);
      return selectionQuestionsArray;
    } else {
      // Return an empty array if no data is found
      console.log("test");
      return [];
    }
  } catch (error) {
    // Handle any errors that may occur during the database operation
    console.error("Error retrieving selectionQuestions:", error);
    throw error; // Re-throw the error to handle it at a higher level if needed
  }
}

export async function getAllRenterSignupQuestions() {
  try {
    const database = getDatabase();

    const pathToRenterSelectionQuestions =
      "portalSettings/settingsID/defaultNewRenterQuestionsSignup/questions";
    // Get a reference to the selectionQuestions path
    const selectionQuestionsRef = ref(database, pathToRenterSelectionQuestions);

    // Retrieve data once from the database
    const snapshot = await get(selectionQuestionsRef);

    // Check if data exists
    if (snapshot.exists()) {
      // Convert the data to an array
      const selectionQuestionsData = snapshot.val();
      const selectionQuestionsArray = Object.values(selectionQuestionsData);
      return selectionQuestionsArray;
    } else {
      // Return an empty array if no data is found
      return [];
    }
  } catch (error) {
    // Handle any errors that may occur during the database operation
    console.error("Error retrieving selectionQuestions:", error);
    throw error; // Re-throw the error to handle it at a higher level if needed
  }
}
export async function getAllVerificationQuestions() {
  try {
    const database = getDatabase();

    const pathToRenterSelectionQuestions =
      "portalSettings/settingsID/defaultVerificationQuestions/questions";
    // Get a reference to the selectionQuestions path
    const selectionQuestionsRef = ref(database, pathToRenterSelectionQuestions);

    // Retrieve data once from the database
    const snapshot = await get(selectionQuestionsRef);

    // Check if data exists
    if (snapshot.exists()) {
      // Convert the data to an array
      const selectionQuestionsData = snapshot.val();
      const selectionQuestionsArray = Object.values(selectionQuestionsData);
      return selectionQuestionsArray.filter(Boolean);
    } else {
      // Return an empty array if no data is found
      return [];
    }
  } catch (error) {
    // Handle any errors that may occur during the database operation
    console.error("Error retrieving selectionQuestions:", error);
    throw error; // Re-throw the error to handle it at a higher level if needed
  }
}

export const BASS_BOAT_TYPE = "Bass Boat";
export const BOAT_TYPES_EXEMPT_FROM_RENTER_VERIFICATION = [
  "Fishing Charter",
  "Kayak",
];

export function boatTypeRequiresRenterVerification(boatType) {
  if (!boatType) return false;
  const normalized = String(boatType).trim().toLowerCase();
  return !BOAT_TYPES_EXEMPT_FROM_RENTER_VERIFICATION.some(
    (exemptType) => exemptType.toLowerCase() === normalized
  );
}

export function getVerifiableBoatTypes(allBoatTypes = []) {
  return allBoatTypes.filter(boatTypeRequiresRenterVerification);
}

export function getDefaultBassBoatVerificationQuestions() {
  return [
    {
      questionKey: "bass_experience",
      questionType: "option",
      questionText: "Do you have experience operating a bass boat?",
      options: ["No", "Yes"],
    },
    {
      questionKey: "bass_experience_description",
      questionType: "text",
      questionText:
        "Please describe your bass boat experience (how long, locations, etc.)",
      maxLength: 1000,
      placeholder: "Describe your experience...",
    },
    {
      questionKey: "bass_tow_capable",
      questionType: "option",
      questionText:
        "Do you have a vehicle capable of safely towing a bass boat?",
      options: ["No", "Yes"],
      helperText:
        "Bass boats require proper towing. Your tow vehicle should be rated to handle the weight and length of the boats you plan to tow.",
    },
    {
      questionKey: "bass_tow_vehicle",
      questionType: "structured",
      questionText: "Tow vehicle details",
      subtitle: "Please provide the vehicle you will use to tow the bass boat.",
      fields: [
        { key: "make", label: "Make", placeholder: "e.g., Ford" },
        { key: "model", label: "Model", placeholder: "e.g., F-250" },
        { key: "year", label: "Year", placeholder: "e.g., 2021" },
      ],
    },
    {
      questionKey: "bass_boats_list",
      questionType: "boat_list",
      questionText: "List the bass boat(s) you have experience operating",
      subtitle:
        "Include make, model, year and motor HP. You can add more than one.",
      fields: [
        { key: "make", label: "Make", placeholder: "e.g., Ranger" },
        { key: "model", label: "Model", placeholder: "e.g., Z520" },
        { key: "year", label: "Year", placeholder: "e.g., 2021" },
        { key: "motorHp", label: "Motor HP", placeholder: "e.g., 250" },
      ],
    },
    {
      questionKey: "bass_proof_upload",
      questionType: "images",
      questionText: "Upload proof of your boating experience",
      proofSubtitle:
        "Please upload one or more of the following (screenshots and photos are accepted):",
      required: true,
      accept: "image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf",
      multiple: true,
    },
  ];
}

export function formatVerificationAnswerForDisplay(question, answer) {
  if (!answer) {
    return "No answer provided";
  }

  if (question?.questionType === "structured") {
    try {
      const data = JSON.parse(answer);
      return [
        data.make ? `Make: ${data.make}` : null,
        data.model ? `Model: ${data.model}` : null,
        data.year ? `Year: ${data.year}` : null,
      ]
        .filter(Boolean)
        .join(", ");
    } catch (error) {
      return answer;
    }
  }

  if (question?.questionType === "boat_list") {
    try {
      const boats = JSON.parse(answer);
      if (!Array.isArray(boats) || boats.length === 0) {
        return "No boats listed";
      }

      return boats
        .map((boat, index) => {
          const parts = [
            boat.make,
            boat.model,
            boat.year ? `(${boat.year})` : null,
            boat.motorHp ? `${boat.motorHp} HP` : null,
          ].filter(Boolean);
          return `Boat ${index + 1}: ${parts.join(" ")}`;
        })
        .join("\n");
    } catch (error) {
      return answer;
    }
  }

  return answer;
}

export function inferVerificationQuestionType(questionText = "") {
  if (questionText === "Tow vehicle details") {
    return "structured";
  }

  if (String(questionText).includes("bass boat(s)")) {
    return "boat_list";
  }

  return "text";
}

export function getDefaultOtherBoatVerificationQuestions(boatType) {
  return [
    {
      questionType: "option",
      questionText: `Do you have experience operating a ${boatType}?`,
      options: ["No", "Yes"],
      correctOption: "Yes",
    },
    {
      questionType: "text",
      questionText: "If yes, please describe your experience",
      maxLength: 1000,
      placeholder: "Describe your experience...",
    },
    {
      questionType: "images",
      questionText: "Upload proof of your boating experience",
      required: true,
      accept: "image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf",
      multiple: true,
    },
  ];
}

export const OTHER_BOAT_PROOF_CATEGORIES = [
  {
    icon: "verified_user",
    title: "Boat ownership",
    description:
      "Current or expired boat insurance showing your name.",
  },
  {
    icon: "description",
    title: "Previous boat rentals",
    description:
      "Screenshot of a rental, booking history, receipt, or renter profile from another platform showing your name.",
  },
  {
    icon: "photo_camera",
    title: "Vessel experience",
    description:
      "Photos of you operating or aboard a similar type of boat.",
  },
  {
    icon: "school",
    title: "State Boater Education Certificate",
    description:
      "Helps demonstrate your boating knowledge and safety education, but does not by itself verify experience operating this specific type of vessel.",
  },
];

export function parseVerificationUploadedFiles(answer) {
  if (!answer) return [];

  if (Array.isArray(answer)) {
    return answer;
  }

  if (typeof answer === "string") {
    const trimmed = answer.trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((item) =>
            typeof item === "string"
              ? { file: item, name: item }
              : {
                  file: item?.file || item?.storedName || "",
                  name: item?.name || item?.originalName || item?.file || "",
                }
          );
        }
      } catch (error) {
        return [{ file: trimmed, name: trimmed }];
      }
    }

    if (trimmed.includes(",")) {
      return trimmed.split(",").map((file) => ({
        file: file.trim(),
        name: file.trim(),
      }));
    }

    return [{ file: trimmed, name: trimmed }];
  }

  return [];
}

function applyBoatTypeToQuestionText(questions, boatType) {
  return questions.map((question) => ({
    ...question,
    questionText:
      question?.questionText?.replace(/\{boatType\}/g, boatType) ||
      question?.questionText,
  }));
}

export async function getVerificationQuestionsForBoatType(boatType) {
  if (boatType === BASS_BOAT_TYPE) {
    return getDefaultBassBoatVerificationQuestions();
  }

  return getDefaultOtherBoatVerificationQuestions(boatType);
}

export function isVerificationStatusActive(status) {
  return String(status || "").toLowerCase() === "active";
}

export function getRenterVerificationStatus(user, userMeta, boatType) {
  if (!boatTypeRequiresRenterVerification(boatType)) {
    return "Active";
  }

  const boatVerifications = userMeta?.boatVerifications || {};
  const typeVerification = boatVerifications[boatType];

  if (typeVerification?.status) {
    return typeVerification.status;
  }

  if (boatType === BASS_BOAT_TYPE && user?.status) {
    return user.status;
  }

  return "unset";
}

export function isRenterVerifiedForBoatType(user, userMeta, boatType) {
  const boatVerifications = userMeta?.boatVerifications || {};
  const typeVerification = boatVerifications[boatType];

  if (typeVerification?.status) {
    return isVerificationStatusActive(typeVerification.status);
  }

  if (boatType === BASS_BOAT_TYPE && user?.status) {
    return isVerificationStatusActive(user.status);
  }

  return false;
}

export function getVerifiedBoatTypes(user, userMeta, allBoatTypes = []) {
  return getVerifiableBoatTypes(allBoatTypes).filter((boatType) =>
    isRenterVerifiedForBoatType(user, userMeta, boatType)
  );
}

export function getActiveVerifiedBoatTypes(user, userMeta) {
  const verifiedTypes = Object.entries(userMeta?.boatVerifications || {})
    .filter(([, verification]) =>
      isVerificationStatusActive(verification?.status)
    )
    .map(([type]) => type);

  if (
    isVerificationStatusActive(user?.status) &&
    !verifiedTypes.includes(BASS_BOAT_TYPE)
  ) {
    verifiedTypes.unshift(BASS_BOAT_TYPE);
  }

  return [...new Set(verifiedTypes)];
}

export function getPendingVerifiedBoatTypes(user, userMeta) {
  const pendingTypes = Object.entries(userMeta?.boatVerifications || {})
    .filter(
      ([, verification]) =>
        String(verification?.status || "").toLowerCase() === "pending"
    )
    .map(([type]) => type);

  if (
    String(user?.status || "").toLowerCase() === "pending" &&
    !pendingTypes.includes(BASS_BOAT_TYPE)
  ) {
    const verifiedTypes = getActiveVerifiedBoatTypes(user, userMeta);
    if (!verifiedTypes.includes(BASS_BOAT_TYPE)) {
      pendingTypes.unshift(BASS_BOAT_TYPE);
    }
  }

  return [...new Set(pendingTypes)];
}

export function getRenterVerificationSummary(user, userMeta) {
  const verifiedTypes = getActiveVerifiedBoatTypes(user, userMeta);
  const pendingTypes = getPendingVerifiedBoatTypes(user, userMeta);

  if (verifiedTypes.length > 0) {
    return {
      status: "verified",
      label:
        verifiedTypes.length === 1
          ? "Verified · 1"
          : `Verified · ${verifiedTypes.length}`,
      detail: verifiedTypes.join(" · "),
      verifiedTypes,
      pendingTypes,
    };
  }

  if (pendingTypes.length > 0) {
    return {
      status: "pending",
      label: "Pending",
      detail: pendingTypes.join(" · "),
      verifiedTypes,
      pendingTypes,
    };
  }

  return {
    status: "unset",
    label: "Not Verified",
    detail: "",
    verifiedTypes,
    pendingTypes,
  };
}

export async function updateBoatVerificationStatus(
  userId,
  boatType,
  newStatus,
  rejectReason
) {
  const boatVerificationRef = ref(
    database,
    `userMeta/${userId}/boatVerifications/${boatType}`
  );

  const updateData = {
    status: newStatus,
    updatedAt: new Date().toISOString(),
  };

  if (rejectReason !== undefined && rejectReason !== null && rejectReason !== "") {
    updateData.rejectionReason = rejectReason;
  }

  await update(boatVerificationRef, updateData);

  if (boatType === BASS_BOAT_TYPE) {
    await updateUserStatusById(userId, newStatus, rejectReason);
  }
}

export function buildVerificationAnswersPayload(
  questions,
  selectedOptions,
  textQuestionAnswers,
  uploadedImages = {},
  structuredAnswers = {}
) {
  return {
    multiChoiceQuestions: questions
      .filter((question) => question.questionType === "option")
      .map((question) => {
        const questionIndex = questions.findIndex(
          (q) => q.questionText === question.questionText
        );
        return {
          questionIndex,
          question: question.questionText,
          answer:
            selectedOptions.find(
              (option) => option.questionText === question.questionText
            )?.option || "",
        };
      }),
    textQuestions: questions
      .filter((question) =>
        ["text", "structured", "boat_list"].includes(question.questionType)
      )
      .map((question, index) => {
        let answer = "";

        if (question.questionType === "text") {
          answer =
            textQuestionAnswers.find(
              (item) => item.questionText === question.questionText
            )?.answer || "";
        } else if (structuredAnswers[question.questionText]) {
          answer = JSON.stringify(structuredAnswers[question.questionText]);
        }

        return {
          questionIndex: index,
          question: question.questionText,
          answer,
        };
      }),
    images: questions
      .filter((question) => question.questionType === "images")
      .map((question, index) => {
        const uploadedValue = uploadedImages[question.questionText];
        let answer = "";

        if (Array.isArray(uploadedValue)) {
          answer = JSON.stringify(
            uploadedValue.map((file) => ({
              file: file.storedName || file.file || "",
              name: file.originalName || file.name || file.storedName || "",
            }))
          );
        } else if (uploadedValue) {
          answer = uploadedValue;
        }

        return {
          questionIndex: index,
          question: question.questionText,
          answer,
        };
      }),
  };
}

export async function getBecomeProQuestions() {
  try {
    const database = getDatabase();

    const pathToBecomeProQuestions =
      "portalSettings/settingsID/defaultProQuestions/questions";
    // Get a reference to the selectionQuestions path
    const selectionQuestionsRef = ref(database, pathToBecomeProQuestions);

    // Retrieve data once from the database
    const snapshot = await get(selectionQuestionsRef);

    // Check if data exists
    if (snapshot.exists()) {
      // Convert the data to an array
      const selectionQuestionsData = snapshot.val();
      const selectionQuestionsArray = Object.values(selectionQuestionsData);
      return selectionQuestionsArray;
    } else {
      // Return an empty array if no data is found
      return [];
    }
  } catch (error) {
    // Handle any errors that may occur during the database operation
    console.error("Error retrieving Become Pro questions:", error);
    throw error; // Re-throw the error to handle it at a higher level if needed
  }
}

export async function getAllRenterSelectionQuestions() {
  try {
    const database = getDatabase();

    const pathToRenterSelectionQuestions =
      "portalSettings/settingsID/defaultRenterQuestionsSignup/selectionQuestions";
    // Get a reference to the selectionQuestions path
    const selectionQuestionsRef = ref(database, pathToRenterSelectionQuestions);

    // Retrieve data once from the database
    const snapshot = await get(selectionQuestionsRef);

    // Check if data exists
    if (snapshot.exists()) {
      // Convert the data to an array
      const selectionQuestionsData = snapshot.val();
      const selectionQuestionsArray = Object.values(selectionQuestionsData);
      return selectionQuestionsArray;
    } else {
      // Return an empty array if no data is found
      return [];
    }
  } catch (error) {
    // Handle any errors that may occur during the database operation
    console.error("Error retrieving selectionQuestions:", error);
    throw error; // Re-throw the error to handle it at a higher level if needed
  }
}

export async function getAllRenterTextQuestions() {
  try {
    const database = getDatabase();

    const pathToRenterTextQuestions =
      "portalSettings/settingsID/defaultRenterQuestionsSignup/textQuestions";
    // Get a reference to the selectionQuestions path
    const selectionQuestionsRef = ref(database, pathToRenterTextQuestions);

    // Retrieve data once from the database
    const snapshot = await get(selectionQuestionsRef);

    // Check if data exists
    if (snapshot.exists()) {
      // Convert the data to an array
      const selectionQuestionsData = snapshot.val();
      const selectionQuestionsArray = Object.values(selectionQuestionsData);
      return selectionQuestionsArray;
    } else {
      // Return an empty array if no data is found
      console.log("test");
      return [];
    }
  } catch (error) {
    // Handle any errors that may occur during the database operation
    console.error("Error retrieving selectionQuestions:", error);
    throw error; // Re-throw the error to handle it at a higher level if needed
  }
}

export async function getUserRoleByEmail(email) {
  try {
    const database = getDatabase();
    const usersRef = ref(database, "users");

    // Create a query to filter users by email using the index
    const userQuery = query(usersRef, orderByChild("email"), equalTo(email));

    // Fetch the results of the query
    const snapshot = await get(userQuery);

    if (snapshot.exists()) {
      const userData = snapshot.val();
      console.log("userData: ", userData);
      const userId = Object.keys(userData)[0]; // Assuming email is unique, get the first key

      if (userId) {
        const user = userData[userId];
        const userRole = user.role;
        console.log("User role:", userRole);
        return userRole;
      } else {
        console.log("No user found with the provided email");
        return null;
      }
    } else {
      console.log("No data found in users node");
      return null;
    }
  } catch (error) {
    console.error("Error retrieving user role:", error);
    throw error;
  }
}

export async function getUserIdByRefferralCode(referralCode) {
  try {
    const normalizedCode = normalizeReferralCode(referralCode);
    console.log(`getUserIdByRefferralCode called with: "${normalizedCode}"`);
    const referralRef = ref(database, "refferalCodes");

    const snapshot = await get(referralRef);

    if (snapshot.exists()) {
      const refferalData = snapshot.val();
      console.log(
        "Available referral codes:",
        Object.values(refferalData).map((item) => item.code)
      );

      const userWithMatchingRefferalCode = findReferralCodeEntry(
        refferalData,
        normalizedCode
      );

      // Promo codes (e.g. START@5) may exist without a referrer userId — only
      // pro-staff referral codes tied to a user should reduce the first rental fee.
      const userId = userWithMatchingRefferalCode?.userId || null;
      console.log("User Id found:", userId);

      return userId;
    } else {
      console.log("No referral codes data found in database");
      return null;
    }
  } catch (error) {
    console.error("Error retrieving user Id:", error);
    throw error;
  }
}

export async function getReferralCodeByUserId(userId) {
  try {
    const referralRef = ref(database, "refferalCodes");

    const snapshot = await get(referralRef);

    if (snapshot.exists()) {
      const referralData = snapshot.val();

      const userWithMatchingUserId = Object.values(referralData).find(
        (user) => user.userId === userId
      );

      const referralCode = userWithMatchingUserId
        ? userWithMatchingUserId.code
        : null;
      console.log("Referral Code:", referralCode);

      return referralCode;
    } else {
      console.log("No data found");
      return null;
    }
  } catch (error) {
    console.error("Error retrieving referral code:", error);
    throw error;
  }
}

const DEFAULT_COMMISSION_RATE = 0.10;

export function normalizeReferralCode(referralCode) {
  if (!referralCode || typeof referralCode !== "string") {
    return "";
  }
  return referralCode.trim().toUpperCase();
}

function parseCommissionRate(value) {
  const rate = Number(value);
  if (!Number.isFinite(rate) || rate < 0) {
    return null;
  }
  // Support legacy values stored as whole percentages (e.g. 5 instead of 0.05).
  return rate > 1 ? rate / 100 : rate;
}

function findReferralCodeEntry(referralData, referralCode) {
  const normalizedCode = normalizeReferralCode(referralCode);
  if (!normalizedCode) {
    return null;
  }

  return Object.values(referralData).find(
    (entry) => normalizeReferralCode(entry?.code) === normalizedCode
  );
}

/**
 * Get the commission rate for a user's referral code.
 * Special referral codes like "START@5" have a custom commission rate (e.g., 0.05 for 5%).
 * Default commission rate is 0.10 (10%).
 *
 * @param {string} referralCode - The referral code to check
 * @returns {Promise<number>} - The commission rate (0.05 for 5%, 0.10 for 10%, etc.)
 */
export async function getCommissionRateByReferralCode(referralCode) {
  try {
    const normalizedCode = normalizeReferralCode(referralCode);
    if (!normalizedCode) {
      return DEFAULT_COMMISSION_RATE;
    }

    const referralRef = ref(database, "refferalCodes");
    const snapshot = await get(referralRef);

    if (snapshot.exists()) {
      const referralData = snapshot.val();
      const matchingEntry = findReferralCodeEntry(referralData, normalizedCode);

      if (matchingEntry && matchingEntry.commissionRate !== undefined) {
        const commissionRate = parseCommissionRate(matchingEntry.commissionRate);
        if (commissionRate !== null) {
          console.log(`Commission rate for ${normalizedCode}:`, commissionRate);
          return commissionRate;
        }
      }
    }

    return DEFAULT_COMMISSION_RATE;
  } catch (error) {
    console.error("Error retrieving commission rate:", error);
    return DEFAULT_COMMISSION_RATE;
  }
}

export async function getAllReferralCodesWithOwners() {
  try {
    const referralRef = ref(database, "refferalCodes");
    const usersRef = ref(database, "users");

    const [referralSnapshot, usersSnapshot] = await Promise.all([
      get(referralRef),
      get(usersRef),
    ]);

    const referralCodesWithOwners = [];

    if (referralSnapshot.exists() && usersSnapshot.exists()) {
      const referralData = referralSnapshot.val();
      const usersData = usersSnapshot.val();

      Object.values(referralData).forEach((referralEntry) => {
        const userId = referralEntry.userId;
        const referralCode = referralEntry.code;

        if (userId && usersData[userId]) {
          const user = usersData[userId];
          const ownerName = `${user.firstName || ""} ${user.lastName || ""
            }`.trim();

          referralCodesWithOwners.push({
            code: referralCode,
            ownerName: ownerName,
            userId: userId,
            displayText: `${referralCode} (${ownerName})`,
          });
        }
      });
    }

    // Sort by owner name for better UX
    referralCodesWithOwners.sort((a, b) =>
      a.ownerName.localeCompare(b.ownerName)
    );

    console.log("All referral codes with owners:", referralCodesWithOwners);
    return referralCodesWithOwners;
  } catch (error) {
    console.error("Error retrieving referral codes with owners:", error);
    throw error;
  }
}

export async function getAllReferralCodes() {
  try {
    const referralRef = ref(database, "refferalCodes");
    const snapshot = await get(referralRef);

    if (snapshot.exists()) {
      const referralData = snapshot.val();
      const referralCodes = Object.entries(referralData).map(([id, data]) => ({
        id,
        ...data,
      }));
      return referralCodes;
    }
    return [];
  } catch (error) {
    console.error("Error retrieving referral codes:", error);
    throw error;
  }
}

export async function addReferralCode(code, commissionRate, userId = null) {
  try {
    const referralRef = ref(database, "refferalCodes");
    const newReferralRef = push(referralRef);
    await set(newReferralRef, {
      code: code.toUpperCase(),
      commissionRate: parseFloat(commissionRate),
      userId: userId,
      createdAt: Date.now(),
    });
    return newReferralRef.key;
  } catch (error) {
    console.error("Error adding referral code:", error);
    throw error;
  }
}

export async function updateReferralCode(id, code, commissionRate, userId = undefined) {
  try {
    const referralRef = ref(database, `refferalCodes/${id}`);
    const updates = {
      code: String(code).toUpperCase(),
      updatedAt: Date.now(),
    };

    const parsedRate = parseFloat(commissionRate);
    if (commissionRate !== "" && commissionRate != null && !Number.isNaN(parsedRate)) {
      updates.commissionRate = parsedRate;
    }

    if (userId !== undefined) {
      updates.userId = userId || null;
    }

    await update(referralRef, updates);
    return true;
  } catch (error) {
    console.error("Error updating referral code:", error);
    throw error;
  }
}

export async function deleteReferralCode(id) {
  try {
    const referralRef = ref(database, `refferalCodes/${id}`);
    await remove(referralRef);
    return true;
  } catch (error) {
    console.error("Error deleting referral code:", error);
    throw error;
  }
}

export async function getCurrentUserDetails() {
  try {
    const auth = getAuth();
    return new Promise((resolve, reject) => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const uid = user.uid;
          const usersRef = ref(database, `users/${uid}`);
          const snapshot = await get(usersRef);
          if (snapshot.exists()) {
            const userDetails = snapshot.val();

            // Include userId in the userDetails
            const userDetailsWithId = {
              userId: uid,
              ...userDetails,
            };

            resolve(userDetailsWithId);
          } else {
            console.log("No data found for the current user");
            resolve(null);
          }
        } else {
          console.log("No user signed in");
          resolve(null);
        }
      });
    });
  } catch (error) {
    console.error("Error retrieving current user details:", error);
    throw error;
  }
}

export async function getUserByID(uid) {
  try {
    return new Promise(async (resolve, reject) => {
      // Assuming `user` is a valid variable, make sure to check if the user is signed in
      // and handle the case where `user` is not defined
      const database = getDatabase();
      const usersRef = ref(database, `users/${uid}`);

      try {
        const snapshot = await get(usersRef);
        if (snapshot.exists()) {
          const userDetails = snapshot.val();
          if (userDetails?.avatar) {
            resolve({
              ...userDetails,
              avatar: normalizeAssetUrl(userDetails.avatar),
            });
          } else {
            resolve(userDetails);
          }
        } else {
          console.log("No data found for the current user");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving user details:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving current user details:", error);
    throw error;
  }
}

export async function getPlatformPercentages() {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const platformPercentagesRef = ref(database, `portalSettings/settingsID`);

      try {
        const snapshot = await get(platformPercentagesRef);
        if (snapshot.exists()) {
          const percentagesDetails = snapshot.val();
          resolve(percentagesDetails);
        } else {
          console.log("No data found");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving", error);
    throw error;
  }
}
export async function getOwnerMeta(id) {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const ownerMetaRef = ref(database, `ownerMeta/${id}`);

      try {
        const snapshot = await get(ownerMetaRef);
        if (snapshot.exists()) {
          const ownerMetaDetails = snapshot.val();
          resolve(ownerMetaDetails);
        } else {
          console.log("No data found");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving", error);
    throw error;
  }
}

export async function getPlatformHourlyRate() {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const hourlyRateRef = ref(
        database,
        `portalSettings/settingsID/hourlyRate`
      );

      try {
        const snapshot = await get(hourlyRateRef);
        if (snapshot.exists()) {
          const hourlyRate = snapshot.val();
          resolve(hourlyRate);
        } else {
          console.log("No data found");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving", error);
    throw error;
  }
}

export async function getBoatTypes() {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const boatTypesRef = ref(database, `portalSettings/settingsID/boatTypes`);

      try {
        const snapshot = await get(boatTypesRef);
        if (snapshot.exists()) {
          const boatTypes = snapshot.val();
          resolve(boatTypes);
        } else {
          console.log("No data found");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving", error);
    throw error;
  }
}

export function getBookingByID(bookingID) {
  return new Promise((resolve, reject) => {
    try {
      const database = getDatabase();
      const bookingsRef = ref(database, `bookings/${bookingID}`);

      onValue(
        bookingsRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const bookingDetails = snapshot.val();
            resolve(bookingDetails);
          } else {
            console.log("No data found for the current booking");
            resolve(null);
          }
        },
        (error) => {
          console.error("Error retrieving booking details:", error);
          reject(error);
        }
      );
    } catch (error) {
      console.error("Error retrieving booking details:", error);
      reject(error);
    }
  });
}

export async function getBookingOnce(bookingID) {
  try {
    const database = getDatabase();
    const bookingsRef = ref(database, `bookings/${bookingID}`);
    const snapshot = await get(bookingsRef);

    if (snapshot.exists()) {
      return snapshot.val();
    }

    console.log("No data found for the current booking");
    return null;
  } catch (error) {
    console.error("Error retrieving booking details:", error);
    throw error;
  }
}

export function getBookingByInquiryId(inquiryId) {
  return new Promise((resolve, reject) => {
    try {
      const database = getDatabase();
      const bookingsRef = ref(database, "bookings");

      // Query the bookings collection where inquiryId matches the given inquiryId
      const inquiryQuery = query(
        bookingsRef,
        orderByChild("inquiryId"),
        equalTo(inquiryId)
      );

      get(inquiryQuery)
        .then((snapshot) => {
          if (snapshot.exists()) {
            const bookings = snapshot.val();

            // Convert the results into an array if multiple bookings exist
            const result = Object.keys(bookings).map((key) => ({
              id: key, // Firebase unique key for each booking
              ...bookings[key], // All booking data
            }));

            resolve(result);
          } else {
            console.log("No bookings found for the given inquiryId");
            resolve(null);
          }
        })
        .catch((error) => {
          console.error("Error retrieving booking details:", error);
          reject(error);
        });
    } catch (error) {
      console.error("Error retrieving booking details:", error);
      reject(error);
    }
  });
}

export async function getPaymentById(id) {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const bookingsRef = ref(database, `payments/${id}`);

      try {
        const snapshot = await get(bookingsRef);
        if (snapshot.exists()) {
          const bookingDetails = snapshot.val();
          resolve(bookingDetails);
        } else {
          console.log("No data found for the current booking");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving booking details:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving booking details:", error);
    throw error;
  }
}

export async function getTipPaymentsByBookingId(bookingId) {
  try {
    if (!bookingId) return [];

    const database = getDatabase();
    const snapshot = await get(ref(database, "payments"));

    if (!snapshot.exists()) {
      return [];
    }

    return Object.entries(snapshot.val())
      .map(([id, payment]) => ({ id, ...payment }))
      .filter(
        (payment) =>
          payment.reason === "Tip" &&
          String(payment.bookingId) === String(bookingId)
      );
  } catch (error) {
    console.error("Error retrieving tip payment details:", error);
    throw error;
  }
}
export async function getPendingPaymentById(id) {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const bookingsRef = ref(database, `pendingPayments/${id}`);

      try {
        const snapshot = await get(bookingsRef);
        if (snapshot.exists()) {
          const bookingDetails = snapshot.val();
          resolve(bookingDetails);
        } else {
          console.log("No data found for the current booking");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving booking details:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving booking details:", error);
    throw error;
  }
}

export async function getDisputeDetailsById(id) {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const disputesRef = ref(database, `disputes/${id}`);

      try {
        const snapshot = await get(disputesRef);
        if (snapshot.exists()) {
          const disputeDetails = snapshot.val();
          resolve(disputeDetails);
        } else {
          console.log("No data found for the current dispute");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving dispute details:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving dispute details:", error);
    throw error;
  }
}

export async function getOfferDetailsByBookingId(bookingID) {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const bookingsRef = ref(database, `bookings/${bookingID}`);

      try {
        const snapshot = await get(bookingsRef);
        if (snapshot.exists()) {
          const bookingDetails = snapshot.val();
          // Get offer details from offers collection using offer ID
          const offerDetails = await getOfferDetailsByInquiryId(
            bookingDetails.inquiryId
          );
          resolve(offerDetails);
        } else {
          console.log("No data found for the current booking");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving booking details:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving booking details:", error);
    throw error;
  }
}

export function calculateAgeFromDateOfBirth(dateOfBirth) {
  if (!dateOfBirth) return null;

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

export function normalizeUserAnswerQuestions(questions) {
  if (!questions) return [];
  const list = Array.isArray(questions) ? questions : Object.values(questions);
  return list.filter(Boolean);
}

export function getUserAnswerQuestionText(entry) {
  if (!entry?.question) return "";
  return typeof entry.question === "string"
    ? entry.question
    : entry.question.questionText || "";
}

export function getDateOfBirthFromUserMeta(userMeta) {
  if (!userMeta) return null;

  if (userMeta.dateOfBirth) {
    return userMeta.dateOfBirth;
  }

  const allQuestions = [
    ...normalizeUserAnswerQuestions(userMeta.userAnswers?.textQuestions),
    ...normalizeUserAnswerQuestions(userMeta.userAnswers?.multiChoiceQuestions),
  ];

  const dobQuestion = allQuestions.find((q) =>
    getUserAnswerQuestionText(q).toLowerCase().includes("date of birth")
  );

  return dobQuestion?.answer || null;
}

export function getUserAgeFromMeta(userMeta) {
  const dateOfBirth = getDateOfBirthFromUserMeta(userMeta);

  if (dateOfBirth) {
    const calculatedAge = calculateAgeFromDateOfBirth(dateOfBirth);
    if (calculatedAge !== null) {
      return calculatedAge;
    }
  }

  const storedAge = userMeta?.age;
  return storedAge !== undefined && storedAge !== null && storedAge !== ""
    ? storedAge
    : null;
}

export async function getUserMetaByID(uid) {
  try {
    const database = getDatabase();
    const userMetaRef = ref(database, `userMeta/${uid}`);

    try {
      const snapshot = await get(userMetaRef);

      if (snapshot.exists()) {
        const userMeta = snapshot.val();
        return userMeta;
      } else {
        console.log("No userMeta found for the current user");
        return null;
      }
    } catch (error) {
      console.error("Error retrieving userMeta:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error retrieving userMeta:", error);
    throw error;
  }
}

export async function getOwnerMetaByID(uid) {
  try {
    const database = getDatabase();
    const userMetaRef = ref(database, `ownerMeta/${uid}`);

    try {
      const snapshot = await get(userMetaRef);

      if (snapshot.exists()) {
        const userMeta = snapshot.val();
        return userMeta;
      } else {
        console.log("No ownerMeta found for the current user");
        return null;
      }
    } catch (error) {
      console.error("Error retrieving ownerMeta:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error retrieving ownerMeta:", error);
    throw error;
  }
}

export async function updateUserStatusById(userId, newStatus, rejectReason) {
  try {
    const usersRef = ref(database, `users/${userId}`);

    // Build update object, only include rejectionReason if it's defined
    const updateData = {
      status: newStatus,
    };
    
    if (rejectReason !== undefined && rejectReason !== null && rejectReason !== "") {
      updateData.rejectionReason = rejectReason;
    }

    // Update the user status in the database
    await update(usersRef, updateData);

    console.log(`User status updated successfully for user with ID ${userId}`);
  } catch (error) {
    console.error("Error updating user status:", error);
    throw error;
  }
}

export async function softDeleteUser(userId) {
  try {
    const usersRef = ref(database, `users/${userId}`);
    await update(usersRef, { isDeleted: true });
    console.log(`User ${userId} soft-deleted successfully`);
  } catch (error) {
    console.error("Error soft-deleting user:", error);
    throw error;
  }
}

export async function hardDeleteUser(userId) {
  try {
    const usersRef = ref(database, `users/${userId}`);
    await remove(usersRef);
    console.log(`User ${userId} permanently deleted successfully`);
  } catch (error) {
    console.error("Error permanently deleting user:", error);
    throw error;
  }
}

export async function restoreUser(userId) {
  try {
    const usersRef = ref(database, `users/${userId}`);
    await update(usersRef, { isDeleted: false });
    console.log(`User ${userId} restored successfully`);
  } catch (error) {
    console.error("Error restoring user:", error);
    throw error;
  }
}

export async function updateListingStatusById(listingId, newStatus) {
  try {
    const listingRef = ref(database, `listings/${listingId}`);
    const snapshot = await get(listingRef);
    const listing = snapshot.val();

    await update(listingRef, { boatStatus: newStatus });

    if (listing?.ownerId) {
      const listingTitle = listing.listingTitle || "Your boat";
      if (newStatus === "Active") {
        notifyOwnerBoatApproved(listing.ownerId, listingTitle, listingId).catch(
          (err) => console.error("Failed to create owner notification:", err)
        );
      } else if (newStatus === "Reject") {
        notifyOwnerBoatRejected(listing.ownerId, listingTitle, listingId).catch(
          (err) => console.error("Failed to create owner notification:", err)
        );
      }
    }

    console.log(`tatus updated successfully`);
  } catch (error) {
    console.error("Error updating status:", error);
    throw error;
  }
}

export async function getAllBoatOwners() {
  try {
    const database = getDatabase();
    const usersRef = ref(database, "users");
    const snapshot = await get(usersRef);

    if (snapshot.exists()) {
      const allUsersDetails = [];
      snapshot.forEach((userSnapshot) => {
        const userId = userSnapshot.key; // Get the userId (Firebase key)
        const userDetails = userSnapshot.val();
        if (userDetails.role === "Boat Owner") {
          const userWithId = { userId, ...userDetails }; // Include userId in the user object
          allUsersDetails.push(userWithId);
        }
      });
      return allUsersDetails;
    } else {
      console.log("No users found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving all users details:", error);
    throw error;
  }
}

export async function getAllUsersDetails() {
  try {
    const database = getDatabase();
    const usersRef = ref(database, "users");
    const snapshot = await get(usersRef);

    if (snapshot.exists()) {
      const allUsersDetails = [];
      snapshot.forEach((userSnapshot) => {
        const userId = userSnapshot.key; // Get the userId (Firebase key)
        const userDetails = userSnapshot.val();
        const userWithId = { userId, ...userDetails }; // Include userId in the user object
        allUsersDetails.push(userWithId);
      });
      return allUsersDetails;
    } else {
      console.log("No users found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving all users details:", error);
    throw error;
  }
}

export async function getAllPendingUsers() {
  try {
    const database = getDatabase();
    const usersRef = ref(database, "users");
    const pendingUsersQuery = query(
      usersRef,
      orderByChild("status"),
      equalTo("Pending")
    );
    const snapshot = await get(pendingUsersQuery);

    if (snapshot.exists()) {
      const pendingUsersDetails = [];
      snapshot.forEach((userSnapshot) => {
        const userId = userSnapshot.key;
        const userDetails = userSnapshot.val();
        const userWithId = { id: userId, ...userDetails };
        pendingUsersDetails.push(userWithId);
      });
      return pendingUsersDetails;
    } else {
      console.log("No pending users found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving pending users details:", error);
    throw error;
  }
}

export async function getAllAttributes() {
  try {
    const database = getDatabase();
    const attRef = ref(
      database,
      "portalSettings/settingsID/defaultAttributesListings"
    );
    const snapshot = await get(attRef);

    if (snapshot.exists()) {
      const allAttDetails = [];
      snapshot.forEach((userSnapshot) => {
        const attDetails = userSnapshot.val();
        const attWithId = { ...attDetails };
        allAttDetails.push(attWithId);
      });
      return allAttDetails;
    } else {
      console.log("No Attributes found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving all Attributes:", error);
    throw error;
  }
}

export async function getReviewsByListingId(listingId) {
  try {
    const database = getDatabase();
    const revRef = ref(database, `reviews/${listingId}`);
    const snapshot = await get(revRef);

    if (snapshot.exists()) {
      const allRevDetails = [];
      snapshot.forEach((userSnapshot) => {
        const revDetails = userSnapshot.val();
        const revWithId = { id: userSnapshot.key, ...revDetails };
        allRevDetails.push(revWithId);
      });
      return allRevDetails;
    } else {
      console.log("No Review found");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving all Review:", error);
    throw error;
  }
}

export async function getReviewsByRenterId(renterId) {
  try {
    if (!renterId) return [];

    const database = getDatabase();
    const revRef = ref(database, `renterReviews/${renterId}`);
    const snapshot = await get(revRef);

    if (snapshot.exists()) {
      const allRevDetails = [];
      snapshot.forEach((userSnapshot) => {
        const revDetails = userSnapshot.val();
        const revWithId = { id: userSnapshot.key, ...revDetails };
        allRevDetails.push(revWithId);
      });
      return allRevDetails;
    } else {
      console.log("No renter Review found");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving all renter Review:", error);
    throw error;
  }
}

export async function getAllReviews() {
  try {
    const database = getDatabase();
    const revRef = ref(database, `reviews`);
    const snapshot = await get(revRef);

    if (snapshot.exists()) {
      return snapshot.val(); // Returns an object with listingIds as keys
    } else {
      return {};
    }
  } catch (error) {
    console.error("Error retrieving all Reviews:", error);
    throw error;
  }
}

export async function deleteReview(listingId, reviewId) {
  try {
    const database = getDatabase();
    const reviewRef = ref(database, `reviews/${listingId}/${reviewId}`);
    await remove(reviewRef);
    console.log("Review deleted successfully");
  } catch (error) {
    console.error("Error deleting review:", error);
    throw error;
  }
}

let cachedWaiversData = null;
let cachedWaiversPromise = null;

export async function getAllWaiversData() {
  if (cachedWaiversData) {
    return cachedWaiversData;
  }

  if (cachedWaiversPromise) {
    return cachedWaiversPromise;
  }

  cachedWaiversPromise = (async () => {
    try {
      const database = getDatabase();
      const waiversRef = ref(database, "waivers");
      const snapshot = await get(waiversRef);

      if (snapshot.exists()) {
        const allWaiversData = [];
        snapshot.forEach((waiverSnapshot) => {
          const waiverId = waiverSnapshot.key;
          const waiverData = waiverSnapshot.val();
          const waiverWithId = { waiverId, ...waiverData };
          allWaiversData.push(waiverWithId);
        });
        cachedWaiversData = allWaiversData;
        return allWaiversData;
      }

      console.log("No waivers found in the database");
      cachedWaiversData = [];
      return [];
    } catch (error) {
      cachedWaiversPromise = null;
      console.error("Error retrieving all waivers data:", error);
      throw error;
    }
  })();

  return cachedWaiversPromise;
}

const WAIVER_TEMPLATE_ALIASES = {
  adult: ["Adult Waiver", "Adult Waiver 18+", "Adult Passenger Waiver"],
  minor: [
    "Minor Waiver",
    "Minor Passenger Waiver",
    "Minor, Infants Waiver",
  ],
};

export function findWaiverTemplate(allWaiversData, type) {
  const aliases = WAIVER_TEMPLATE_ALIASES[type] || [];
  for (const name of aliases) {
    const waiver = allWaiversData.find((w) => w.waiverName === name);
    if (waiver) return waiver;
  }
  return null;
}

const waiverTemplateCache = {};

export async function getWaiverTemplateByType(type) {
  if (waiverTemplateCache[type]) {
    return waiverTemplateCache[type];
  }

  const aliases = WAIVER_TEMPLATE_ALIASES[type] || [];
  const databaseURL = process.env.REACT_APP_FIREBASE_DATABASE_URL;

  if (databaseURL) {
    try {
      const shallowResponse = await fetch(
        `${databaseURL}/waivers.json?shallow=true`
      );
      if (shallowResponse.ok) {
        const waiverKeys = await shallowResponse.json();
        const database = getDatabase();

        for (const waiverId of Object.keys(waiverKeys || {})) {
          const nameSnapshot = await get(
            ref(database, `waivers/${waiverId}/waiverName`)
          );
          const waiverName = nameSnapshot.val();
          if (!aliases.includes(waiverName)) continue;

          const waiverSnapshot = await get(ref(database, `waivers/${waiverId}`));
          if (!waiverSnapshot.exists()) continue;

          const waiver = { waiverId, ...waiverSnapshot.val() };
          waiverTemplateCache[type] = waiver;
          return waiver;
        }
      }
    } catch (error) {
      console.error("Shallow waiver lookup failed, falling back:", error);
    }
  }

  const allWaiversData = await getAllWaiversData();
  const waiver = findWaiverTemplate(allWaiversData, type);
  if (waiver) {
    waiverTemplateCache[type] = waiver;
  }
  return waiver || null;
}

export async function getWaiverById(waiverId) {
  try {
    const database = getDatabase();
    const waiverRef = ref(database, `waivers/${waiverId}`);
    const snapshot = await get(waiverRef);

    if (snapshot.exists()) {
      const waiverData = snapshot.val();
      return { waiverId, ...waiverData };
    } else {
      console.log(`Waiver with ID ${waiverId} not found in the database`);
      return null;
    }
  } catch (error) {
    console.error("Error retrieving waiver data:", error);
    throw error;
  }
}

export async function getWaiverDataByName(waiverName) {
  try {
    const allWaiversData = await getAllWaiversData();
    let waiver = allWaiversData.find(
      (waiver) => waiver.waiverName === waiverName
    );

    if (!waiver && waiverName === "Adult Waiver") {
      waiver = findWaiverTemplate(allWaiversData, "adult");
    } else if (!waiver && waiverName === "Minor Waiver") {
      waiver = findWaiverTemplate(allWaiversData, "minor");
    }

    if (waiver) {
      return waiver.questions;
    } else {
      console.log(`Waiver with name '${waiverName}' not found`);
      return null;
    }
  } catch (error) {
    console.error("Error retrieving waiver data by name:", error);
    throw error;
  }
}

export async function getWaiverDescriptionByName(waiverName) {
  try {
    const allWaiversData = await getAllWaiversData();
    let waiver = allWaiversData.find(
      (waiver) => waiver.waiverName === waiverName
    );

    if (!waiver && waiverName === "Adult Waiver") {
      waiver = findWaiverTemplate(allWaiversData, "adult");
    } else if (!waiver && waiverName === "Minor Waiver") {
      waiver = findWaiverTemplate(allWaiversData, "minor");
    }

    if (waiver) {
      return waiver.waiverDescription;
    } else {
      console.log(`Waiver with name '${waiverName}' not found`);
      return null;
    }
  } catch (error) {
    console.error("Error retrieving waiver data by name:", error);
    throw error;
  }
}

export async function getAllUsersMeta() {
  try {
    const database = getDatabase();
    const usersRef = ref(database, "userMeta");
    const snapshot = await get(usersRef);

    if (snapshot.exists()) {
      const allUsersDetails = [];
      snapshot.forEach((userSnapshot) => {
        const userDetails = userSnapshot.val();
        if (userDetails.uid) {
          allUsersDetails.push(userDetails);
        } else {
          allUsersDetails.push({ ...userDetails, uid: userSnapshot.key });
        }
      });
      return allUsersDetails;
    } else {
      console.log("No users found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving all users details:", error);
    throw error;
  }
}

export async function getListingCharterOptions() {
  try {
    const database = getDatabase();

    const pathToCharterOptions =
      "portalSettings/settingsID/defaultAttributesListings/charterOptions";
    // Get a reference to the selectionQuestions path
    const charterOptionsRef = ref(database, pathToCharterOptions);

    // Retrieve data once from the database
    const snapshot = await get(charterOptionsRef);

    // Check if data exists
    if (snapshot.exists()) {
      // Convert the data to an array
      const charterOptionsData = snapshot.val();
      const charterOptionsArray = Object.values(charterOptionsData);
      return charterOptionsArray;
    } else {
      // Return an empty array if no data is found
      return [];
    }
  } catch (error) {
    // Handle any errors that may occur during the database operation
    console.error("Error retrieving charterOptionsArray:", error);
    throw error; // Re-throw the error to handle it at a higher level if needed
  }
}

export async function getListingFeaturesOptions() {
  try {
    const database = getDatabase();

    const pathToListingFeatures =
      "portalSettings/settingsID/defaultAttributesListings/listingFeatures";
    // Get a reference to the selectionQuestions path
    const listingFeaturesRef = ref(database, pathToListingFeatures);

    // Retrieve data once from the database
    const snapshot = await get(listingFeaturesRef);

    // Check if data exists
    if (snapshot.exists()) {
      // Convert the data to an array
      const listingFeaturesData = snapshot.val();
      const listingFeaturesArray = Object.values(listingFeaturesData);
      return listingFeaturesArray;
    } else {
      // Return an empty array if no data is found
      return [];
    }
  } catch (error) {
    // Handle any errors that may occur during the database operation
    console.error("Error retrieving listingFeaturesArray:", error);
    throw error; // Re-throw the error to handle it at a higher level if needed
  }
}

export async function getAllListings() {
  try {
    const database = getDatabase();
    const listingsRef = ref(database, "listings");
    const snapshot = await get(listingsRef);

    if (snapshot.exists()) {
      const allListingsDetails = [];
      snapshot.forEach((listingSnapshot) => {
        const listingId = listingSnapshot.key;
        const userDetails = listingSnapshot.val();
        const listingWithId = normalizeListingAssetUrls({ id: listingId, ...userDetails });
        allListingsDetails.push(listingWithId);
      });
      return allListingsDetails;
    } else {
      console.log("No listings found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving all listings details:", error);
    throw error;
  }
}

export async function getActiveListings() {
  try {
    const database = getDatabase();
    const listingsRef = ref(database, "listings");
    const activeListingsQuery = query(
      listingsRef,
      orderByChild("boatStatus"),
      equalTo("Active")
    );
    const snapshot = await get(activeListingsQuery);

    if (snapshot.exists()) {
      const allListingsDetails = [];
      snapshot.forEach((listingSnapshot) => {
        const listingId = listingSnapshot.key;
        const userDetails = listingSnapshot.val();
        const listingWithId = normalizeListingAssetUrls({ id: listingId, ...userDetails });
        allListingsDetails.push(listingWithId);
      });
      return allListingsDetails;
    } else {
      console.log("No active listings found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving active listings details:", error);
    throw error;
  }
}

export async function getAllPendingListings() {
  try {
    const database = getDatabase();
    const listingsRef = ref(database, "listings");
    const activeListingsQuery = query(
      listingsRef,
      orderByChild("boatStatus"),
      equalTo("Pending")
    );
    const snapshot = await get(activeListingsQuery);

    if (snapshot.exists()) {
      const allListingsDetails = [];
      snapshot.forEach((listingSnapshot) => {
        const listingId = listingSnapshot.key;
        const userDetails = listingSnapshot.val();
        const listingWithId = normalizeListingAssetUrls({ id: listingId, ...userDetails });
        allListingsDetails.push(listingWithId);
      });
      allListingsDetails.sort(
        (a, b) => getListingSubmittedAt(b, b.id) - getListingSubmittedAt(a, a.id)
      );
      return allListingsDetails;
    } else {
      console.log("No active listings found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving active listings details:", error);
    throw error;
  }
}

/** Decode approximate creation time from a Firebase RTDB push id. */
export function getPushIdTimestamp(pushId) {
  if (!pushId || typeof pushId !== "string") return null;
  const PUSH_CHARS =
    "-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz";
  let time = 0;
  for (let i = 0; i < 8 && i < pushId.length; i++) {
    const index = PUSH_CHARS.indexOf(pushId[i]);
    if (index === -1) return null;
    time = time * 64 + index;
  }
  return time;
}

/** Best-effort submitted / pending date for a listing. */
export function getListingSubmittedAt(listing, listingId) {
  if (listing?.pendingAt) return Number(listing.pendingAt);
  if (listing?.updatedAt) return Number(listing.updatedAt);
  if (listing?.createdAt) return Number(listing.createdAt);
  return getPushIdTimestamp(listingId || listing?.id) || 0;
}

export function formatListingDate(timestamp) {
  if (!timestamp) return "—";
  const date = new Date(Number(timestamp));
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export async function getAllRejectedListings() {
  try {
    const database = getDatabase();
    const listingsRef = ref(database, "listings");
    const activeListingsQuery = query(
      listingsRef,
      orderByChild("boatStatus"),
      equalTo("Reject")
    );
    const snapshot = await get(activeListingsQuery);

    if (snapshot.exists()) {
      const allListingsDetails = [];
      snapshot.forEach((listingSnapshot) => {
        const listingId = listingSnapshot.key;
        const userDetails = listingSnapshot.val();
        const listingWithId = { id: listingId, ...userDetails };
        allListingsDetails.push(listingWithId);
      });
      return allListingsDetails;
    } else {
      console.log("No rejected listings found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving rejected listings details:", error);
    throw error;
  }
}


export async function getAllDisputes() {
  try {
    const database = getDatabase();
    const disputesRef = ref(database, "disputes");
    const snapshot = await get(disputesRef);

    if (snapshot.exists()) {
      const allDisputesDetails = [];
      snapshot.forEach((disputeSnapshot) => {
        const disputeId = disputeSnapshot.key;
        const disputeDetails = disputeSnapshot.val();
        const disputeWithId = { id: disputeId, ...disputeDetails };
        allDisputesDetails.push(disputeWithId);
      });
      return allDisputesDetails;
    } else {
      console.log("No disputes found in the database");
      return [];
    }
  } catch (error) {
    console.error("Error retrieving all disputes details:", error);
    throw error;
  }
}

export async function getListingByBookingId(id) {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();

      const bookingDetails = await getBookingByID(id);
      console.log("bookingDetails: ", bookingDetails);

      const inquiryId = bookingDetails?.inquiryId;
      console.log("inquiryId: ", bookingDetails?.inquiryId);

      const inquiryDetails = await getInquiryDataById(inquiryId);
      const listingId = inquiryDetails?.listingId;
      const listingsRef = ref(database, `listings/${listingId}`);
      console.log("listingId: ", listingId);

      try {
        const snapshot = await get(listingsRef);
        if (snapshot.exists()) {
          const listing = snapshot.val();

          if (listing) {
            resolve(listing);
          } else {
            console.log("No data found for the listing");
            resolve(null);
          }
        } else {
          console.log("No data found for the listings");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving listing details:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving listing details:", error);
    throw error;
  }
}

export async function getListingByID(id) {
  try {
    const database = getDatabase();
    const listingRef = ref(database, `listings/${id}`);

    // Fetch the specific listing by its ID
    const snapshot = await get(listingRef);

    if (snapshot.exists()) {
      // Directly return the data if it exists
      const listing = snapshot.val();
      return normalizeListingAssetUrls({ id, ...listing });
    } else {
      console.log("No data found for the listing with ID:", id);
      return null;
    }
  } catch (error) {
    console.error("Error retrieving listing details:", error);
    throw error;
  }
}

// export async function getListingLockDatesByID(id) {
//   try {
//     return new Promise(async (resolve, reject) => {
//       const database = getDatabase();
//       const listingsRef = ref(database, `listings/${id}/lockDates`);
//       console.log("listing res lockDates", listingsRef);
//       try {
//         const snapshot = await get(listingsRef);
//         if (snapshot.exists()) {
//           const listingsObject = snapshot.val();
//           if (listingsObject) {
//             resolve(listingsObject);
//           } else {
//             console.log("No Lock Dates");
//             resolve(null);
//           }
//         } else {
//           console.log("No Lock Dates");
//           resolve(null);
//         }
//       } catch (error) {
//         console.error("Error retrieving No Lock Dates details:", error);
//         reject(error);
//       }
//     });
//   } catch (error) {
//     console.error("Error retrieving No Lock Dates details:", error);
//     throw error;
//   }
// }

export async function getListingLockDatesByID(id) {
  try {
    return new Promise(async (resolve, reject) => {
      const listingsRef = ref(database, `listings/${id}/lockDates`);
      console.log("listing res lockDates", listingsRef);
      try {
        const snapshot = await get(listingsRef);
        if (snapshot.exists()) {
          const listingsObject = snapshot.val();
          if (listingsObject) {
            resolve(listingsObject);
          } else {
            console.log("No Lock Dates");
            resolve(null);
          }
        } else {
          console.log("No Lock Dates");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving No Lock Dates details:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving No Lock Dates details:", error);
    throw error;
  }
}

export async function getListingByUserID(userId) {
  try {
    const database = getDatabase();
    const listingsRef = ref(database, "listings");

    // Create a query to filter listings by ownerId using the index
    const listingsQuery = query(
      listingsRef,
      orderByChild("ownerId"),
      equalTo(userId)
    );

    // Fetch the results of the query
    const snapshot = await get(listingsQuery);

    if (snapshot.exists()) {
      const listingsObject = snapshot.val();

      // Convert the object of objects to an array of objects with 'id' property
      const listingsArray = Object.keys(listingsObject).map((key) =>
        normalizeListingAssetUrls({
          id: key,
          ...listingsObject[key],
        })
      );

      return listingsArray;
    } else {
      console.log("No listings found for the user.");
      return null;
    }
  } catch (error) {
    console.error("Error retrieving listings:", error);
    throw error;
  }
}

export async function getInquiryDataById(id) {
  try {
    const database = getDatabase();
    const inquiryRef = ref(database, `inquiries/${id}`);

    // Fetch the inquiry data
    const snapshot = await get(inquiryRef);

    if (snapshot.exists()) {
      const inquiryDetail = snapshot.val();
      console.log(inquiryDetail, "IIIII-------");
      // Include the ID in the returned object
      return { id, ...inquiryDetail };
    } else {
      console.log("No data found for the inquiry ID:", id);
      return null;
    }
  } catch (error) {
    console.error("Error retrieving inquiry data:", error);
    throw error;
  }
}

export async function getOfferDetailsByInquiryId(id) {
  try {
    // If no inquiry ID provided, return null
    if (!id) return null;

    const offerRef = ref(database, `inquiries/${id}`);
    const offerSnapshot = await get(offerRef);

    // Check if snapshot exists and has offerData
    if (offerSnapshot.exists() && offerSnapshot.val().offerData) {
      return offerSnapshot.val().offerData;
    }

    // Return empty object if no offerData found
    return {};
  } catch (error) {
    console.error("Error retrieving inquiries:", error);
    throw error;
  }
}

export async function getBookingsByUserId(userId) {
  try {
    const database = getDatabase();

    // Create queries for both owner and renter fields
    const ownerQuery = query(
      ref(database, "bookings"),
      orderByChild("owner"),
      equalTo(userId)
    );
    const renterQuery = query(
      ref(database, "bookings"),
      orderByChild("renter"),
      equalTo(userId)
    );

    // Fetch results from both queries concurrently
    const [ownerSnapshot, renterSnapshot] = await Promise.all([
      get(ownerQuery),
      get(renterQuery),
    ]);

    // Initialize arrays to store results
    const ownerBookings = ownerSnapshot.exists() ? ownerSnapshot.val() : {};
    const renterBookings = renterSnapshot.exists() ? renterSnapshot.val() : {};

    // Combine and deduplicate results
    const allBookings = { ...ownerBookings, ...renterBookings };
    const bookingsArray = Object.keys(allBookings).map((key) => ({
      id: key,
      ...allBookings[key],
    }));

    // Return filtered bookings
    if (bookingsArray.length > 0) {
      return bookingsArray;
    } else {
      console.log("No data found for the bookings with userId:", userId);
      return null;
    }
  } catch (error) {
    console.error("Error retrieving booking details:", error);
    throw error;
  }
}

export async function getAllBookings() {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const bookingsRef = ref(database, "bookings"); // Updated variable name to 'bookingsRef'

      try {
        const snapshot = await get(bookingsRef); // Updated variable name to 'bookingsRef'
        if (snapshot.exists()) {
          const bookingsObject = snapshot.val(); // Updated variable name to 'bookingsObject'

          // Convert the object of objects to an array of objects with 'id' property
          const bookingsArray = Object.keys(bookingsObject).map((key) => ({
            id: key,
            ...bookingsObject[key],
          }));

          // Assuming you want to return an array of matching items
          resolve(bookingsArray);
        } else {
          console.log("No data found for the bookings");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving booking details:", error); // Updated error message
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving booking details:", error); // Updated error message
    throw error;
  }
}

export async function getWithdrawalRequests() {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const withdrawalsRef = ref(database, "withdrawals"); // Updated variable name to 'withdrawalsRef'

      try {
        const snapshot = await get(withdrawalsRef); // Updated variable name to 'withdrawalsRef'
        if (snapshot.exists()) {
          const withdrawalsObject = snapshot.val(); // Updated variable name to 'withdrawalsObject'

          // Convert the object of objects to an array of objects with 'id' property
          const withdrawalsArray = Object.keys(withdrawalsObject).map(
            (key) => ({
              id: key,
              ...withdrawalsObject[key],
            })
          );

          if (withdrawalsArray.length > 0) {
            // Assuming you want to return an array of matching items
            resolve(withdrawalsArray);
          } else {
            console.log("No data found for the withdrawals:");
            resolve(null);
          }
        } else {
          console.log("No data found for the withdrawals");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving withdrawal details:", error); // Updated error message
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving withdrawal details:", error); // Updated error message
    throw error;
  }
}
export async function getCurrentUserWithdrawalRequests(userId) {
  try {
    const database = getDatabase();
    const withdrawalsRef = ref(database, "withdrawals");

    // Create a query to filter withdrawals by userId using the index
    const withdrawalsQuery = query(
      withdrawalsRef,
      orderByChild("userId"),
      equalTo(userId)
    );

    // Fetch the results of the query
    const snapshot = await get(withdrawalsQuery);

    if (snapshot.exists()) {
      const withdrawalsObject = snapshot.val();

      // Convert the object of objects to an array of objects with 'id' property
      const withdrawalsArray = Object.keys(withdrawalsObject).map((key) => ({
        id: key,
        ...withdrawalsObject[key],
      }));

      if (withdrawalsArray.length > 0) {
        return withdrawalsArray;
      } else {
        console.log("No data found for the userId:", userId);
        return null;
      }
    } else {
      console.log("No data found for the withdrawals");
      return null;
    }
  } catch (error) {
    console.error("Error retrieving withdrawal details:", error);
    throw error;
  }
}

export async function getAllPayments() {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const bookingsRef = ref(database, "payments"); // Updated variable name to 'bookingsRef'

      try {
        const snapshot = await get(bookingsRef); // Updated variable name to 'bookingsRef'
        if (snapshot.exists()) {
          const bookingsObject = snapshot.val(); // Updated variable name to 'bookingsObject'

          // Convert the object of objects to an array of objects with 'id' property
          const bookingsArray = Object.keys(bookingsObject).map((key) => ({
            id: key,
            ...bookingsObject[key],
          }));

          if (bookingsArray.length > 0) {
            // Assuming you want to return an array of matching items
            resolve(bookingsArray);
          } else {
            console.log("No data found for the bookings:");
            resolve(null);
          }
        } else {
          console.log("No data found for the bookings");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving booking details:", error); // Updated error message
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving booking details:", error); // Updated error message
    throw error;
  }
}

export async function getPaymentsByUserId(userId) {
  try {
    const database = getDatabase();
    const paymentsRef = ref(database, "payments");

    // Create queries for each relevant field
    const ownerQuery = query(
      paymentsRef,
      orderByChild("owner"),
      equalTo(userId)
    );
    const referrerQuery = query(
      paymentsRef,
      orderByChild("referrer"),
      equalTo(userId)
    );
    const userQuery = query(
      paymentsRef,
      orderByChild("userId"),
      equalTo(userId)
    );

    // Fetch results from all queries concurrently
    const [ownerSnapshot, referrerSnapshot, userSnapshot] = await Promise.all([
      get(ownerQuery),
      get(referrerQuery),
      get(userQuery),
    ]);

    // Initialize arrays to store results
    const ownerPayments = ownerSnapshot.exists() ? ownerSnapshot.val() : {};
    const referrerPayments = referrerSnapshot.exists()
      ? referrerSnapshot.val()
      : {};
    const userPayments = userSnapshot.exists() ? userSnapshot.val() : {};

    // Combine results into one object, ensuring no duplicates
    const allPayments = {
      ...ownerPayments,
      ...referrerPayments,
      ...userPayments,
    };

    // Convert the combined object to an array of objects with 'id' property
    const paymentsArray = Object.keys(allPayments).map((key) => ({
      id: key,
      ...allPayments[key],
    }));

    if (paymentsArray.length > 0) {
      return paymentsArray;
    } else {
      console.log("No data found for the userId:", userId);
      return null;
    }
  } catch (error) {
    console.error("Error retrieving payment details:", error);
    throw error;
  }
}

export async function checkCoupon(coupon) {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const couponCodesRef = ref(
        database,
        "portalSettings/settingsID/couponCodes"
      );

      try {
        const snapshot = await get(couponCodesRef); // Updated variable name to 'couponCodesRef'
        if (snapshot.exists()) {
          const couponCodesObject = snapshot.val(); // Updated variable name to 'couponCodesObject'

          // Convert the object of objects to an array of objects with 'id' property
          const couponCodesArray = Object.keys(couponCodesObject).map(
            (key) => ({
              id: key,
              ...couponCodesObject[key],
            })
          );

          // Filter the coupon codes to only include those with coupon equal to the provided coupon
          const filteredCouponCodes = couponCodesArray.filter(
            (code) => code.coupon === coupon
          );

          if (filteredCouponCodes.length > 0) {
            // Assuming you want to return an array of matching items
            resolve(filteredCouponCodes);
          } else {
            console.log("No data found for the coupon code:", coupon);
            resolve(null);
          }
        } else {
          console.log("No data found for the coupon codes");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving coupon code details:", error); // Updated error message
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving coupon code details:", error); // Updated error message
    throw error;
  }
}

// export async function getBookingsByListingId(id) {
//   try {
//     const database = getDatabase();
//     const bookingsRef = ref(database, "bookings");

//     // Create a query that filters bookings by listingId using the index
//     const bookingsQuery = query(
//       bookingsRef,
//       orderByChild("listingId"),
//       equalTo(id)
//     );

//     // Fetch the results of the query
//     const snapshot = await get(bookingsQuery);

//     if (snapshot.exists()) {
//       const bookingsObject = snapshot.val();

//       // Convert the object of objects to an array of objects with 'id' property
//       const bookingsArray = Object.keys(bookingsObject).map((key) => ({
//         id: key,
//         ...bookingsObject[key],
//       }));

//       // Return the filtered bookings
//       return bookingsArray;
//     } else {
//       console.log("No data found for the bookings with listingId:", id);
//       return null;
//     }
//   } catch (error) {
//     console.error("Error retrieving booking details:", error);
//     throw error;
//   }
// }

// export async function getEventsByListingId(id) {
//   try {
//     const bookings = await getBookingsByListingId(id);

//     const lockDates = await getListingLockDatesByID(id);

//     if (!bookings && !lockDates) {
//       return [];
//     }

//     const resolvedEvents =
//       Array.isArray(bookings) && bookings.length > 0
//         ? bookings.map(async (booking) => {
//             if (booking?.isDeleted === true) {
//               return {};
//             }

//             let inquireInfo;
//             try {
//               inquireInfo = await getInquiryDataById(booking.inquiryId);
//             } catch (error) {
//               console.error(
//                 `Error fetching offer details for booking ID ${booking.id}:`,
//                 error.message
//               );
//               return null;
//             }

//             const newEvent = {
//               id: booking.id,
//               title: "Booked",
//               color: "red",
//             };
//             const startDateFormatted = new Date(
//               inquireInfo?.offerData?.offeredDate ||
//                 inquireInfo?.inquiryData?.prefferedDate
//             );

//             const isoDate = moment(
//               startDateFormatted || inquireInfo?.inquiryData?.prefferedDate,
//               "dddd, MMMM DD, YYYY"
//             );
//             if (
//               "Sat Apr 12 2025 19:00:00 GMT-0500 (Colombia Standard Time)".includes(
//                 isoDate
//               )
//             ) {
//               console.log(
//                 "inquireInfo221",
//                 moment(
//                   inquireInfo?.offerData?.offeredDate ||
//                     inquireInfo?.inquiryData?.prefferedDate
//                 ).toDate()
//               );
//             }
//             const formattedDate = isoDate.format("YYYY-MM-DD");
//             const startDate = moment(
//               inquireInfo?.offerData?.offeredDate ||
//                 inquireInfo?.inquiryData?.prefferedDate
//             ).toDate();
//             if (isNaN(startDate)) {
//               console.error(
//                 `Invalid start date for booking ID ${booking.id}: ${formattedDate}`
//               );
//               return null;
//             }
//             // Calculate endDate by adding offeredHours or overnight
//             const endDate = new Date(startDate);
//             let endHours = 0;
//             if (
//               parseInt(inquireInfo?.offerData?.offeredHours) > 0 &&
//               parseInt(inquireInfo?.offerData?.offeredHours) < 24
//             ) {
//               endHours = parseInt(inquireInfo?.offerData?.offeredHours);
//             } else if (parseInt(inquireInfo?.offerData?.offeredHours) >= 24) {
//               endHours = parseInt(inquireInfo?.offerData?.offeredHours) + 24;
//             } else if (parseInt(inquireInfo?.offerData?.overnight) > 0) {
//               endHours = parseInt(inquireInfo?.offerData?.overnight) * 24;
//             } else if (
//               inquireInfo?.offerData === undefined &&
//               inquireInfo?.inquiryData?.overnight > 0
//             ) {
//               endHours =
//                 (parseInt(inquireInfo?.inquiryData?.overnight) + 1) * 24;
//             } else if (
//               inquireInfo?.offerData === undefined &&
//               inquireInfo?.inquiryData?.sameDayHours > 0
//             ) {
//               endHours = parseInt(inquireInfo?.inquiryData?.sameDayHours);
//             } else if (
//               inquireInfo?.offerData === undefined &&
//               inquireInfo?.inquiryData?.sameDayHours >= 24
//             ) {
//               endHours = parseInt(inquireInfo?.inquiryData?.sameDayHours) + 48;
//             }
//             endDate.setHours(endDate.getHours() + endHours);
//             newEvent.start = startDate;
//             newEvent.end = endDate;
//             return newEvent;
//           })
//         : [];

//     const events = await Promise.all(resolvedEvents);
//     console.log("events3442123342", events);

//     const disabledDatesFromLockDates = lockDates
//       ? Object.values(lockDates)
//           .map((lockDate) => {
//             // Validate lock dates
//             const startLockDate = lockDate.start_date
//               ? new Date(lockDate.start_date)
//               : null;
//             const endLockDate = lockDate.end_date
//               ? new Date(lockDate.end_date)
//               : null;

//             if (!startLockDate || isNaN(startLockDate)) {
//               console.error(`Invalid lock start date:`, lockDate);
//               return null; // Skip invalid lock dates
//             }
//             if (!endLockDate || isNaN(endLockDate)) {
//               console.error(`Invalid lock end date:`, lockDate);
//               return null; // Skip invalid lock dates
//             }

//             return {
//               title: "Locked",
//               color: "orange",
//               start: startLockDate,
//               end: endLockDate,
//             };
//           })
//           .filter((date) => date !== null)
//       : [];

//     return [
//       ...events.filter((event) => event !== null),
//       ...disabledDatesFromLockDates,
//     ];
//   } catch (error) {
//     console.log("Error retrieving events:", error.message);
//     throw error;
//   }
// }
export async function getBookingsByListingId(id) {
  try {
    const database = getDatabase();
    const bookingsRef = ref(database, "bookings");

    // Create a query that filters bookings by listingId using the index
    const bookingsQuery = query(
      bookingsRef,
      orderByChild("listingId"),
      equalTo(id)
    );

    // Fetch the results of the query
    const snapshot = await get(bookingsQuery);

    if (snapshot.exists()) {
      const bookingsObject = snapshot.val();

      // Convert the object of objects to an array of objects with 'id' property
      const bookingsArray = Object.keys(bookingsObject).map((key) => ({
        id: key,
        ...bookingsObject[key],
      }));

      // Return the filtered bookings
      return bookingsArray;
    } else {
      console.log("No data found for the bookings with listingId:", id);
      return null;
    }
  } catch (error) {
    console.error("Error retrieving booking details:", error);
    throw error;
  }
}
export async function getEventsByListingId(id) {
  try {
    const bookings = await getBookingsByListingId(id);

    const lockDates = await getListingLockDatesByID(id);

    if (!bookings && !lockDates) {
      return [];
    }

    const resolvedEvents =
      Array.isArray(bookings) && bookings.length > 0
        ? bookings.map(async (booking) => {
          if (booking?.isDeleted === true) {
            return {};
          }

          const status = booking?.status?.toLowerCase();
          if (
            status === "cancelled" ||
            status === "pending" ||
            status === "trip concluded" ||
            status === "trip completed"
          ) {
            console.log(
              `Skipping booking ${booking.id} with status: ${booking.status}`
            );
            return null;
          }

          let inquireInfo;
          try {
            inquireInfo = await getInquiryDataById(booking.inquiryId);
          } catch (error) {
            console.error(
              `Error fetching offer details for booking ID ${booking.id}:`,
              error.message
            );
            return null;
          }

          const newEvent = {
            id: booking.id,
            title: "Booked",
            color: "red",
          };
          const startDate = getBookingEventStartDate(inquireInfo, booking);

          if (!startDate || isNaN(startDate)) {
            console.error(
              `Invalid start date for booking ID ${booking.id}`
            );
            return null;
          }
          // Calculate endDate by adding offeredHours or overnight
          const endDate = new Date(startDate);
          let endHours = 0;
          if (
            parseInt(inquireInfo?.offerData?.offeredHours) > 0 &&
            parseInt(inquireInfo?.offerData?.offeredHours) < 24
          ) {
            endHours = parseInt(inquireInfo?.offerData?.offeredHours);
          } else if (parseInt(inquireInfo?.offerData?.offeredHours) >= 24) {
            endHours = parseInt(inquireInfo?.offerData?.offeredHours) + 24;
          } else if (parseInt(inquireInfo?.offerData?.overnight) > 0) {
            endHours = parseInt(inquireInfo?.offerData?.overnight) * 24;
          } else if (
            inquireInfo?.offerData === undefined &&
            inquireInfo?.inquiryData?.overnight > 0
          ) {
            endHours =
              parseInt(inquireInfo?.inquiryData?.overnight) * 24;
          } else if (
            inquireInfo?.offerData === undefined &&
            inquireInfo?.inquiryData?.sameDayHours > 0
          ) {
            endHours = parseInt(inquireInfo?.inquiryData?.sameDayHours);
          } else if (
            inquireInfo?.offerData === undefined &&
            inquireInfo?.inquiryData?.sameDayHours >= 24
          ) {
            endHours = parseInt(inquireInfo?.inquiryData?.sameDayHours) + 48;
          }
          endDate.setHours(endDate.getHours() + endHours);
          newEvent.start = startDate;
          newEvent.end = endDate;
          newEvent.isOvernight = moment(endDate).startOf("day").isAfter(moment(startDate).startOf("day"));
          return newEvent;
        })
        : [];

    const events = await Promise.all(resolvedEvents);

    const disabledDatesFromLockDates = lockDates
      ? Object.values(lockDates)
        .map((lockDate) => {
          // Validate lock dates
          const startLockDate = lockDate.start_date
            ? new Date(lockDate.start_date)
            : null;
          const endLockDate = lockDate.end_date
            ? new Date(lockDate.end_date)
            : null;

          if (!startLockDate || isNaN(startLockDate)) {
            console.error(`Invalid lock start date:`, lockDate);
            return null; // Skip invalid lock dates
          }
          if (!endLockDate || isNaN(endLockDate)) {
            console.error(`Invalid lock end date:`, lockDate);
            return null; // Skip invalid lock dates
          }

          // Handle multiple time ranges or legacy single time
          let events = [];
          
          if (lockDate.time_ranges && lockDate.time_ranges.length > 0) {
            // Create separate events for each range on each locked day.
            const currentDay = moment(startLockDate).startOf("day");
            const lastDay = moment(endLockDate).startOf("day");

            while (currentDay.isSameOrBefore(lastDay, "day")) {
              lockDate.time_ranges.forEach((range) => {
                let startDateTime = currentDay.toDate();
                let endDateTime = currentDay.toDate();
                
                // Set time for this range
                const [startHours, startMinutes] = range.start.split(':');
                const [endHours, endMinutes] = range.end.split(':');
                
                startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);
                endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
                if (endDateTime <= startDateTime) {
                  endDateTime.setDate(endDateTime.getDate() + 1);
                }
                
                events.push({
                  title: `Locked (${range.start} - ${range.end})`,
                  color: "orange",
                  start: startDateTime,
                  end: endDateTime,
                  isOvernight: moment(endDateTime).startOf("day").isAfter(moment(startDateTime).startOf("day")),
                  isHourlyLock: true,
                });
              });
              currentDay.add(1, "day");
            }
          } else if (lockDate.start_time && lockDate.end_time) {
            // Legacy single time range
            let startDateTime = new Date(startLockDate);
            let endDateTime = new Date(endLockDate);
            
            const [startHours, startMinutes] = lockDate.start_time.split(':');
            const [endHours, endMinutes] = lockDate.end_time.split(':');
            
            startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);
            endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
            if (endDateTime <= startDateTime) {
              endDateTime.setDate(endDateTime.getDate() + 1);
            }
            
            events.push({
              title: `Locked (${lockDate.start_time} - ${lockDate.end_time})`,
              color: "orange",
              start: startDateTime,
              end: endDateTime,
              isOvernight: moment(endDateTime).startOf("day").isAfter(moment(startDateTime).startOf("day")),
              isHourlyLock: true,
            });
          } else {
            // All day lock
            events.push({
              title: "Locked (All Day)",
              color: "orange",
              start: startLockDate,
              end: endLockDate,
              isOvernight: true,
            });
          }

          return events;
        })
        .flatMap(events => events) // Flatten the array of events
        .filter((date) => date !== null)
      : [];

    return [
      ...events.filter((event) => event !== null),
      ...disabledDatesFromLockDates,
    ];
  } catch (error) {
    console.log("Error retrieving events:", error.message);
    throw error;
  }
}

export async function getEventsByID(id) {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const listingsRef = ref(database, "events");

      try {
        const snapshot = await get(listingsRef);
        if (snapshot.exists()) {
          const listingsObject = snapshot.val();

          // Convert the object of objects to an array of objects with 'id' property
          const listingsArray = Object.keys(listingsObject).map((key) => ({
            id: key,
            ...listingsObject[key],
          }));

          const filteredListings = listingsArray.filter(
            (item) => item.listingId === id
          );

          if (filteredListings.length > 0) {
            // Assuming you want to return an array of matching items
            resolve(filteredListings);
          } else {
            console.log("No data found for the listings with :", id);
            resolve(null);
          }
        } else {
          console.log("No data found for the listings");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving listing details:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving listing details:", error);
    throw error;
  }
}

// export async function getEventsByUserId(userId) {
//   try {
//     const bookings = await getBookingsByUserId(userId);

//     const events =
//       Array.isArray(bookings) && bookings.length > 0
//         ? bookings.map(async (booking) => {
//             const getStatusColor = (status) => {
//               const statusColors = {
//                 pending: "red",
//                 Pending: "red",
//                 Paid: "#2196f3",
//                 "Waiver Pending": "#651fff",
//                 "Waiver Filled": "#2196F3",
//                 tripStarted: "orange",
//                 "Trip Completed": "#39570f",
//                 "Trip Concluded": "green",
//                 cancelled: "#E843C3",
//               };
//               return statusColors[status] || "black";
//             };

//             const newEvent = {
//               id: booking.id,
//               title: booking.listingTitle,
//               color: getStatusColor(booking.status),
//               overNight: 0,
//             };

//             let singleOffer;
//             let inquireInfo;
//             try {
//               singleOffer = await getOfferDetailsByInquiryId(booking.inquiryId);

//               inquireInfo = await getInquiryDataById(booking.inquiryId);

//             } catch (error) {
//               console.error(
//                 `Error fetching offer details for booking ID ${booking.id}:`,
//                 error.message
//               );
//               return null;
//             }

//             // Create a new Date object with the input date
//             let date = new Date(inquireInfo?.offerData?.offerDate);

//             // Subtract one day from the date
//             date.setDate(date.getDate() - 1);

//             // Format the date to "Friday, March 7, 2025"
//             let options = {
//               weekday: "long",
//               year: "numeric",
//               month: "long",
//               day: "numeric",
//             };
//             let formattedDatee = date.toLocaleDateString("en-US", options);

//             // prefferedDate
//             // :
//             // "Saturday, May 3, 2025"
//             // prefferedTime
//             // :
//             // "10:00:pm"

//             // Use default time if offeredTime is missing
//             const offeredTime = singleOffer?.offeredTime?.trim()
//               ? singleOffer.offeredTime
//               : inquireInfo.inquiryData.prefferedTime.trim()
//               ? inquireInfo.inquiryData.prefferedTime
//               : "00:00";

//             const isoDate = moment(
//               new Date(
//                 inquireInfo.inquiryData.prefferedDate?.trim() +
//                   " " +
//                   inquireInfo.inquiryData.prefferedTime
//               ),
//               "dddd, MMMM DD, YYYY"
//             );

//             const formattedDate = isoDate.format("YYYY-MM-DD");
//             const startDate = new Date(isoDate);
//             console.log('inqui234234reInfo',startDate)
//             if (isNaN(startDate)) {
//               console.error(
//                 `Invalid start date for booking ID ${booking.id}: ${formattedDate}`
//               );
//               return null;
//             }

//             // Calculate endDate by adding offeredHours or overnight
//             const endDate = new Date(startDate);
//             let endHours = 0;
//             if (inquireInfo?.inquiryData?.offeredHours > 0) {
//               endHours = inquireInfo?.inquiryData?.offeredHours;
//             } else if (inquireInfo?.inquiryData?.overnight > 0) {
//               endHours = parseInt(inquireInfo?.inquiryData?.overnight) * 24;
//             }
//             // endDate.setHours(endDate.getHours() + endHours);

//             newEvent.start = startDate;
//             newEvent.end = endDate;
//             newEvent.inquireInfo = singleOffer;
//             newEvent["overNight"] =
//               inquireInfo?.offerData?.overnight ||
//               inquireInfo?.inquiryData?.overnight;

//             return newEvent;
//           })
//         : [];

//     // Await all asynchronous operations inside the map function
//     const resolvedEvents = await Promise.all(events);

//     // Filter out null values (invalid events)
//     return resolvedEvents.filter((event) => event !== null);
//   } catch (error) {
//     console.log("Error retrieving events:", error.message);
//     throw error;
//   }
// }

export const getBookingCalendarColor = (status) => {
  const statusColors = {
    pending: "red",
    accepted: "green",
    paid: "#2196F3",
    "waiver pending": "#651FFF",
    "waiver filled": "#2196F3",
    tripstarted: "orange",
    // Stay grey until Cancel Hold / charge releases the deposit → Trip Concluded (green)
    "trip returned": "#3131309e",
    "trip completed": "#3131309e",
    "trip concluded": "green",
    cancelled: "#E843C3",
    canceled: "#E843C3",
    disputed: "#E843C3",
  };

  const normalized = (status || "").toLowerCase().trim();
  return statusColors[normalized] || "black";
};

export const formatBookingStatusLabel = (status) => {
  if (status == null || status === "" || status === "-") return "-";
  const normalized = String(status).toLowerCase().trim();
  const labels = {
    pending: "Pending",
    sent: "Offer Sent",
    accepted: "Accepted",
    paid: "Paid",
    "payment pending": "Payment Pending",
    "waiver pending": "Waiver Pending",
    "waiver filled": "Waiver Filled",
    tripstarted: "Trip Started",
    completed: "Trip Returned",
    "trip completed": "Trip Returned",
    "trip returned": "Trip Returned",
    "trip concluded": "Trip Concluded",
    cancelled: "Cancelled",
    canceled: "Cancelled",
    rejected: "Rejected",
    disputed: "Disputed",
    "in progress": "In Progress",
  };
  return labels[normalized] || status;
};

export async function getEventsByUserId(userId) {
  try {
    const bookings = await getBookingsByUserId(userId);

    const events =
      Array.isArray(bookings) && bookings.length > 0
        ? bookings.map(async (booking) => {
          if (booking?.isDeleted === true) {
            return {};
          }
          let inquireInfo;
          try {
            inquireInfo = await getInquiryDataById(booking.inquiryId);
          } catch (error) {
            console.error(
              `Error fetching offer details for booking ID ${booking.id}:`,
              error.message
            );
            return null;
          }

          const newEvent = {
            id: booking.id,
            title: booking.listingTitle,
            color: getBookingCalendarColor(booking.status),
            bookingStatus: booking.status,
            inquireInfo: inquireInfo,
            createdAt: booking.createdAt || booking.timestamp || inquireInfo?.timestamp || 0,
          };
          const startDate = getBookingEventStartDate(inquireInfo, booking);

          if (!startDate || isNaN(startDate)) {
            console.error(
              `Invalid start date for booking ID ${booking.id}`
            );
            return null;
          }
          // Calculate endDate by adding offeredHours or overnight
          const endDate = new Date(startDate);
          let endHours = 0;
          const offeredHours = parseInt(inquireInfo?.offerData?.offeredHours) ||
            parseInt(inquireInfo?.inquiryData?.sameDayHours) ||
            parseInt(booking.duration?.sameDayHours) || 0;

          const overnight = parseInt(inquireInfo?.offerData?.overnight) ||
            parseInt(inquireInfo?.inquiryData?.overnight) ||
            parseInt(booking.duration?.overnight) || 0;

          if (offeredHours > 0 && offeredHours < 24) {
            endHours = offeredHours;
          } else if (offeredHours >= 24) {
            endHours = offeredHours + 24;
          } else if (overnight > 0) {
            endHours = overnight * 24;
          }
          endDate.setHours(endDate.getHours() + endHours);
          newEvent.start = startDate;
          newEvent.end = endDate;
          newEvent.inquireInfo = inquireInfo;
          newEvent["overNight"] = overnight;
          newEvent["sameDayHour"] = offeredHours;
          return newEvent;
        })
        : [];
    // Await all asynchronous operations inside the map function
    const resolvedEvents = await Promise.all(events);
    // Filter out null values (invalid events)
    return resolvedEvents.filter((event) => event !== null);
  } catch (error) {
    console.log("Error retrieving events:", error.message);
    throw error;
  }
}

export async function getAllEvents(userId) {
  try {
    const bookings = await getAllBookings();
    const events =
      Array.isArray(bookings) && bookings.length > 0
        ? bookings.map(async (booking) => {
          if (booking?.isDeleted === true) {
            return {};
          }

          let inquireInfo;
          try {
            inquireInfo = await getInquiryDataById(booking.inquiryId);
          } catch (error) {
            console.error(
              `Error fetching offer details for booking ID ${booking.id}:`,
              error.message
            );
            return null;
          }

          const newEvent = {
            id: booking.id,
            title: booking.listingTitle,
            color: getBookingCalendarColor(booking.status),
            bookingStatus: booking.status,
            inquireInfo: inquireInfo,
            createdAt: booking.createdAt || booking.timestamp || inquireInfo?.timestamp || 0,
          };
          const startDate = getBookingEventStartDate(inquireInfo, booking);

          if (!startDate || isNaN(startDate)) {
            console.error(
              `Invalid start date for booking ID ${booking.id}`
            );
            return null;
          }
          // Calculate endDate by adding offeredHours or overnight
          const endDate = new Date(startDate);
          let endHours = 0;
          const offeredHours = parseInt(inquireInfo?.offerData?.offeredHours) ||
            parseInt(inquireInfo?.inquiryData?.sameDayHours) ||
            parseInt(booking.duration?.sameDayHours) || 0;

          const overnight = parseInt(inquireInfo?.offerData?.overnight) ||
            parseInt(inquireInfo?.inquiryData?.overnight) ||
            parseInt(booking.duration?.overnight) || 0;

          if (offeredHours > 0 && offeredHours < 24) {
            endHours = offeredHours;
          } else if (offeredHours >= 24) {
            endHours = offeredHours + 24;
          } else if (overnight > 0) {
            endHours = overnight * 24;
          }
          endDate.setHours(endDate.getHours() + endHours);
          newEvent.start = startDate;
          newEvent.end = endDate;
          newEvent.inquireInfo = inquireInfo;
          newEvent["overNight"] = overnight;
          newEvent["sameDayHour"] = offeredHours;
          return newEvent;
        })
        : [];
    // Await all asynchronous operations inside the map function
    const resolvedEvents = await Promise.all(events);
    // Filter out null values (invalid events)
    return resolvedEvents.filter((event) => event !== null);
  } catch (error) {
    console.log("Error retrieving events:", error.message);
    throw error;
  }
}

export async function getBoatOwnerPayments(id) {
  try {
    const userDetails = await getUserByID(id);
    console.log("userDetails");
    console.log(userDetails);

    if (userDetails.role == "Boat Owner") {
      const database = getDatabase();
      const ownerMetaPayments = ref(database, `ownerMeta/${id}`);
      const snapshot = await get(ownerMetaPayments);

      if (snapshot.exists()) {
        const paymentDetails = snapshot.val();

        // Convert paymentDetails object to an array of promises
        const promises = Object.values(paymentDetails).map((value) =>
          Promise.resolve(value)
        );

        // Wait for all promises to resolve
        const result = await Promise.all(promises);

        return result;
      }
    }
  } catch (error) {
    console.log("Error retrieving ownermeta payments: ", error.message);
  }
}

export async function getEventDetailsByEventID(id) {
  try {
    return new Promise(async (resolve, reject) => {
      const database = getDatabase();
      const listingsRef = ref(database, "events");

      try {
        const snapshot = await get(listingsRef);
        if (snapshot.exists()) {
          const listingsObject = snapshot.val();

          // Convert the object of objects to an array of objects with 'id' property
          const listingsArray = Object.keys(listingsObject).map((key) => ({
            id: key,
            ...listingsObject[key],
          }));

          const filteredListings = listingsArray.filter(
            (item) => item.id === id
          );

          if (filteredListings.length > 0) {
            // Assuming you want to return an array of matching items
            resolve(filteredListings);
          } else {
            console.log("No data found for the listings with :", id);
            resolve(null);
          }
        } else {
          console.log("No data found for the listings");
          resolve(null);
        }
      } catch (error) {
        console.error("Error retrieving listing details:", error);
        reject(error);
      }
    });
  } catch (error) {
    console.error("Error retrieving listing details:", error);
    throw error;
  }
}

export const updateDisputeStatus = async (disputeId, status) => {
  try {
    const database = getDatabase();
    // Assuming you are using Firebase Realtime Database
    await update(ref(database, `disputes/${disputeId}`), { status });
    console.log(`dispute ${disputeId} status updated to ${status}`);
  } catch (error) {
    console.error("Error updating dispute status:", error);
  }
};

export const updateBookingStatus = async (bookingId, status, options = {}) => {
  try {
    const database = getDatabase();
    const bookingRef = ref(database, `bookings/${bookingId}`);
    const snapshot = await get(bookingRef);
    const previousStatus = snapshot.exists() ? snapshot.val().status : null;

    await update(bookingRef, { status });
    console.log(`Booking ${bookingId} status updated to ${status}`);

    if (
      !options.skipRenterTripEndNotification &&
      isTripEndedStatus(status) &&
      !isTripEndedStatus(previousStatus)
    ) {
      notifyTripEndedForRenter(bookingId, {
        hasIssue: options.hasTripIssue === true,
      }).catch((err) =>
        console.error("Failed to create renter trip end notification:", err)
      );
    }
  } catch (error) {
    console.error("Error updating booking status:", error);
  }
};

export const updateProStaffStatus = async (userId, proStaffStatus) => {
  try {
    const database = getDatabase();
    // Assuming you are using Firebase Realtime Database
    await update(ref(database, `ownerMeta/${userId}`), { proStaffStatus });
    console.log(`${userId} status updated to ${proStaffStatus}`);
  } catch (error) {
    console.error("Error updating status:", error);
  }
};
export const getProStaffMemberById = async (userId) => {
  try {
    const database = getDatabase();

    // First, fetch the user's data from the users node
    const userSnapshot = await get(ref(database, `users/${userId}`));

    // Then fetch the ownerMeta data which might contain the referral code
    const ownerMetaSnapshot = await get(ref(database, `ownerMeta/${userId}`));

    // Combine the data
    let userData = null;
    let ownerMetaData = null;
    let refferalCode = null; // Note: Using the spelling from the database (with two f's)

    if (userSnapshot.exists()) {
      userData = userSnapshot.val();
      userData.userId = userId; // Add the userId to the user data

      // Check if user data has a referral code
      if (userData.refferalCode) {
        refferalCode = userData.refferalCode;
      }
    }

    if (ownerMetaSnapshot.exists()) {
      ownerMetaData = ownerMetaSnapshot.val();

      // Check if ownerMeta has a referral code and we don't already have one
      if (ownerMetaData.refferalCode && !refferalCode) {
        refferalCode = ownerMetaData.refferalCode;
      }
    }

    // If we have both user data and owner meta data, combine them
    if (userData && ownerMetaData) {
      const combinedData = {
        ...userData,
        ownerMeta: ownerMetaData,
        refferalCode: refferalCode || "", // Always include refferalCode at the top level
      };
      console.log("Combined pro staff member data:", combinedData);
      return combinedData;
    }
    // If we only have user data
    else if (userData) {
      // Add refferalCode at the top level if not already there
      if (!userData.refferalCode) {
        userData.refferalCode = refferalCode || "";
      }
      console.log("User data found but no owner meta data for ID:", userId);
      return userData;
    }
    // If we only have owner meta data
    else if (ownerMetaData) {
      const result = {
        ownerMeta: ownerMetaData,
        refferalCode: refferalCode || "", // Always include refferalCode at the top level
      };
      console.log("Owner meta data found but no user data for ID:", userId);
      return result;
    }
    // If we have neither
    else {
      console.log(`No data found for user ID: ${userId}`);
      return null;
    }
  } catch (error) {
    console.error("Error getting pro staff member:", error);
    return null;
  }
};

export const checkAndUpdateStatus = async (bookingId) => {
  try {
    const database = getDatabase();

    // Fetch the current waivers and status
    const bookingRef = ref(database, `bookings/${bookingId}`);
    const bookingSnapshot = await get(bookingRef);
    const inquiryData = await getInquiryDataById(
      bookingSnapshot.val().inquiryId
    );
    console.log("inquiryData from check and update");
    console.log(inquiryData);

    const currentWaivers = bookingSnapshot.val().waiversFilled || {
      adult: 0,
      minor: 0,
    };
    const currentStatus = bookingSnapshot.val().status;

    // Check if both adult and minor waivers are completed
    if (
      currentWaivers.adult ===
      inquiryData[0].inquiryData.adults +
      inquiryData[0].inquiryData.seniors &&
      currentWaivers.minor ===
      inquiryData[0].inquiryData.infants + inquiryData[0].inquiryData.children
    ) {
      // Update the status to 'Paid'
      await update(bookingRef, { status: "Paid" });
      console.log(`Status updated to Paid`);
    } else {
      console.log(`Waivers are not completed yet`);
    }
  } catch (error) {
    console.error("Error checking and updating status:", error);
  }
};

// Renter drives when boatCaptain is false (renter may operate the boat).
// Owner/captain-driven listings (boatCaptain === true) require waivers for every passenger.
export const isRenterCaptainBoat = (boatDetails) =>
  boatDetails?.boatCaptain === false;

export const getTotalPartySize = (bookingData) => {
  const bookingPax = getPassengerCounts(bookingData?.passengers);
  const inquiryPax = getPassengerCounts(
    bookingData?.inquiryData || bookingData?.inquiry?.inquiryData
  );
  const totalWaivers = toPassengerCount(
    bookingData?.totalWaivers ?? 0,
    "total"
  );
  return Math.max(bookingPax.total, inquiryPax.total, totalWaivers);
};

// On renter-driven boats the captain completes their waiver at trip start.
export const getRequiredPassengerWaivers = (bookingData, boatDetails) => {
  const total = getTotalPartySize(bookingData);
  if (isRenterCaptainBoat(boatDetails)) {
    return Math.max(0, total - 1);
  }
  return total;
};

export const getFilledPassengerWaivers = (bookingData) => {
  const waivers = bookingData?.waiversFilled || { adult: 0, minor: 0 };
  return (waivers.adult || 0) + (waivers.minor || 0);
};

export const getSignedPassengerWaiverCount = (bookingData) => {
  if (!bookingData?.tripData?.waivers) {
    return 0;
  }

  let count = 0;
  const adultWaivers = bookingData.tripData.waivers["Adult Waiver"];
  const minorWaivers = bookingData.tripData.waivers["Minor Waiver"];

  if (adultWaivers) {
    count += Object.keys(adultWaivers).length;
  }
  if (minorWaivers) {
    count += Object.keys(minorWaivers).length;
  }

  return count;
};

// Passenger waivers are complete when all required pre-trip waivers are submitted.
export const arePassengerWaiversComplete = (bookingData, boatDetails) => {
  const filled = getFilledPassengerWaivers(bookingData);
  const required = getRequiredPassengerWaivers(bookingData, boatDetails);

  if (required === 0) {
    return true;
  }

  return filled >= required;
};

export const DIRECT_CHARGE_INSPECTION_ANSWERS = [
  "Charge fuel surcharge",
  "Charge for cleaning",
];

export const isDirectChargeInspectionAnswer = (answer) =>
  DIRECT_CHARGE_INSPECTION_ANSWERS.includes(answer);

export const isInspectionChargeAnswer = (answer) =>
  isDirectChargeInspectionAnswer(answer) || answer === "There's an issue";

export const getInspectionIssueCategory = (question = "", answer = "") => {
  const questionText = String(question).toLowerCase();
  const answerText = String(answer).toLowerCase();

  if (
    answerText.includes("fuel surcharge") ||
    questionText.includes("fuel surcharge") ||
    questionText.includes("fuel") ||
    questionText.includes("gas")
  ) {
    return "fuel";
  }
  if (
    answerText.includes("cleaning") ||
    questionText === "cleaning" ||
    questionText.includes("cleaning")
  ) {
    return "cleaning";
  }
  if (questionText.includes("trash")) {
    return "cleaning";
  }
  return "damage";
};

export const getInspectionIssueItemsFromBooking = (booking) => {
  const checklist =
    booking?.tripData?.waivers?.["Boat Owner Equipment Checklist"];
  if (!checklist) {
    return [];
  }

  const entries = Object.values(checklist);
  if (!entries.length) {
    return [];
  }

  const questions = entries[entries.length - 1]?.questions || [];
  const issues = [];

  for (let index = 0; index < questions.length; index += 1) {
    const current = questions[index];
    if (!isInspectionChargeAnswer(current?.answer)) {
      continue;
    }

    const issueItem = {
      question: current.question,
      answer: current.answer,
      category: getInspectionIssueCategory(current.question, current.answer),
      photos: [],
    };

    const next = questions[index + 1];
    if (next?.question === "Picture" && next?.answer) {
      if (Array.isArray(next.answer)) {
        issueItem.photos = next.answer
          .map((item) => (typeof item === "string" ? item : item?.url))
          .filter(Boolean);
      } else if (typeof next.answer === "string" && next.answer) {
        issueItem.photos = [next.answer];
      }
    }

    issues.push(issueItem);
  }

  return issues;
};

export const getInspectionIssueCategoriesFromBooking = (booking) => {
  const items = getInspectionIssueItemsFromBooking(booking);
  return [
    ...new Set(
      items.map((item) => item.category || getInspectionIssueCategory(item.question, item.answer))
    ),
  ];
};

export const requiresRenterApprovalForInspection = (booking) =>
  getInspectionIssueCategoriesFromBooking(booking).includes("damage");

export const allowsDirectChargeFromInspection = (booking) => {
  const categories = getInspectionIssueCategoriesFromBooking(booking);
  return (
    categories.length > 0 &&
    categories.every((category) => category === "fuel" || category === "cleaning")
  );
};

export const getRenterInspectionIssueCategories = (booking) => {
  const categories = getInspectionIssueCategoriesFromBooking(booking);
  if (categories.length) {
    return categories;
  }

  const claimCategories = booking?.damageClaim?.categories;
  if (Array.isArray(claimCategories) && claimCategories.length) {
    return [...new Set(claimCategories.filter(Boolean))];
  }

  return [];
};

export const formatRenterInspectionIssuePhrase = (booking) => {
  const categories = getRenterInspectionIssueCategories(booking);
  const parts = [];

  if (categories.includes("fuel")) {
    parts.push("a fuel surcharge");
  }
  if (categories.includes("cleaning")) {
    parts.push("a cleaning charge");
  }
  if (categories.includes("damage")) {
    parts.push("damage or another issue");
  }

  if (!parts.length) {
    return "an issue, fuel surcharge, or cleaning charge";
  }

  if (parts.length === 1) {
    return parts[0];
  }

  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`;
  }

  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
};

export const formatRenterInspectionChargeLabel = (booking) => {
  const categories = getRenterInspectionIssueCategories(booking);
  const labels = categories
    .map((category) => CLAIM_CATEGORY_LABELS[category])
    .filter(Boolean);

  if (!labels.length) {
    return "Damage";
  }

  if (labels.length === 1) {
    return labels[0];
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }

  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};

export const CLAIM_CATEGORY_LABELS = {
  fuel: "Fuel surcharge",
  cleaning: "Cleaning",
  damage: "Damage",
};

const getClaimIssueItems = (claim) => {
  if (!claim?.issueItems) {
    return [];
  }

  return Array.isArray(claim.issueItems)
    ? claim.issueItems
    : Object.values(claim.issueItems);
};

export const getDamageClaimChargeLines = (booking) => {
  const claim = booking?.damageClaim;
  const chargedAmount = Number(booking?.damageCharge);
  const claimAmount = Number(claim?.amount);
  const amount =
    Number.isFinite(chargedAmount) && chargedAmount > 0
      ? chargedAmount
      : Number.isFinite(claimAmount)
        ? claimAmount
        : 0;

  if ((!claim || claim.status === "rejected") && !(amount > 0)) {
    return [];
  }

  if (!claim && amount > 0) {
    return [
      {
        key: "damage",
        label: CLAIM_CATEGORY_LABELS.damage,
        amount,
        status: "charged",
      },
    ];
  }

  if (!claim || !(amount > 0) || claim.status === "rejected") {
    return [];
  }

  const categoryAmounts = claim.categoryAmounts;
  if (categoryAmounts && typeof categoryAmounts === "object") {
    const lines = Object.entries(categoryAmounts)
      .filter(([, value]) => Number(value) > 0)
      .map(([key, value]) => ({
        key,
        label: CLAIM_CATEGORY_LABELS[key] || key,
        amount: Number(value),
        status: claim.status,
      }));

    if (lines.length) {
      return lines;
    }
  }

  const items = getClaimIssueItems(claim);
  let categories = [
    ...new Set(
      items
        .map(
          (item) =>
            item.category || getInspectionIssueCategory(item?.question, item?.answer)
        )
        .filter(Boolean)
    ),
  ];

  if (!categories.length && Array.isArray(claim.categories)) {
    categories = [...new Set(claim.categories.filter(Boolean))];
  }

  if (categories.length === 1) {
    return [
      {
        key: categories[0],
        label: CLAIM_CATEGORY_LABELS[categories[0]],
        amount,
        status: claim.status,
      },
    ];
  }

  if (categories.length > 1) {
    return [
      {
        key: "combined",
        label: categories.map((key) => CLAIM_CATEGORY_LABELS[key]).join(", "),
        amount,
        status: claim.status,
      },
    ];
  }

  const reason = String(claim.reason || "").toLowerCase();
  if (reason.includes("fuel") || reason.includes("gas")) {
    return [
      {
        key: "fuel",
        label: CLAIM_CATEGORY_LABELS.fuel,
        amount,
        status: claim.status,
      },
    ];
  }
  if (reason.includes("clean") || reason.includes("trash")) {
    return [
      {
        key: "cleaning",
        label: CLAIM_CATEGORY_LABELS.cleaning,
        amount,
        status: claim.status,
      },
    ];
  }

  return [
    {
      key: "damage",
      label: CLAIM_CATEGORY_LABELS.damage,
      amount,
      status: claim.status,
    },
  ];
};

// Post-trip charges (fuel, cleaning, damage) are credited to ownerMeta when
// captured from the security deposit hold, but are not stored as payment rows.
export const getPostTripOwnerChargeCents = (booking) => {
  const claim = booking?.damageClaim;
  const chargedDollars = Number(booking?.damageCharge);
  const claimDollars = Number(claim?.amount);
  const amountDollars =
    Number.isFinite(chargedDollars) && chargedDollars > 0
      ? chargedDollars
      : Number.isFinite(claimDollars) && claimDollars > 0
        ? claimDollars
        : 0;

  if (amountDollars <= 0) return 0;

  const status = String(claim?.status || "").toLowerCase();
  if (status === "rejected" || status === "pending_renter_approval") {
    return 0;
  }

  // Only count charges that were actually captured from the hold.
  if (!claim || status === "charged") {
    return Math.round(amountDollars * 100);
  }

  return 0;
};

export const getPostTripChargeReason = (booking) => {
  const lines = getDamageClaimChargeLines(booking).filter(
    (line) => String(line.status || "").toLowerCase() === "charged"
  );
  if (lines.length === 1) {
    return lines[0].label;
  }
  if (lines.length > 1) {
    return lines.map((line) => line.label).join(", ");
  }

  const categories = getRenterInspectionIssueCategories(booking);
  if (categories.length) {
    return categories
      .map((category) => CLAIM_CATEGORY_LABELS[category] || category)
      .join(", ");
  }

  return "Post-trip charge";
};

export const getPostTripChargeDate = (booking) => {
  const claim = booking?.damageClaim;
  if (claim?.chargedAt) {
    return new Date(claim.chargedAt).toISOString();
  }
  if (claim?.renterRespondedAt) {
    return new Date(claim.renterRespondedAt).toISOString();
  }
  if (booking?.tripData?.endTime) {
    return new Date(booking.tripData.endTime).toISOString();
  }
  return null;
};

const sumOwnerPostTripChargesCents = (ownerId, allBookings) => {
  return (allBookings || []).reduce((sum, booking) => {
    if (!booking || booking.owner !== ownerId) return sum;
    return sum + getPostTripOwnerChargeCents(booking);
  }, 0);
};

const buildOwnerPostTripChargeTransactions = (ownerId, allBookings) => {
  return (allBookings || [])
    .filter(
      (booking) =>
        booking?.owner === ownerId && getPostTripOwnerChargeCents(booking) > 0
    )
    .map((booking) => {
      const amount = getPostTripOwnerChargeCents(booking);
      return {
        id: `postTrip-${booking.id}`,
        type: "payment",
        amount,
        totalAmount: amount,
        date: getPostTripChargeDate(booking),
        status: "completed",
        tripCompleted: true,
        bookingId: booking.id,
        reason: getPostTripChargeReason(booking),
        userId: booking.renter,
        charterDeposit: false,
        cashBalanceCollected: 0,
      };
    });
};

export const formatUploadedAssetUrl = (filename) => {
  if (!filename) {
    return "";
  }

  const value =
    typeof filename === "string" ? filename : filename?.url || "";

  if (!value) {
    return "";
  }

  if (value.startsWith("http") || value.startsWith("data:")) {
    return value;
  }

  return `${process.env.REACT_APP_BASE_URL}/images/${value}`;
};

const CHAT_IMAGE_URL_REGEX = /\.(jpg|jpeg|png|gif|webp|bmp)(\?[^\s<]*)?$/i;

export const formatChatMessageHtml = (message) => {
  if (!message) {
    return "";
  }

  let html = message.replace(/LineBreak/g, "<br />");
  const urlRegex = /(https?:\/\/[^\s<]+)/g;

  html = html.replace(urlRegex, (url) => {
    if (CHAT_IMAGE_URL_REGEX.test(url)) {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer"><img src="${url}" alt="Attached image" style="max-width: 100%; max-height: 220px; border-radius: 8px; margin: 6px 0; display: block;" /></a>`;
    }

    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">${url}</a>`;
  });

  return html;
};

const getChatReadStatusUpdates = (chatData, senderId) => {
  if (chatData.senderId === senderId) {
    return { senderReadStatus: 1, receiverReadStatus: 0 };
  }

  return { senderReadStatus: 0, receiverReadStatus: 1 };
};

export const sendBookingChatMessage = async ({
  ownerId,
  renterId,
  senderId,
  receiverId,
  message,
  listingId,
  inquiryId,
}) => {
  const database = getDatabase();
  const chatId1 = `${ownerId}_${renterId}`;
  const chatId2 = `${renterId}_${ownerId}`;
  const messageData = {
    senderId,
    receiverId,
    message,
    timestamp: serverTimestamp(),
    listingId,
    inquiryId,
    offerStatus: null,
  };

  const chatRef1 = ref(database, `chats/${chatId1}`);
  const chatRef2 = ref(database, `chats/${chatId2}`);
  const chatSnapshot1 = await get(chatRef1);
  const chatSnapshot2 = await get(chatRef2);
  let usedChatId;

  if (chatSnapshot1.exists()) {
    const chatData = chatSnapshot1.val();
    const existingMessages = chatData.messages || [];
    usedChatId = chatId1;
    await update(chatRef1, {
      lastSenderId: senderId,
      messages: [...existingMessages, messageData],
      ...getChatReadStatusUpdates(chatData, senderId),
    });
  } else if (chatSnapshot2.exists()) {
    const chatData = chatSnapshot2.val();
    const existingMessages = chatData.messages || [];
    usedChatId = chatId2;
    await update(chatRef2, {
      lastSenderId: senderId,
      messages: [...existingMessages, messageData],
      ...getChatReadStatusUpdates(chatData, senderId),
    });
  } else {
    usedChatId = chatId1;
    await set(chatRef1, {
      senderId: ownerId,
      receiverId: renterId,
      senderReadStatus: senderId === ownerId ? 1 : 0,
      receiverReadStatus: senderId === ownerId ? 0 : 1,
      lastSenderId: senderId,
      messages: [messageData],
    });
  }

  fetch(`${process.env.REACT_APP_BASE_URL}/schedule-message`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      senderId,
      receiverId,
      lastSenderId: senderId,
      chatId: usedChatId,
    }),
  }).catch((err) => console.error("Error scheduling inbox message notification:", err));

  return usedChatId;
};

export const updateWaiversFilled = async (bookingId, type) => {
  try {
    const database = getDatabase();

    // Fetch the current waivers object
    const bookingRef = ref(database, `bookings/${bookingId}`);
    const bookingSnapshot = await get(bookingRef);
    const bookingData = bookingSnapshot.val();
    const currentWaivers = bookingData.waiversFilled || {
      adult: 0,
      minor: 0,
    };
    const currentStatus = bookingData.status;

    currentWaivers[type] = currentWaivers[type] + 1;

    // Filling any passenger waiver marks the booking as Waiver Filled
    // (remaining pending waivers are still tracked separately).
    const updateData = {
      waiversFilled: currentWaivers,
    };

    if (
      currentStatus !== "tripStarted" &&
      currentStatus !== "Completed" &&
      currentStatus !== "Trip Returned" &&
      currentStatus !== "Trip Completed" &&
      currentStatus !== "Trip Concluded"
    ) {
      updateData.status = "Waiver Filled";
    }

    // Update the database with the modified waivers object
    await update(bookingRef, updateData);

    if (bookingData.owner) {
      notifyOwnerWaiverCompleted(
        bookingData.owner,
        bookingData.listingTitle,
        bookingId
      ).catch((err) =>
        console.error("Failed to create owner notification:", err)
      );
    }

    console.log(`status updated`);
  } catch (error) {
    console.error("Error updating booking status:", error);
  }
};

export const updateInquiryStatus = async (inquiryId, status) => {
  try {
    const database = getDatabase();
    // Assuming you are using Firebase Realtime Database
    await update(ref(database, `inquiries/${inquiryId}`), { status });
    console.log(`Inquiries ${inquiryId} status updated to ${status}`);
  } catch (error) {
    console.error("Error updating inquiry status:", error);
  }
};

export const updateWithdrawalRequestStatus = async (withdrawalId, status) => {
  try {
    const database = getDatabase();
    // Assuming you are using Firebase Realtime Database
    await update(ref(database, `withdrawals/${withdrawalId}`), { status });
    console.log(`Withdrawal ${withdrawalId} status updated to ${status}`);
  } catch (error) {
    console.error("Error updating booking status:", error);
  }
};

export const isOffPlatformDeposit = (paymentDetails) =>
  paymentDetails?.paymentHeld?.status === "off_platform_deposit";

export const getSecurityAmountCents = (boatType) =>
  boatType?.toLowerCase() === "kayak"
    ? 20000
    : boatType?.toLowerCase() === "jetski"
    ? 50000
    : 80000;

export const recordOffPlatformDeposit = async (
  paymentId,
  boatType,
  overriddenBy
) => {
  const database = getDatabase();
  const paymentRef = ref(database, `payments/${paymentId}`);
  const currentData = (await get(paymentRef)).val() || {};

  const paymentHeld = {
    status: "off_platform_deposit",
    method: "admin_override",
    amount: getSecurityAmountCents(boatType),
    overriddenBy,
    overriddenAt: new Date().toISOString(),
  };

  await update(paymentRef, { ...currentData, paymentHeld });
};

export const updateHeldPaymentStatus = async (paymentId, status) => {
  try {
    const database = getDatabase();
    // Assuming you are using Firebase Realtime Database
    await update(ref(database, `payments/${paymentId}`), { status });
    console.log(`Booking ${paymentId} status updated to ${status}`);
  } catch (error) {
    console.error("Error updating booking status:", error);
  }
};

// export const updateEventStatus = async (eventId, color) => {
//   try {
//     const database = getDatabase();
//     // Assuming you are using Firebase Realtime Database
//     await update(ref(database, `events/${eventId}`), { color });
//     console.log(`Event ${eventId} status updated to ${color}`);
//   } catch (error) {
//     console.error('Error updating event color:', error);
//   }
// };
export async function duplicateListing(listingId) {
  try {
    const database = getDatabase();
    const listingRef = ref(database, `listings/${listingId}`);
    const snapshot = await get(listingRef);

    if (!snapshot.exists()) {
      throw new Error("Listing not found");
    }

    const originalListing = snapshot.val();
    
    // Create a deep copy of the listing
    const duplicatedListing = JSON.parse(JSON.stringify(originalListing));
    
    // Modify the title to indicate it's a copy
    duplicatedListing.listingTitle = originalListing.listingTitle 
      ? `${originalListing.listingTitle} (Copy)` 
      : "Untitled (Copy)";
    
    // Set status to Pending for the duplicated listing
    duplicatedListing.boatStatus = "Pending";
    duplicatedListing.boatListed = false;
    
    // Remove any booking-related data that shouldn't be copied
    delete duplicatedListing.bookings;
    delete duplicatedListing.lockDates;
    
    // Push the new listing to Firebase
    const newListingRef = push(ref(database, "listings/"));
    await set(newListingRef, duplicatedListing);
    
    return { success: true, newListingId: newListingRef.key };
  } catch (error) {
    console.error("Error duplicating listing:", error);
    throw error;
  }
}

export const chatWithAgent = async (message, history = []) => {
  try {
    const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/chatbot`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        question: message || "Hello",
        history: history,
      }),
    });

    if (!res.ok) throw new Error("Failed to fetch response from backend");

    return await res.json();
  } catch (error) {
    console.error("Error with AI:", error);
    return { error: "Failed to get response" };
  }
};

// ============================================
// PAYMENTS DASHBOARD FUNCTIONS (Admin Only)
// ============================================

const COMPLETED_TRIP_BOOKING_STATUSES = new Set([
  "trip concluded",
  "trip completed",
  "trip returned",
  "completed",
]);

// "refunded" / "dispute resolved" are hold-release statuses written at trip end
// (see Event.js concludeTrip). They are not owner-payout reversals.
const CREDITED_PAYMENT_STATUSES = new Set([
  "completed",
  "paid",
  "refunded",
  "dispute resolved",
]);

const isCompletedTripBooking = (booking) =>
  COMPLETED_TRIP_BOOKING_STATUSES.has(
    String(booking?.status || "").toLowerCase()
  );

const isCreditedPaymentRecord = (payment, booking) => {
  if (!payment) return false;
  if (payment.reason === "Tip") return true;
  if (payment.tripCompleted === true) return true;
  const status = String(payment.status || "").toLowerCase();
  if (CREDITED_PAYMENT_STATUSES.has(status)) return true;
  return isCompletedTripBooking(booking);
};

const getLinkedBooking = (payment, bookingsById, bookingsByPaymentId) => {
  if (payment?.bookingId && bookingsById?.[payment.bookingId]) {
    return bookingsById[payment.bookingId];
  }
  if (payment?.id && bookingsByPaymentId?.[payment.id]) {
    return bookingsByPaymentId[payment.id];
  }
  return null;
};

const buildBookingsByPaymentId = (allBookings) =>
  (allBookings || []).reduce((acc, booking) => {
    if (booking?.paymentId) acc[booking.paymentId] = booking;
    return acc;
  }, {});

const isCompletedTripPayment = (payment, booking) => {
  const status = String(payment?.status || "").toLowerCase();
  return (
    payment?.tripCompleted === true ||
    status === "completed" ||
    status === "refunded" ||
    status === "dispute resolved" ||
    isCompletedTripBooking(booking)
  );
};

const getOwnerCreditedPaymentAmount = (payment, ownerId, booking) => {
  if (!payment || !isCreditedPaymentRecord(payment, booking)) return 0;

  if (payment.referrer === ownerId) {
    return Number(payment.referAmount) || 0;
  }

  if (payment.owner !== ownerId) return 0;

  let ownerAmount = Number(payment.ownerAmount) || 0;

  if (isCompletedTripPayment(payment, booking) && payment.charterDeposit && payment.collectBalanceAtMeetup) {
    ownerAmount += Number(payment.charterCashBalance) || Number(payment.charterRemainingBalance) || 0;
  }

  return ownerAmount;
};

const getRentalAmountFromPaymentCents = (payment) => {
  const finalAmount = Number(payment?.totalAmount) || 0;
  const tax = Number(payment?.totalTax) || 0;
  return Math.max(0, Math.round((finalAmount - tax) / 1.05));
};

const getFirstRentalAdminFeeCents = (
  payment,
  offerPriceCents = 0,
  rentalAmountCents = 0
) => {
  if (!payment?.firstRentalPayment) return 0;

  const storedFirstCut = Number(payment.adminFirstTimeCut) || 0;
  if (storedFirstCut > 0) return storedFirstCut;

  if (payment.firstTimeSpecialRate) {
    const base = offerPriceCents > 0 ? offerPriceCents : rentalAmountCents;
    return base > 0 ? Math.round(base * 0.5) : 0;
  }

  const base = offerPriceCents > 0 ? offerPriceCents : rentalAmountCents;
  return base >= 20000 ? 10000 : 0;
};

// The $100 admin fee is one-time per boat owner. Multiple payments can be
// flagged firstRentalPayment when bookings were created before the first trip
// completed, so only the earliest flagged payment should carry the fee.
const getOwnerFirstRentalPaymentId = (ownerPayments) => {
  const candidates = (ownerPayments || [])
    .filter((p) => p?.firstRentalPayment && p.reason !== "Tip")
    .sort((a, b) => {
      const dateA = a.dateTime ? new Date(a.dateTime) : new Date(0);
      const dateB = b.dateTime ? new Date(b.dateTime) : new Date(0);
      return dateA - dateB;
    });

  return candidates[0]?.id || null;
};

const paymentQualifiesForFirstRentalFee = (payment, ownerFirstRentalPaymentId) => {
  if (!payment?.firstRentalPayment || payment.reason === "Tip") return false;
  if (!ownerFirstRentalPaymentId) return true;
  return payment.id === ownerFirstRentalPaymentId;
};

const getPaymentOfferPriceCents = (payment, bookingsById, allInquiries) => {
  const booking = payment?.bookingId ? bookingsById[payment.bookingId] : null;
  const inquiryId = booking?.inquiryId;
  const offerPriceDollars = inquiryId && allInquiries[inquiryId]?.offerData?.offerPrice;
  const offerPrice = Number(offerPriceDollars);
  return Number.isFinite(offerPrice) ? Math.round(offerPrice * 100) : 0;
};

const getPaymentCommissionRate = (payment) => {
  const cutPct = Number(payment?.cutPercentage);
  return Number.isFinite(cutPct) && cutPct >= 0 ? cutPct : 0.1;
};

const buildReferralRateMap = (allReferralCodes) => {
  const map = {};
  (allReferralCodes || []).forEach((entry) => {
    const code = normalizeReferralCode(entry?.code);
    if (!code) return;
    const rate = parseCommissionRate(entry?.commissionRate);
    if (rate != null) map[code] = rate;
  });
  return map;
};

const getOwnerCommissionRateForPayment = (payment, ownerReferralCode, referralRateByCode) => {
  const ownerCode = normalizeReferralCode(ownerReferralCode);
  if (ownerCode && referralRateByCode[ownerCode] != null) {
    return referralRateByCode[ownerCode];
  }
  return getPaymentCommissionRate(payment);
};

const getOwnerShareFromCutPercentage = (cutPct) => {
  return Number.isFinite(cutPct) && cutPct >= 0 ? 1 - cutPct : 0.9;
};

const getOwnerNetEarningsCents = (
  payment,
  ownerId,
  bookingsById,
  allInquiries,
  ownerReferralCode,
  referralRateByCode,
  ownerFirstRentalPaymentId = null,
  bookingsByPaymentId = null
) => {
  const booking = getLinkedBooking(payment, bookingsById, bookingsByPaymentId);
  if (!payment || !isCreditedPaymentRecord(payment, booking)) return 0;

  if (payment.referrer === ownerId) {
    return Number(payment.referAmount) || 0;
  }

  if (payment.owner !== ownerId) return 0;

  if (payment.reason === "Tip") {
    return Number(payment.ownerAmount) || 0;
  }

  const storedNet = getOwnerCreditedPaymentAmount(payment, ownerId, booking);
  const offerPriceCents = getPaymentOfferPriceCents(payment, bookingsById, allInquiries);
  const rentalAmountCents = getRentalAmountFromPaymentCents(payment);
  if (!offerPriceCents) return storedNet;

  const cutPct = getOwnerCommissionRateForPayment(
    payment,
    ownerReferralCode,
    referralRateByCode
  );
  const projectedGross = Math.round(
    offerPriceCents * getOwnerShareFromCutPercentage(cutPct)
  );
  const applyFirstRentalFee = paymentQualifiesForFirstRentalFee(
    payment,
    ownerFirstRentalPaymentId
  );
  const firstRentalFee = applyFirstRentalFee
    ? getFirstRentalAdminFeeCents(payment, offerPriceCents, rentalAmountCents)
    : 0;
  let projectedNet = Math.max(0, projectedGross - firstRentalFee);

  let charterCash = 0;
  if (isCompletedTripPayment(payment, booking) && payment.charterDeposit && payment.collectBalanceAtMeetup) {
    charterCash =
      Number(payment.charterCashBalance) ||
      Number(payment.charterRemainingBalance) ||
      0;
    projectedNet += charterCash;
  }

  // Prefer the amount that actually hit ownerMeta. Projected offer-price
  // math inflates historical hold-released trips (e.g. Neil Beebee) above
  // what was credited and withdrawn. Only raise to projected when the
  // owner's current referral cut is better than the stored payment cut.
  const storedCut = getPaymentCommissionRate(payment);
  const retroactiveBetterCut = cutPct + 1e-9 < storedCut;
  let net;
  if (storedNet > 0 && !retroactiveBetterCut) {
    net = storedNet;
  } else {
    net = Math.max(storedNet, projectedNet);
  }

  // Cap at owner share minus the one-time first rental admin fee so gross
  // projections never show up as earnings when adminFirstTimeCut was missing.
  if (applyFirstRentalFee && payment.firstRentalPayment && firstRentalFee > 0) {
    net = Math.min(net, Math.max(0, projectedGross - firstRentalFee) + charterCash);
  }

  return net;
};

const sumOwnerWithdrawalsInCents = (ownerWithdrawals, statuses) => {
  return (ownerWithdrawals || [])
    .filter((w) => statuses.includes(w.status))
    .reduce((sum, w) => sum + (Number(w.amount) || 0), 0) * 100;
};

const reconcileOwnerAvailableCents = (
  metaCurrent,
  totalNetEarnings,
  processedWithdrawalsInCents,
  pendingWithdrawalsInCents
) => {
  const earnedMinusProcessed = Math.max(
    0,
    totalNetEarnings - (Number(processedWithdrawalsInCents) || 0)
  );
  // Withdrawable cash is the live ownerMeta wallet. Reconstructed payment
  // totals can exceed that wallet after hold-released trips are included;
  // those extras were already paid out and must not become a new balance.
  const reconciledBeforePending = Number(metaCurrent) || 0;
  const available = Math.max(
    0,
    reconciledBeforePending - (Number(pendingWithdrawalsInCents) || 0)
  );
  return { available, reconciledBeforePending, earnedMinusProcessed };
};

const getPaymentBbrCommissionCents = (
  payment,
  bookingsById,
  allInquiries,
  ownerReferralCode,
  referralRateByCode
) => {
  if (!payment || payment.reason === "Tip") return 0;

  const offerPriceCents = getPaymentOfferPriceCents(payment, bookingsById, allInquiries);
  const finalAmount = Number(payment.totalAmount) || 0;
  const tax = Number(payment.totalTax) || 0;
  const rentalAmountCents = Math.max(0, Math.round((finalAmount - tax) / 1.05));
  const commissionBaseCents = offerPriceCents > 0 ? offerPriceCents : rentalAmountCents;
  const cutPct = getOwnerCommissionRateForPayment(
    payment,
    ownerReferralCode,
    referralRateByCode
  );

  return Math.round(commissionBaseCents * cutPct);
};

/**
 * Compute withdrawable balance using payment records and the owner's current
 * referral-code commission rate (supports retroactive ZERO / reduced rates).
 */
export async function getOwnerAvailableBalance(ownerId) {
  const database = getDatabase();
  const [
    allPayments,
    allWithdrawals,
    ownerMeta,
    allBookings,
    inquiriesSnap,
    allReferralCodes,
    ownerDetails,
  ] = await Promise.all([
    getAllPayments(),
    getWithdrawalRequests(),
    getOwnerMeta(ownerId),
    getAllBookings(),
    get(ref(database, "inquiries")),
    getAllReferralCodes(),
    getUserByID(ownerId),
  ]);

  const allInquiries = inquiriesSnap && inquiriesSnap.exists() ? inquiriesSnap.val() : {};
  const bookingsById = (allBookings || []).reduce((acc, booking) => {
    if (booking && booking.id) acc[booking.id] = booking;
    return acc;
  }, {});
  const bookingsByPaymentId = buildBookingsByPaymentId(allBookings);
  const referralRateByCode = buildReferralRateMap(allReferralCodes);
  const ownerReferralCode = ownerDetails?.refferalCode || "";

  const ownerPayments = (allPayments || []).filter(
    (p) =>
      (p.owner === ownerId || p.referrer === ownerId) &&
      isCreditedPaymentRecord(p, getLinkedBooking(p, bookingsById, bookingsByPaymentId))
  );
  const ownerFirstRentalPaymentId = getOwnerFirstRentalPaymentId(
    ownerPayments.filter((p) => p.owner === ownerId)
  );

  const totalNetEarningsFromPayments = ownerPayments.reduce(
    (sum, p) =>
      sum +
      getOwnerNetEarningsCents(
        p,
        ownerId,
        bookingsById,
        allInquiries,
        ownerReferralCode,
        referralRateByCode,
        ownerFirstRentalPaymentId,
        bookingsByPaymentId
      ),
    0
  );
  const totalPostTripCharges = sumOwnerPostTripChargesCents(
    ownerId,
    allBookings
  );
  const totalNetEarnings = totalNetEarningsFromPayments + totalPostTripCharges;

  const ownerWithdrawals = (allWithdrawals || []).filter((w) => w.userId === ownerId);
  const totalWithdrawalsInCents = sumOwnerWithdrawalsInCents(
    ownerWithdrawals,
    ["Processed", "Approved"]
  );
  const pendingWithdrawalsInCents = sumOwnerWithdrawalsInCents(
    ownerWithdrawals,
    ["Pending"]
  );

  const metaCurrent = Number(ownerMeta?.payments?.currentAmount) || 0;
  const metaTotal = Number(ownerMeta?.payments?.totalAmount) || 0;
  const { available, reconciledBeforePending } = reconcileOwnerAvailableCents(
    metaCurrent,
    totalNetEarnings,
    totalWithdrawalsInCents,
    pendingWithdrawalsInCents
  );

  return {
    currentAmount: available,
    computedAmount: available,
    reconciledBeforePending,
    metaAmount: metaCurrent,
    totalEarnings: metaTotal > 0 ? metaTotal : totalNetEarnings,
    totalWithdrawals: totalWithdrawalsInCents,
    pendingWithdrawals: pendingWithdrawalsInCents,
  };
}

/**
 * Get aggregated payment data for all boat owners
 * Returns: Array of owner objects with earnings, withdrawals, and balance
 */
export async function getPaymentsDashboardData() {
  try {
    // Fetch all data in parallel
    const database = getDatabase();
    const [boatOwners, allPayments, allWithdrawals, allOwnerMeta, allBookings, inquiriesSnap, allReferralCodes] = await Promise.all([
      getAllBoatOwners(),
      getAllPayments(),
      getWithdrawalRequests(),
      getAllOwnerMeta(),
      getAllBookings(),
      get(ref(database, "inquiries")),
      getAllReferralCodes()
    ]);

    const allInquiries = inquiriesSnap && inquiriesSnap.exists() ? inquiriesSnap.val() : {};
    const bookingsById = (allBookings || []).reduce((acc, booking) => {
      if (booking && booking.id) acc[booking.id] = booking;
      return acc;
    }, {});
    const bookingsByPaymentId = buildBookingsByPaymentId(allBookings);
    const referralRateByCode = buildReferralRateMap(allReferralCodes);

    // Filter out deleted boat owners
    const activeBoatOwners = boatOwners ? boatOwners.filter(owner => !owner.isDeleted) : [];

    if (!activeBoatOwners || activeBoatOwners.length === 0) {
      return { owners: [], summary: { totalPayments: 0, totalWithdrawals: 0, totalBalance: 0, pendingWithdrawals: 0 } };
    }

    // Process each boat owner
    const ownersData = activeBoatOwners.map(owner => {
      const ownerId = owner.userId;

      // Balance still comes from ownerMeta because withdrawals mutate that counter.
      // Total earnings are calculated from payment records so successful paid
      // bookings are reflected even before the trip-completion payout step runs.
      const ownerMeta = (allOwnerMeta && allOwnerMeta[ownerId]) || {};
      const metaPayments = ownerMeta.payments || {};
      const metaTotalEarnings = Number(metaPayments.totalAmount) || 0;
      const metaCurrentBalance = Number(metaPayments.currentAmount) || 0;

      const ownerPayments = allPayments
        ? allPayments.filter(p =>
            p.owner === ownerId &&
            isCreditedPaymentRecord(p, getLinkedBooking(p, bookingsById, bookingsByPaymentId))
          )
        : [];

      const referralPayments = allPayments
        ? allPayments.filter(p =>
            p.referrer === ownerId &&
            isCreditedPaymentRecord(p, getLinkedBooking(p, bookingsById, bookingsByPaymentId)) &&
            (Number(p.referAmount) || 0) > 0
          )
        : [];

      const creditedPayments = [...ownerPayments, ...referralPayments];
      const ownerReferralCode = owner.refferalCode || "";
      const ownerFirstRentalPaymentId = getOwnerFirstRentalPaymentId(ownerPayments);
      const totalNetEarningsFromPayments = creditedPayments.reduce(
        (sum, p) =>
          sum +
          getOwnerNetEarningsCents(
            p,
            ownerId,
            bookingsById,
            allInquiries,
            ownerReferralCode,
            referralRateByCode,
            ownerFirstRentalPaymentId,
            bookingsByPaymentId
          ),
        0
      );
      const totalPostTripCharges = sumOwnerPostTripChargesCents(
        ownerId,
        allBookings
      );
      const totalNetEarnings =
        totalNetEarningsFromPayments + totalPostTripCharges;
      const totalEarnings =
        metaTotalEarnings > 0 ? metaTotalEarnings : totalNetEarnings;

      // Aggregate per-payment breakdowns across completed trips (all in cents).
      // Tax: sum of stored totalTax. For the 5% renter platform fee and the First
      // Rental Fee, we derive the underlying rental `amount` from stored totals:
      //   finalAmount = amount + tax + 0.05*amount  →  amount = (finalAmount - tax) / 1.05
      let totalTaxInCents = 0;
      let totalPlatformFeeInCents = 0;
      let totalFirstRentalFeeInCents = 0;
      let totalBbrCommissionInCents = 0;
      ownerPayments.forEach(p => {
        if (p.reason === "Tip") return;

        const finalAmount = Number(p.totalAmount) || 0;
        const tax = Number(p.totalTax) || 0;
        totalTaxInCents += tax;
        const rentalAmount = Math.max(0, (finalAmount - tax) / 1.05);
        totalPlatformFeeInCents += rentalAmount * 0.05;
        totalBbrCommissionInCents += getPaymentBbrCommissionCents(
          p,
          bookingsById,
          allInquiries,
          ownerReferralCode,
          referralRateByCode
        );

        if (paymentQualifiesForFirstRentalFee(p, ownerFirstRentalPaymentId)) {
          const offerPriceCents = getPaymentOfferPriceCents(
            p,
            bookingsById,
            allInquiries
          );
          totalFirstRentalFeeInCents += getFirstRentalAdminFeeCents(
            p,
            offerPriceCents,
            rentalAmount
          );
        }
      });

      const ownerWithdrawals = allWithdrawals ? allWithdrawals.filter(w => w.userId === ownerId) : [];

      // Withdrawals are stored in dollars — convert to cents to keep UI units consistent
      const totalWithdrawalsInCents = sumOwnerWithdrawalsInCents(
        ownerWithdrawals,
        ["Processed", "Approved"]
      );

      const pendingWithdrawalsInCents = sumOwnerWithdrawalsInCents(
        ownerWithdrawals,
        ["Pending"]
      );

      const { available: currentBalance } = reconcileOwnerAvailableCents(
        metaCurrentBalance,
        totalNetEarnings,
        totalWithdrawalsInCents,
        pendingWithdrawalsInCents
      );

      return {
        id: ownerId,
        name: `${owner.firstName || ''} ${owner.lastName || ''}`.trim() || 'Unknown',
        email: owner.email || '',
        phone: owner.phone || '',
        totalEarnings,
        totalWithdrawals: totalWithdrawalsInCents,
        currentBalance,
        pendingWithdrawals: pendingWithdrawalsInCents,
        firstRentalFee: totalFirstRentalFeeInCents, // $100 (or 50% for sub-$200) taken on first rental
        bbrCommission: totalBbrCommissionInCents,   // offer price × cutPercentage (matches Event.js)
        platformFee: totalPlatformFeeInCents,       // 5% renter booking fee across trips
        tax: totalTaxInCents,                       // total sales tax collected
        completedPaymentsCount: creditedPayments.length,
        withdrawalsCount: ownerWithdrawals.filter(w => w.status === 'Processed' || w.status === 'Approved').length,
        lastCompletedPaymentDate: creditedPayments.length > 0
          ? creditedPayments.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))[0]?.dateTime
          : null,
        status: owner.status || 'Active'
      };
    });

    // Sort owners by total earnings (highest first)
    const sortedOwnersData = ownersData.sort((a, b) => {
      return (Number(b.totalEarnings) || 0) - (Number(a.totalEarnings) || 0);
    });

    // Calculate summary totals
    // Ensure all values are numbers to prevent string concatenation
    const summary = {
      totalPayments: sortedOwnersData.reduce((sum, o) => sum + (Number(o.totalEarnings) || 0), 0),
      totalWithdrawals: sortedOwnersData.reduce((sum, o) => sum + (Number(o.totalWithdrawals) || 0), 0),
      totalBalance: sortedOwnersData.reduce((sum, o) => sum + (Number(o.currentBalance) || 0), 0),
      pendingWithdrawals: sortedOwnersData.reduce((sum, o) => sum + (Number(o.pendingWithdrawals) || 0), 0),
      totalOwners: sortedOwnersData.length
    };

    return { owners: sortedOwnersData, summary };
  } catch (error) {
    console.error("Error fetching payments dashboard data:", error);
    throw error;
  }
}

/**
 * Get all ownerMeta data for aggregation
 */
export async function getAllOwnerMeta() {
  try {
    const database = getDatabase();
    const ownerMetaRef = ref(database, "ownerMeta");
    const snapshot = await get(ownerMetaRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    return {};
  } catch (error) {
    console.error("Error fetching all ownerMeta:", error);
    return {};
  }
}

/**
 * Get detailed transaction history for a specific boat owner
 */
export async function getOwnerTransactionHistory(ownerId) {
  try {
    console.log('=== getOwnerTransactionHistory START ===');
    console.log('Owner ID:', ownerId);
    
    // Fetch payments, withdrawals, bookings and inquiries for this owner.
    // Bookings + inquiries are needed to resolve each payment's offerPrice so we
    // can show the projected Owner Amount the same way the booking detail page
    // (Event.js) does — using offerPrice * (1 - cutPercentage) − firstRentalFee.
    // The stored payment.ownerAmount is 0 for first-time-owner advance bookings
    // until trip end (see PaymentForm.js), which produced the "$0.00" rows.
    const database = getDatabase();
    const [allPayments, allWithdrawals, ownerDetails, ownerMeta, allBookings, inquiriesSnap, allReferralCodes] = await Promise.all([
      getAllPayments(),
      getWithdrawalRequests(),
      getUserByID(ownerId),
      getOwnerMeta(ownerId),
      getAllBookings(),
      get(ref(database, "inquiries")),
      getAllReferralCodes()
    ]);

    const allInquiries = inquiriesSnap && inquiriesSnap.exists() ? inquiriesSnap.val() : {};
    const bookingsById = (allBookings || []).reduce((acc, b) => {
      if (b && b.id) acc[b.id] = b;
      return acc;
    }, {});
    const bookingsByPaymentId = buildBookingsByPaymentId(allBookings);
    const referralRateByCode = buildReferralRateMap(allReferralCodes);
    const ownerReferralCode = ownerDetails?.refferalCode || "";

    console.log('Owner Meta:', ownerMeta);
    console.log('Owner Meta Payments:', ownerMeta?.payments);
    console.log('Current Amount from Meta:', ownerMeta?.payments?.currentAmount);
    console.log('Total Amount from Meta:', ownerMeta?.payments?.totalAmount);

    // Filter payments for this owner. Include paid records so admin totals move
    // when payment is collected, not only after trip completion.
    // Compute Owner Amount the same way Event.js does so the dashboard matches
    // the booking detail screen, and falls back to stored ownerAmount otherwise.
    const creditedOwnerPayments = allPayments
      ? allPayments.filter(
          (p) =>
            (p.owner === ownerId || p.referrer === ownerId) &&
            isCreditedPaymentRecord(p, getLinkedBooking(p, bookingsById, bookingsByPaymentId))
        )
      : [];
    const ownerFirstRentalPaymentId = getOwnerFirstRentalPaymentId(
      creditedOwnerPayments.filter((p) => p.owner === ownerId)
    );

    const ownerPayments = creditedOwnerPayments.map(p => {
            if (p.referrer === ownerId && p.owner !== ownerId) {
              return {
                id: p.id,
                type: 'payment',
                amount: Number(p.referAmount) || 0,
                totalAmount: Number(p.referAmount) || 0,
                date: p.dateTime,
                status: p.status || 'paid',
                tripCompleted: p.tripCompleted || false,
                bookingId: p.bookingId,
                reason: 'Referral',
                userId: p.userId,
                charterDeposit: false,
                cashBalanceCollected: 0
              };
            }

            if (p.reason === 'Tip') {
              return {
                id: p.id,
                type: 'payment',
                amount: Number(p.ownerAmount) || 0,
                totalAmount: Number(p.totalAmount) || Number(p.ownerAmount) || 0,
                date: p.dateTime,
                status: p.status || 'completed',
                tripCompleted: p.tripCompleted || true,
                bookingId: p.bookingId,
                reason: 'Tip',
                userId: p.userId,
                charterDeposit: false,
                cashBalanceCollected: 0
              };
            }

            const amount = getOwnerNetEarningsCents(
              p,
              ownerId,
              bookingsById,
              allInquiries,
              ownerReferralCode,
              referralRateByCode,
              ownerFirstRentalPaymentId,
              bookingsByPaymentId
            );

            return {
              id: p.id,
              type: 'payment',
              amount,
              totalAmount: Number(p.totalAmount) || 0,
              date: p.dateTime,
              status: p.status || 'completed',
              tripCompleted: p.tripCompleted || false,
              bookingId: p.bookingId,
              reason: p.reason || 'Booking',
              userId: p.userId, // renter who paid
              charterDeposit: p.charterDeposit || false,
              cashBalanceCollected: p.collectBalanceAtMeetup ? (Number(p.charterCashBalance) || Number(p.charterRemainingBalance) || 0) : 0
            };
          });

    const postTripChargeTransactions = buildOwnerPostTripChargeTransactions(
      ownerId,
      allBookings
    );

    // Filter withdrawals for this owner
    const ownerWithdrawals = allWithdrawals
      ? allWithdrawals.filter(w => w.userId === ownerId).map(w => ({
          id: w.id,
          type: 'withdrawal',
          amount: (Number(w.amount) || 0) * 100, // Convert to cents for consistency
          date: w.timestamp ? new Date(w.timestamp).toISOString() : null,
          status: w.status || 'Pending',
          bookingId: w.bookingId || null
        }))
      : [];

    // Combine and sort by date (newest first)
    const allTransactions = [
      ...ownerPayments,
      ...postTripChargeTransactions,
      ...ownerWithdrawals,
    ].sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA;
      });

    // Calculate running balance (from oldest to newest, then reverse)
    let runningBalance = 0;
    const transactionsWithBalance = [...allTransactions].reverse().map(t => {
      if (t.type === 'payment') {
        runningBalance += t.amount;
      } else if (t.type === 'withdrawal' && (t.status === 'Processed' || t.status === 'Approved')) {
        runningBalance -= t.amount;
      }
      return { ...t, runningBalance };
    }).reverse();

    // Calculate total withdrawals (only processed/approved)
    const processedWithdrawals = ownerWithdrawals.filter(w => w.status === 'Processed' || w.status === 'Approved');
    const totalWithdrawals = processedWithdrawals.reduce((sum, w) => sum + (Number(w.amount) || 0), 0);
    
    // Calculate pending withdrawals
    const pendingWithdrawals = ownerWithdrawals.filter(w => w.status === 'Pending');
    const totalPendingWithdrawals = pendingWithdrawals.reduce((sum, w) => sum + (Number(w.amount) || 0), 0);
    
    // Calculate total from payments and post-trip charges
    const totalFromPayments = ownerPayments.reduce(
      (sum, p) => sum + (Number(p.amount) || 0),
      0
    );
    const totalFromPostTripCharges = postTripChargeTransactions.reduce(
      (sum, t) => sum + (Number(t.amount) || 0),
      0
    );
    const totalFromAllPayments = totalFromPayments + totalFromPostTripCharges;
    
    // Calculate expected balance - deduct both processed AND pending withdrawals
    const calculatedBalance =
      totalFromAllPayments - totalWithdrawals - totalPendingWithdrawals;
    const balanceFromMeta = Number(ownerMeta?.payments?.currentAmount) || 0;
    const balanceDifference = balanceFromMeta - calculatedBalance;
    
    console.log('=== BALANCE CALCULATION DEBUG ===');
    console.log('Owner ID:', ownerId);
    console.log('Total Payments Count:', ownerPayments.length);
    console.log('Post-trip Charges Count:', postTripChargeTransactions.length);
    console.log('Total from Payments:', totalFromPayments, '($' + (totalFromPayments/100).toFixed(2) + ')');
    console.log(
      'Total from Post-trip Charges:',
      totalFromPostTripCharges,
      '($' + (totalFromPostTripCharges / 100).toFixed(2) + ')'
    );
    console.log('Total Withdrawals (Processed/Approved):', totalWithdrawals, '($' + (totalWithdrawals/100).toFixed(2) + ')');
    console.log('Total Pending Withdrawals:', totalPendingWithdrawals, '($' + (totalPendingWithdrawals/100).toFixed(2) + ')');
    console.log('Calculated Balance (Payments - Withdrawals - Pending):', calculatedBalance, '($' + (calculatedBalance/100).toFixed(2) + ')');
    console.log('Balance from ownerMeta:', balanceFromMeta, '($' + (balanceFromMeta/100).toFixed(2) + ')');
    console.log('Difference (Meta - Calculated):', balanceDifference, '($' + (balanceDifference/100).toFixed(2) + ')');
    console.log('Running Balance (last transaction):', runningBalance, '($' + (runningBalance/100).toFixed(2) + ')');
    console.log('Processed Withdrawals:', processedWithdrawals.map(w => ({ 
      id: w.id, 
      amount: w.amount, 
      amountUSD: '$' + (w.amount/100).toFixed(2),
      status: w.status 
    })));
    console.log('Pending Withdrawals:', pendingWithdrawals.map(w => ({ 
      id: w.id, 
      amount: w.amount, 
      amountUSD: '$' + (w.amount/100).toFixed(2),
      status: w.status 
    })));

    const metaCurrentBalance = Number(ownerMeta?.payments?.currentAmount) || 0;
    const metaTotalEarnings = Number(ownerMeta?.payments?.totalAmount) || 0;
    const { available: currentBalance } = reconcileOwnerAvailableCents(
      metaCurrentBalance,
      totalFromAllPayments,
      totalWithdrawals,
      totalPendingWithdrawals
    );

    return {
      owner: {
        id: ownerId,
        name: `${ownerDetails?.firstName || ''} ${ownerDetails?.lastName || ''}`.trim(),
        email: ownerDetails?.email || '',
        phone: ownerDetails?.phone || '',
        taxInformation: ownerMeta?.taxInformation
          ? {
              taxIdType: ownerMeta.taxInformation.taxIdType || '',
              taxIdLast4: ownerMeta.taxInformation.taxIdLast4 || String(ownerMeta.taxInformation.taxId || '').slice(-4)
            }
          : null
      },
      currentBalance,
      totalEarnings:
        metaTotalEarnings > 0 ? metaTotalEarnings : totalFromAllPayments,
      totalWithdrawals: totalWithdrawals,
      transactions: transactionsWithBalance
    };
  } catch (error) {
    console.error("Error fetching owner transaction history:", error);
    throw error;
  }
}
